/**
 * HUD ARENA — GIAO DIỆN MÀN HÌNH THỨ BA (chế độ BỔ SUNG, không thay thế game TD).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ VÌ SAO LÀ FILE RIÊNG, KHÔNG NHÉT VÀO `src/ui/hud.ts`
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * `src/ui/hud.ts` đã 155 KB / 3100 dòng và là HUD của trận 10-wave ĐANG CHẠY PRODUCTION. Người
 * dùng đã nhấn mạnh: **Arena là chế độ BỔ SUNG, KHÔNG thay thế game hiện tại.** Nhét HUD Arena vào
 * đó nghĩa là mọi lần sửa Arena đều có nguy cơ chạm vào luồng 10-wave đang ổn, và mọi test/ công cụ
 * đo của game cũ phải chạy lại. Tách file ⇒ hai chế độ có hai HUD, chỉ gặp nhau ở `Hud.showScreen`.
 *
 * ⚠️ VÌ SAO MODULE NÀY KHÔNG IMPORT PHASER: nó thuần DOM. `ArenaBattleScene` là thứ duy nhất cầm
 * Phaser, và HUD chỉ nói chuyện với nó QUA MỘT INTERFACE HẸP (`ArenaHudHost` bên dưới). Nhờ vậy
 * `tests/arena-hud.test.ts`... — xem chú thích ở `ArenaHudHost`: kiểu hẹp là hợp đồng, không phải
 * "cho dễ viết".
 *
 * ⚠️⚠️ 4 QUYẾT ĐỊNH Ở ĐÂY ĐỀU LÀ BẪY ĐÃ SẬP TRONG REPO — ĐỌC TRƯỚC KHI SỬA
 *
 * 1. **MỌI CHUỖI ĐỀU ĐI QUA `t('vi','en')`.** Repo lưu nội dung gốc bằng TIẾNG VIỆT và bản EN tra
 *    theo id/ tại chỗ (`src/i18n/index.ts`). Viết thẳng một chuỗi tiếng Việt vào `innerHTML` là
 *    người chơi tiếng Anh đọc thấy tiếng Việt — mà `tests/i18n.test.ts` KHÔNG bắt được (nó kiểm
 *    bảng dịch, không kiểm DOM). Vì vậy `render()` được gọi lại khi `setLang` phát `onLangChange`.
 *
 * 2. **HUD ĐỌC THẲNG TỪ SIM, KHÔNG GIỮ BẢN SAO.** Tài nguyên/ đồng hồ/ điểm được lấy qua `host`
 *    mỗi lần `render()`. Giữ bản sao trong HUD là mở đường cho "HUD hiện 500 mà ví thật 300" —
 *    đúng loại lỗi hai nguồn sự thật mà `formatArenaResource()` đã cảnh báo.
 *
 * 3. **MỌI LỆNH MUA ĐI QUA SIM — HUD KHÔNG TỰ XÉT LUẬT.** HUD chỉ làm ĐÚNG hai việc: quy đổi toạ
 *    độ thả (hỏi scene qua `host.worldFromScreen`) và gọi `summonHero(classId, x, y)` /
 *    `buyFarmer(x, y)`. Sim kiểm toạ độ/ tiền/ trạng thái trận rồi trả `{ ok, message }`; HUD chỉ
 *    hiện toast. HUD tự "đoán" một toạ độ hợp lệ là mở đường cho hai bản luật lệch nhau — đúng loại
 *    lỗi mà `deploy-pos.ts` đã tách ra khỏi tầng UI để tránh.
 *
 *    ⚠️ Vì cùng lý do, HUD KHÔNG tự viết công thức màn hình → thế giới: `Scale.FIT` làm tỉ lệ phụ
 *    thuộc kích thước canvas thật, nên công thức chép tay sẽ sai ở một số màn hình. Hỏi scene.
 *
 * 4. **KHÔNG TỰ DỰNG SỐ LIỆU.** Giá tướng lấy từ `heroOptions()`, định dạng số từ
 *    `formatArenaResource()`, toạ độ Nhà từ `ARENA_CORE`, luật thả từ `sim/arena/deploy-pos.ts`.
 *    Chép số/ chữ vào đây là tạo nguồn thứ hai và HUD sẽ nói SAI luật sau lần chỉnh cân bằng đầu tiên.
 *    ⚠️ *"Nhịp thả quân"* nay KHÔNG còn hiện ở HUD (cùng bảng "Kinh tế & nhịp trận" bị xoá 02/10/2026
 *    theo yêu cầu người dùng) ⇒ `arenaCadenceText()` không còn được import ở đây.
 *
 * ⚠️ KHÔNG đụng `meta.gold`: tài nguyên Arena là VÍ RIÊNG (xem `spendFromRunWallet()` trong
 * `src/game.ts`). HUD này không có đường nào ghi vào `meta`.
 */
import { t } from '../i18n';
import { artUrl } from '../art/art-url';
import { CLASSES, type ClassId } from '../data/classes';
/*
 * ⚠️ `import type` — BẮT BUỘC, KHÔNG ĐƯỢC LÀ IMPORT THƯỜNG.
 * `render/unitview.ts` kéo Phaser (nó là lớp vẽ), mà module này phải chạy được trong node của vitest
 * (xem khối "VÌ SAO MODULE NÀY KHÔNG IMPORT PHASER" ở đầu file). `import type` bị XOÁ hoàn toàn khi
 * biên dịch nên không có phụ thuộc runtime — nhưng vẫn dùng ĐÚNG một định nghĩa `UnitArtKind` với
 * scene, không tạo bản sao thứ hai để lệch.
 */
import type { UnitArtKind } from '../render/unitview';
import { className } from '../i18n/loc';
import { ARENA_CORE, ARENA_TEXT, arenaTimeSpeedMul } from '../data/arena';
import { ARENA_CORE_DRAW_R, ARENA_ECON } from '../sim/arena/world';
import { ARENA_TEXT_EN } from '../i18n/content-en-arena';
import { formatArenaResource } from '../render/arena-battle';
import { DEPLOY_MARGIN, clampToOwnHalf, isValidDeployPos } from '../sim/arena/deploy-pos';

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * ICON KÉO THẢ TỰ DO — TẦNG LOGIC THUẦN (không DOM, không Phaser, không `window`)
 * ════════════════════════════════════════════════════════════════════════════════════════════
 *
 * YÊU CẦU NGƯỜI DÙNG (nguyên văn): *"Thay bằng icon nhân vật, tôi có thể giữ/kéo vào màn vị trí
 * tôi muốn"*. Khối dưới đây là phần TOÁN của tính năng đó; phần DOM nằm ở `ArenaHud` phía dưới.
 *
 * ⚠️⚠️ VÌ SAO TÁCH RA HÀM THUẦN THAY VÌ NHÉT VÀO CLASS DOM: bài kiểm `tests/arena-hud-drag.test.ts`
 * phải khoá được HAI luật dễ sai nhất (kẹp trong khung · ngưỡng tap-vs-drag) bằng SỐ. Trong môi
 * trường vitest KHÔNG có layout engine (không `getBoundingClientRect` thật, không `pointerdown`
 * thật), nên logic nằm trong class DOM là logic KHÔNG TEST ĐƯỢC — đúng loại "test xanh mà không
 * chứng minh gì" mà chú thích đầu file này đã cảnh báo.
 */

/** 6 vùng neo của icon — neo theo VÙNG để màn hình nhỏ không làm icon chồng lên nhau. */
export type DragZone = 'tl' | 'tc' | 'tr' | 'bl' | 'bc' | 'br';

/** Vị trí icon: neo vào 1 trong 6 vùng + tỉ lệ 0..1 trong vùng đó. */
export interface IconAnchor {
  zone: DragZone;
  fx: number;
  fy: number;
}

/**
 * ICON NÀO — khoá lưu/ định danh. Hai dạng DUY NHẤT:
 *   · `hero:<classId>` — AVATAR MUA TƯỚNG (mỗi class đã mở khoá một avatar);
 *   · `farmer`         — avatar mua nông dân.
 *
 * ⚠️ Khoá `land:<0|1|2>` ĐÃ BỊ XOÁ cùng khái niệm land (xem đầu `src/sim/arena/types.ts`). Neo vị
 * trí cũ trong `localStorage` với khoá `land:*` là RÁC: nó không khớp khoá nào nữa nên bị bỏ qua,
 * và người chơi vào lại thấy avatar ở vị trí mặc định — hành vi đúng, không cần migrate.
 */
export type IconKey = string;

/**
 * GLYPH AVATAR DỰ PHÒNG — chỉ dùng khi class CHƯA có ảnh đầu (`CLASS_HEAD_FILE[id] === null`).
 *
 * Người dùng chốt *"Avatar nhân vật là hình đầu nhân vật"*, nên đường chính nay là `<img class="adi-face">`
 * (`headImageUrl()`). Glyph ở đây là LƯỚI AN TOÀN, không phải giao diện chính:
 *   · `carpenter`/ `diver` chưa có sheet bộ phận ⇒ không có ảnh đầu;
 *   · nếu ảnh 404 (chưa `sync:art`), người chơi vẫn thấy một avatar nhận ra được thay vì ô trắng.
 *
 * ⚠️ VÌ SAO KHAI Ở ĐÂY CHỨ KHÔNG THÊM TRƯỜNG VÀO `data/classes.ts`: file đó là DỮ LIỆU của game TD
 * 10-wave (và nằm NGOÀI write scope) — thêm trường ở đó là mọi nơi đọc `ClassDef` phải biết tới một
 * trường chỉ Arena dùng. Glyph ở đây là chi tiết TRÌNH BÀY của riêng HUD Arena.
 *
 * ⚠️ Bảng này bị `tests/arena-hud-drag.test.ts` khoá phủ HẾT `CLASSES`: thiếu một class là avatar
 * của nó hiện RỖNG (nút tròn không có gì để bấm) mà không có lỗi nào được ném ra.
 */
export const CLASS_AVATAR_GLYPH: Record<ClassId, string> = {
  strength: '💪',
  agility: '💃',
  street: '🧺',
  army: '🔫',
  mother: '🤱',
  carpenter: '🪚',
  diver: '🤿',
};

/**
 * GLYPH DỰ PHÒNG CỦA NÔNG DÂN — xem `CLASS_AVATAR_GLYPH` về lý do còn giữ glyph. Nông dân KHÔNG phải
 * `ClassId` nên không nằm trong bảng trên; khai ở đây để `iconsHtml()` không phải nhét một chuỗi emoji
 * vào giữa template literal.
 */
export const FARMER_GLYPH = '\u{1F9D1}\u200D\u{1F33E}';

/**
 * KÍCH THƯỚC ART CỦA NHÀ (đơn vị thế giới) — dùng để avatar KHÔNG ĐÈ LÊN NHÀ.
 *
 * ⚠️⚠️ ẢNH NHÀ LÀ `2·DRAW_R` VUÔNG VẮP — KHÔNG PHẢI `4,2·DRAW_R` NHƯ CHÚ THÍCH CŨ GHI (SỬA 02/10/2026).
 *
 * `syncHouseArt()` trong `src/render/arena-battle.ts` từng vẽ ảnh ở `target = 4,2·ARENA_CORE_DRAW_R`
 * (≈192,5 đơn vị) và chính nó đã được sửa thành **`target = 2·R`** (≈91,7) vì ảnh to gấp 2,1 lần cái BOX
 * của nó. Hằng số ở đây thì KHÔNG được cập nhật theo ⇒ vùng "cấm đặt avatar" rộng gấp ~2,2 lần art thật.
 *
 * SỐ ĐO THẬT của lỗi đó (740×360, qua `window.__iconDebug`): hình cấm của Nhà là **133×133px** trong khi
 * art chỉ ~58×58px, và nó chồm vào dải đen bên trái ⇒ `layoutDefaultAvatars`/`clampOutOfRects` ĐẨY cột
 * avatar: 3 avatar đầu ở `x=22`, 3 avatar cuối ở `x=35` — cột bị "gãy khúc" 13px (đúng thứ `verify:arena:hud`
 * §5 bắt được ở 740×360 và 844×390).
 *
 * 🔢 Nay: ảnh 1024×1024 vuông (`art/ai/house.png`) vẽ ở `2·R` ⇒ **nửa bề ngang = `R`**; lấy `1,08·R`
 * (≈49,5) để nới ~8% làm lề — thà né rộng hơn art một chút còn hơn né hụt (avatar đè lên mái Nhà).
 *
 * ⚠️ Vẫn là "bản sao có chủ ý" của hệ số trong tầng render: nếu tầng render đổi cỡ Nhà thì hai bên lệch,
 * nên `tools/verify-arena-hud.mjs` §6 đo TRỰC TIẾP trên Chrome (hình chữ nhật Nhà lấy từ chính sprite đang
 * vẽ) để bắt lệch — thay vì tin vào con số này.
 */
const CORE_ART_HALF = ARENA_CORE_DRAW_R * 1.08;
/** ĐÁY ART CỦA NHÀ (đơn vị thế giới) — khớp `syncHouseArt()`: `ARENA_CORE.y + DRAW_R * 0.9`. */
const CORE_ART_BOTTOM = ARENA_CORE.y + ARENA_CORE_DRAW_R * 0.9;

/**
 * NỀN TINT CỦA Ô AVATAR THEO MÀU CLASS — chuỗi `background` cho inline style.
 *
 * ⚠️⚠️ VÌ SAO TINT BẰNG INLINE STYLE CHỨ KHÔNG THÊM 7 CLASS CSS: màu class đã có MỘT nguồn sự thật là
 * `CLASSES[id].color`. Chép 7 mã màu sang `src/style.css` là hai bản để lệch
 * nhau, mà CSS của Lead lại chỉ khai được MỘT màu nền chung. Truyền màu từ dữ liệu xuống DOM là cách
 * duy nhất giữ một nguồn.
 *
 * Hình dạng giữ ĐÚNG khuôn Lead đã đặt trong `src/style.css` (`radial-gradient` sáng ở trên, tối ở
 * dưới) để đầu nhân vật nổi lên; chỉ ĐỔI hai đầu màu theo class. Alpha ~0,55 đủ phân biệt nhanh mà
 * không lấn át khuôn mặt (đầu là PNG nền trong suốt nên màu nền lộ ra sau tóc).
 *
 * ⚠️⚠️ CHỈ dùng cho `background` — TUYỆT ĐỐI không dùng để đặt `border-color` (xem chú thích ở
 * `iconsHtml`: inline `border-color` đè mất tín hiệu màu của `.is-blocked`/ `.drop-target`).
 *
 * Số rác (âm/ > 0xFFFFFF/ NaN) bị kẹp ⇒ không sinh CSS sai.
 */
function classTintBackground(color: number): string {
  const n = Number.isFinite(color) ? Math.max(0, Math.min(0xffffff, Math.floor(color))) : 0xffffff;
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `radial-gradient(circle at 50% 32%, rgba(${r},${g},${b},0.55), rgba(20,15,11,0.95))`;
}

/**
 * Ý NGHĨA CỦA MỘT ICON — hàm THUẦN, test được.
 *
 * ⚠️ VÌ SAO CẦN BƯỚC PHÂN TÍCH NÀY: `data-icon` là chuỗi tự do trong DOM, nên một khoá rác (save cũ
 * còn `land:0`, hoặc ai đó gõ sai) sẽ chảy thẳng xuống `CLASSES[...]` ⇒ `undefined.name` ⇒ `TypeError`
 * giữa trận. Trả `null` cho khoá lạ để nơi gọi BỎ QUA thay vì ném.
 */
export type IconAction = { kind: 'hero'; classId: ClassId } | { kind: 'farmer' };

/** Phân tích `data-icon` thành hành động. Khoá lạ/ class không tồn tại ⇒ `null`. */
export function iconAction(key: IconKey): IconAction | null {
  if (key === 'farmer') return { kind: 'farmer' };
  if (typeof key === 'string' && key.startsWith('hero:')) {
    const id = key.slice(5) as ClassId;
    return CLASSES[id] ? { kind: 'hero', classId: id } : null;
  }
  return null;
}

/**
 * BẢNG TRA class → TÊN FILE ĐẦU NHÂN VẬT — MỘT CHỖ DUY NHẤT, không rải chuỗi khắp file.
 *
 * Nguồn ảnh: `tools/gen-heads.mjs` cắt ô (0,0) của `art/ai/<class>-parts.png` (chính sheet bộ phận mà
 * tầng Phaser dùng để GHÉP nhân vật trên sân) rồi key nền magenta và ghi ra `art/ai/head-<class>.png`.
 * Nhờ vậy avatar và nhân vật trên sân KHÔNG THỂ lệch nhau — sinh ảnh mới là mở đường cho hai bộ art.
 *
 * ⚠️⚠️ VÌ SAO `carpenter`/`diver` LÀ `null`: hai class đó là KHUNG PLACEHOLDER chưa có thiết kế/ chưa
 * có sheet bộ phận (`gen-heads.mjs` in ra cảnh báo đúng hai tên này), và chúng CHƯA MỞ KHOÁ nên không
 * nằm trong `CLASS_ORDER` ⇒ không bao giờ thành avatar. Khai `null` (thay vì bịa tên file) để:
 *   · avatar của chúng rơi về GLYPH emoji thay vì `<img>` trỏ tới file 404 (404 ⇒ ô trắng + icon vỡ);
 *   · `tests/arena-hud-drag.test.ts` khoá được "chỉ đúng 2 class này thiếu art", nên ngày ai thêm
 *     `carpenter-parts.png` thì bài kiểm nhắc phải cập nhật bảng này.
 * ⚠️ `Record<ClassId, …>` (không phải `Record<string, …>`) là CỐ Ý: thêm class mới vào `CLASSES` mà
 * quên bảng này là `tsc` đỏ ngay, chứ không âm thầm ra avatar rỗng.
 */
export const CLASS_HEAD_FILE: Record<ClassId, string | null> = {
  strength: 'head-strength',
  agility: 'head-agility',
  street: 'head-street',
  army: 'head-army',
  mother: 'head-mother',
  // 2 class placeholder — chưa có sheet bộ phận, xem khối chú thích trên.
  carpenter: null,
  diver: null,
};

/**
 * FILE ĐẦU CỦA NÔNG DÂN — khai riêng vì `farmer` KHÔNG phải `ClassId` (nó là khoá icon, xem
 * `IconAction`). Vẫn nằm trong cùng một chỗ tra để không ai đi tìm chuỗi `head-farmer` ở nơi khác.
 */
export const FARMER_HEAD_FILE = 'head-farmer';

/**
 * TÊN FILE ĐẦU cho một hành động icon (tướng HOẶC nông dân). `null` = chưa có art.
 *
 * ⚠️ Mọi nơi cần tên file đều đi qua ĐÂY + `headImageUrl()`; viết thẳng `head-${classId}` ở chỗ gọi
 * là tạo nguồn thứ hai và avatar sẽ trỏ sai file khi bảng tra đổi.
 */
export function headFileFor(action: IconAction): string | null {
  if (action.kind === 'farmer') return FARMER_HEAD_FILE;
  const file = CLASS_HEAD_FILE[action.classId];
  return typeof file === 'string' && file.length > 0 ? file : null;
}

/**
 * URL RUNTIME CỦA ẢNH ĐẦU — đi qua `artUrl()` (src/art/art-url.ts), KHÔNG ghép chuỗi `.webp` thủ công.
 *
 * ⚠️⚠️ VÌ SAO KHÔNG ĐƯỢC VIẾT `.webp` CỨNG: `tools/sync-art.mjs` có chế độ LÙI — chưa cài `sharp` thì
 * nó COPY NGUYÊN `.png`, và ghi đuôi đang dùng vào `public/art/ai/.ext`. Viết cứng `.webp` ⇒ bản lùi
 * đó 404 toàn bộ avatar (im lặng: `<img>` vỡ, không có lỗi nào ném ra). `artUrl()` là đường DUY NHẤT
 * biết đuôi thật, và `game.ts` gọi `loadArtExt()` lúc boot TRƯỚC khi HUD Arena dựng.
 *
 * Trả `null` khi class chưa có art ⇒ nơi gọi dùng glyph dự phòng, xem `CLASS_HEAD_FILE`.
 */
export function headImageUrl(action: IconAction): string | null {
  const file = headFileFor(action);
  return file ? artUrl(`art/ai/${file}.png`) : null;
}

/**
 * TOẠ ĐỘ "RA TỪ NHÀ" — điểm hợp lệ GẦN NHẤT phía trước Nhà người chơi, dùng cho cú TAP avatar.
 *
 * ⚠️⚠️ VÌ SAO KHÔNG DÙNG THẲNG TÂM NHÀ (`ARENA_CORE.leftX/y`): `summonHero` kiểm toạ độ bằng
 * `isValidDeployPos`, mà luật đó CẤM thả trong thân Nhà (`reason: 'in-core'`). Tâm Nhà chắc chắn
 * nằm trong thân Nhà ⇒ TAP sẽ LUÔN bị từ chối kèm toast lỗi, tức tính năng "tap để mua" chết ngay
 * từ đầu — mà lại trông như lỗi của sim.
 *
 * Cách làm: bắt đầu từ tâm Nhà rồi bước ra phía sân địch từng 1px cho tới khi CHÍNH LUẬT CỦA SIM
 * (`isValidDeployPos`) chấp nhận. Nhờ vậy:
 *   · không hard-code số nào — đổi `ARENA_CORE`/ bán kính va chạm là hàm tự điều chỉnh;
 *   · không có "bản luật thứ hai" trong tầng UI (hàm chỉ HỎI luật, không chép luật).
 *
 * ⚠️ `unitRadius` mặc định là `DEPLOY_MARGIN` — bán kính LỚN NHẤT trong các loại người chơi thả được
 * (tướng). Dùng nó cho CẢ nông dân là cố ý: nông dân ra xa Nhà hơn vài px (vô hại), đổi lại không
 * phải chép bán kính nông dân của sim sang tầng UI.
 */
export const HOME_SPAWN_SEARCH_PX = 400;

export function homeDeployPoint(unitRadius: number = DEPLOY_MARGIN): { x: number; y: number } {
  const y = ARENA_CORE.y;
  const r = Number.isFinite(unitRadius) && unitRadius > 0 ? unitRadius : 0;
  for (let d = 0; d <= HOME_SPAWN_SEARCH_PX; d++) {
    const x = ARENA_CORE.leftX + d;
    if (isValidDeployPos(x, y, r).ok) return { x, y };
  }
  /*
   * Không tìm được (hằng số sim đổi tới mức Nhà nuốt cả nửa sân) ⇒ kẹp về nửa sân mình. Vẫn là toạ
   * độ HỢP LỆ theo hình học; sim là nơi cuối cùng phán quyết và sẽ trả lỗi nếu không.
   */
  return clampToOwnHalf(ARENA_CORE.leftX + HOME_SPAWN_SEARCH_PX, y, r);
}

/** Toạ độ px trong KHUNG NHÌN. */
export interface Vec2 {
  x: number;
  y: number;
}

/** Kích thước khung nhìn (hoặc khung chứa nút nổi). */
export interface Vec2Size {
  w: number;
  h: number;
}

export const DRAG_ZONES: readonly DragZone[] = ['tl', 'tc', 'tr', 'bl', 'bc', 'br'];

/**
 * NGƯỠNG PHÂN BIỆT CÚ BẤM (tap) VỚI KÉO (drag) — 8px, tính theo TỔNG dịch chuyển ngón tay kể từ
 * lúc chạm xuống (không phải khoảng cách so với điểm gốc).
 *
 * ⚠️ VÌ SAO PHẢI CÓ NGƯỠNG: yêu cầu #3 của task — kéo icon KHÔNG được kích hoạt lệnh mua/chọn.
 * Người chơi giữ icon rồi đổi ý, tay rung vài px, nếu không có ngưỡng thì vừa mua tướng vừa kéo
 * icon ⇒ vừa mất tiền vừa mất vị trí. 8px là ngưỡng quen thuộc của con trỏ/ chạm (bằng ngưỡng
 * "slop" của trình duyệt cho thao tác chạm), đủ nhỏ để kéo ăn ngay, đủ lớn để không bắt nhầm.
 */
export const DRAG_TAP_SLOP_PX = 8;

export const ICON_MIN_GAP_PX = 8;

/**
 * DUNG SAI KHI XÉT "GIAO NHAU" (px) — hai hộp CHẠM nhau đúng biên KHÔNG phải giao nhau.
 *
 * ⚠️⚠️ CẦN VÌ TOẠ ĐỘ BỐ CỤC LÀ SỐ THỰC: `arrangeIconLayout` nới hộp avatar đã đặt thêm đúng
 * `ICON_MIN_GAP_PX` mỗi phía, nên avatar kế tiếp CHẠM đúng hộp đó (khe = 8px). Nhưng hiệu của hai số
 * thực cùng nguồn là ~1e-13 chứ không phải 0 ⇒ nếu không có dung sai thì phép so `<` báo "giao" và
 * `clampOutOfRects` đẩy avatar đi (đo được: cột avatar ở 740×360 lệch 13px giữa 3 nút đầu và 3 nút cuối).
 */
export const RECT_EPS = 1e-6;

/** LỀ TRÁI/ PHẢI tối thiểu của HÀNG avatar mặc định (px) — để hàng không dính sát mép màn hình. */
export const ICON_ROW_MARGIN_PX = 8;

function finite(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/**
 * PHÂN BIỆT CÚ BẤM VỚI KÉO — thuần, test được.
 *
 * Trả `'drag'` khi TỔNG đường đi ≥ `slop` HOẶC đã từng vượt ngưỡng trong thao tác (`everDragged`).
 * Tham số thứ hai là thứ giữ cho thao tác NHẤT QUÁN: người chơi kéo ra 40px rồi quay lại đúng
 * điểm xuất phát thì lúc nhả tay `total` ≈ 0, nhưng đó vẫn là KÉO — nếu chỉ so `total` thì icon
 * "tự về chỗ cũ và bấm mua luôn", đúng kiểu lỗi khó lần ra.
 */
export function gestureKind(total: number, slop: number = DRAG_TAP_SLOP_PX, everDragged = false): 'tap' | 'drag' {
  if (everDragged) return 'drag';
  if (!finite(total) || !finite(slop)) return 'drag'; // số rác ⇒ KHÔNG bắn lệnh mua (an toàn)
  return total >= slop ? 'drag' : 'tap';
}

/** Đã vượt ngưỡng kéo chưa — dùng trong `pointermove` để bật cờ dính. */
export function crossedDragSlop(total: number, slop: number = DRAG_TAP_SLOP_PX): boolean {
  return !finite(total) || !finite(slop) || total >= slop;
}

/**
 * KẸP VỊ TRÍ NÚT TRONG KHUNG — yêu cầu #5 của task, và là thứ khiến nút không "mất" sau khi xoay.
 *
 * ⚠️ Kẹp theo TÂM nút (`pos` là tâm), với nửa kích thước `half`. Kẹp theo góc trên-trái là sai
 * trên màn dọc: nút 44px ở `x = innerWidth` vẫn nằm ngoài mép phải 22px.
 *
 * ⚠️ KHUNG NHỎ HƠN NÚT (màn 320×200): `max < min` ⇒ kẹp về `min` (mép trái/ trên) chứ KHÔNG kẹp
 * về `Math.max(min, Math.min(max, v))` rồi ra `max < min` — trường hợp đó nút nhảy ra ngoài khung.
 */
export function clampIconPos(pos: Vec2, half: number | Vec2, size: Vec2Size): Vec2 {
  const hx = finite((half as Vec2)?.x) ? (half as Vec2).x : (half as number);
  const hy = finite((half as Vec2)?.y) ? (half as Vec2).y : (half as number);
  const halfX = finite(hx) ? Math.max(0, hx) : 0;
  const halfY = finite(hy) ? Math.max(0, hy) : 0;
  const w = finite(size?.w) ? size.w : 0;
  const h = finite(size?.h) ? size.h : 0;
  const minX = Math.min(halfX, w / 2);
  const maxX = Math.max(minX, w - halfX);
  const minY = Math.min(halfY, h / 2);
  const maxY = Math.max(minY, h - halfY);
  return {
    x: finite(pos?.x) ? Math.max(minX, Math.min(maxX, pos.x)) : minX,
    y: finite(pos?.y) ? Math.max(minY, Math.min(maxY, pos.y)) : minY,
  };
}

/*
 * ⚠️ `defaultAnchor()` ĐÃ BỊ XOÁ (02/10/2026) — không còn ai dùng nó.
 *
 * Lịch sử: nó trả một NEO THEO VÙNG (`tr`/ `br`, `fx = 1`) để `defaultIconPx()` chia cột dọc ở mép
 * phải. Nhưng bố cục đó bị chính luật "né Nhà" đẩy vào GIỮA SÂN: ở 844×390, cột mép phải giao với Nhà
 * địch nên bị lùi sang trái tới x ≈ 583/637 ⇒ 2 cột × 3 hàng lơ lửng giữa bãi chiến (Lead đo được).
 * Bố cục MẶC ĐỊNH nay do `defaultIconPx()` tính trực tiếp thành MỘT HÀNG NGANG ở mép dưới (xem hàm
 * đó) — không đi qua vùng neo nào. Neo vẫn còn cần cho VỊ TRÍ NGƯỜI CHƠI ĐÃ KÉO (`anchorToPx`/
 * `pxToAnchor`/`anchorZones`), vì nó là thứ giữ vị trí sống sót khi xoay màn hình.
 */

/**
 * VỊ TRÍ MẶC ĐỊNH = **MỘT HÀNG NGANG Ở MÉP DƯỚI, NGAY TRÊN DOCK**, xếp từ mép TRÁI.
 *
 * ⚠️⚠️ VÌ SAO ĐỔI TỪ "CỘT DỌC Ở MÉP PHẢI" SANG "HÀNG NGANG Ở MÉP DƯỚI" (đo được, 02/10/2026):
 * bản cũ đặt cột ở mép PHẢI, nhưng Nhà địch nay TO và nằm sát mép phải, nên phép "né Nhà theo trục
 * ngang" đẩy cả cột vào GIỮA SÂN. Số đo ở 844×390: 6 avatar thành 2 cột × 3 hàng tại x = 583 và 637 —
 * một khối lơ lửng giữa bãi chiến, đè lên vùng sông/ cầu. Người dùng gọi đó là "chồng lên nhau, sắp
 * xếp chưa hợp lý", và đúng là bố cục tồi.
 *
 * Hàng ngang ở mép dưới là bố cục đúng vì:
 *   · dock là thanh NGANG đầy bề rộng ở đáy ⇒ một hàng ngay trên nó đọc như thanh công cụ, luôn ở MÉP;
 *   · hai Nhà nằm ở giữa chiều CAO (y ≈ 105..222 ở 844×390; 259..518 ở 1440×900) nên hàng sát đáy
 *     KHÔNG BAO GIỜ giao với Nhà — trong khi cột mép phải thì luôn phải né;
 *   · 6 avatar × (38..44px) + khe chỉ ~280..310px, vừa MỘT hàng ở MỌI chế độ yêu cầu (hẹp nhất là
 *     390×844 với 6×44 + 5×8 = 304px). Chỉ khi màn HẸP HƠN cả hàng (không có trong 4 chế độ) mới phải
 *     xếp lên hàng thứ hai — vẫn sát mép dưới, không bao giờ nhảy vào giữa sân.
 *
 * ⚠️ `yBase = band.bottom` là ĐÁY của dải trống giữa bar và dock (xem `defaultIconBand`) ⇒ icon nằm
 * sát dock nhất có thể. `blockedRects()` đã nới bar/dock thêm 6px nên hàng không dính vào dock.
 *
 * ⚠️ Hàm này KHÔNG tự né các avatar khác. Việc đó do `arrangeIconLayout()` làm (nó truyền danh sách
 * `placed` vào đây qua `blocked`), nhờ vậy bất biến "không 2 avatar giao nhau" đúng cho CẢ vị trí đã lưu.
 *
 * ⚠️ Tham số `columns` và hàm `iconColumnsFor()` của bản cũ ĐÃ BỊ XOÁ: chia cột dọc chỉ có nghĩa với
 * bố cục cột, mà bố cục đó chính là thứ vừa bị bỏ.
 */
export function defaultIconPx(
  slot: number,
  size: Vec2Size,
  half: number | Vec2,
  count: number,
  blocked: readonly Rect[] = [],
): Vec2 {
  const hx = finite((half as Vec2)?.x) ? (half as Vec2).x : (half as number);
  const hy = finite((half as Vec2)?.y) ? (half as Vec2).y : (half as number);
  const halfX = finite(hx) ? Math.max(0, hx) : 0;
  const halfY = finite(hy) ? Math.max(0, hy) : 0;
  const w = finite(size?.w) ? size.w : 0;
  const n = Math.max(1, Number.isInteger(count) && count > 0 ? count : 1);
  const i = Number.isInteger(slot) && slot >= 0 ? slot : 0;

  const band = defaultIconBand(halfY, size, blocked);
  const gap = ICON_MIN_GAP_PX;
  const margin = ICON_ROW_MARGIN_PX;

  /*
   * SỐ ICON VỪA MỘT HÀNG — suy từ bề rộng thật, không hard-code 6. `+ gap` ở tử số vì `k` icon cần
   * `k*đường_kính + (k-1)*gap`; giải ra `k = floor((avail + gap) / (đường_kính + gap))`.
   */
  const stepX = 2 * halfX + gap;
  const avail = Math.max(0, w - 2 * margin);
  const perRow = Math.max(1, Math.floor((avail + gap) / Math.max(1, stepX)));

  const row = Math.floor(i / perRow);
  const col = i % perRow;
  /*
   * ⚠️ TRỪ THÊM `ICON_MIN_GAP_PX` so với `band.bottom`: `blockedRects()` đã nới dock 6px, nhưng chiều
   * cao dock có thể ĐỔI mà không có `resize` (chữ xuống dòng khi đổi ngôn ngữ/ font nạp muộn). Hàng chỉ
   * cách dock 6px thì chỉ cần dock cao thêm vài px là icon bị panel phủ ⇒ cú bấm vào avatar rơi xuống
   * dock (đo được MỘT lần: 1/3 cú bấm bị "nuốt" trong bài tap của `verify-arena-drag.mjs`; lần chạy sau
   * lại sạch nên đây là biên rất sát). Thêm đúng một khe là đủ chịu sai số mà vẫn dán mép dưới.
   */
  const yBase = band.bottom - ICON_MIN_GAP_PX;
  const pos = {
    x: margin + halfX + col * stepX,
    /* Hàng thứ 2 trở đi xếp LÊN TRÊN, cách đúng một khe — vẫn dán mép dưới. */
    y: yBase - row * (2 * halfY + gap),
  };
  /*
   * ⚠️ KẸP LẦN CUỐI: Nhà/ bar/ dock có thể cắt vào hàng ở màn rất thấp, và `arrangeIconLayout` còn
   * truyền thêm các avatar đã đặt vào `blocked`. `clampOutOfRects` giải chính xác, không lặp.
   */
  return clampOutOfRects(pos, { x: halfX, y: halfY }, size, blocked);
}

/**
 * CỠ AVATAR NHỎ NHẤT ĐƯỢC PHÉP khi phải thu nhỏ để vừa một cột dọc (px).
 *
 * ⚠️⚠️ VÌ SAO 30 VÀ VÌ SAO PHẢI CÓ NGƯỠNG: ở 740×360 dải đen bên trái rộng 105px nhưng vùng trống DỌC
 * (giữa bar 51px và dock 36px) chỉ còn ~223px — một cột 6 avatar cỡ CSS 38px cần
 * `6×38 + 5×8 = 268px` ⇒ KHÔNG vừa. Thu xuống 30px thì `6×30 + 5×8 = 220px` ⇒ vừa, và đó là cách duy
 * nhất giữ ĐÚNG yêu cầu "xếp dọc" của người dùng ở chế độ này mà không tràn vào ảnh sân.
 * Nhỏ hơn nữa thì hàng nút khó bấm hơn cả việc chia 2 cột ⇒ dùng 2 CỘT (đã cho phép) rồi mới lùi về
 * HÀNG. Ghi rõ số đo ở đây để lần sau không ai hạ ngưỡng xuống "cho vừa" mà không biết mình đang đánh
 * đổi vùng chạm.
 */
export const ICON_MIN_SIZE_PX = 30;

/**
 * MÉP TRÁI/ PHẢI CỦA CANVAS THEO PX KHUNG NHÌN — nguồn duy nhất để biết "dải đen hai bên".
 *
 * ⚠️⚠️ PHẢI ĐO TỪ DOM (`#arena canvas` `getBoundingClientRect()`), KHÔNG được tự tính lại công thức
 * `Scale.FIT`: Phaser đã đặt kích thước/ vị trí canvas thật (có thể lệch do letterbox, DPR, thanh cuộn),
 * và một công thức chép tay sẽ đúng ở một chế độ rồi sai ở chế độ khác — đúng loại lỗi "hai đường tính
 * toạ độ" mà `ArenaHudHost.worldFromScreen` đã cảnh báo.
 */
export interface CanvasBands {
  left: number;
  right: number;
}

/** Kết quả bố cục MẶC ĐỊNH: dùng cột dọc trong dải đen, hay hàng ngang ở mép dưới. */
export interface AvatarLayout {
  mode: 'column' | 'row';
  /** Nửa cỡ avatar PHẢI dùng (có thể nhỏ hơn cỡ CSS để vừa cột). */
  half: Vec2;
  /** Vị trí mặc định theo slot (px khung nhìn, đã né bar/dock/ Nhà). */
  positions: Vec2[];
}

/**
 * BỐ CỤC MẶC ĐỊNH CHO AVATAR — CỘT DỌC TRONG DẢI ĐEN BÊN TRÁI khi có, ngược lại HÀNG NGANG mép dưới.
 *
 * ⚠️⚠️ VÌ SAO CẦN (yêu cầu người dùng 02/10/2026): *"Phần avatar trong arena màn hình ngang để vậy rất
 * xấu, trong khi cạnh trái còn khoảng hở. Hãy để avatar xếp dọc ngoài ảnh sân."*
 * Màn NGANG (844×390, 740×360) có canvas FIT hẹp hơn khung nhìn ⇒ hai DẢI ĐEN hai bên (đo được 97px và
 * 70px). Đó là không gian CHẾT: đặt avatar vào đó thì không che sân, không đè Nhà, và không "lơ lửng
 * giữa bãi chiến". Màn DỌC (390×844) và desktop (1440×900) KHÔNG có dải bên ⇒ giữ hàng ngang mép dưới.
 *
 * ⚠️ Cột phải NẰM GỌN trong dải: dải hẹp hơn bề rộng avatar ⇒ LÙI VỀ hàng (không bao giờ tràn vào sân).
 * ⚠️ Cột cao hơn vùng trống (bar→dock) ⇒ THU NHỎ avatar (tới `ICON_MIN_SIZE_PX`), nếu vẫn không đủ thì
 * chia 2 CỘT trong dải, cuối cùng mới lùi về hàng. Không đường nào tràn ra ngoài khung nhìn.
 * ⚠️ Hàm THUẦN (nhận `canvas` đã đo) nên khoá được bằng số trong test cho cả 4 chế độ.
 */
export function layoutDefaultAvatars(
  count: number,
  size: Vec2Size,
  half: number | Vec2,
  blocked: readonly Rect[] = [],
  canvas: CanvasBands | null = null,
): AvatarLayout {
  const hx = finite((half as Vec2)?.x) ? (half as Vec2).x : (half as number);
  const hy = finite((half as Vec2)?.y) ? (half as Vec2).y : (half as number);
  const baseHalfX = finite(hx) ? Math.max(0, hx) : 0;
  const baseHalfY = finite(hy) ? Math.max(0, hy) : 0;
  const n = Math.max(1, Number.isInteger(count) && count > 0 ? count : 1);
  const vw = finite(size?.w) ? size.w : 0;
  const vh = finite(size?.h) ? size.h : 0;

  /** Hàng ngang ở mép dưới — bố cục nền của mọi chế độ KHÔNG có dải bên, và là lưới an toàn. */
  const rowFallback = (): AvatarLayout => ({
    mode: 'row',
    half: { x: baseHalfX, y: baseHalfY },
    positions: Array.from({ length: n }, (_, i) => defaultIconPx(i, size, { x: baseHalfX, y: baseHalfY }, n, blocked)),
  });

  if (!canvas || vw <= 0 || vh <= 0) return rowFallback();
  const leftBand = Math.max(0, Math.min(vw, canvas.left));
  const rightBand = Math.max(0, Math.min(vw, vw - canvas.right));
  const need = 2 * baseHalfX + 2; // dải phải rộng ít nhất bằng bề rộng avatar + khe, không thì tràn vào sân
  /*
   * ⚠️⚠️ LUÔN ƯU TIÊN DẢI **TRÁI** — YÊU CẦU NGƯỜI DÙNG 02/10/2026 (BUG ĐÃ ĐO ĐƯỢC):
   * *"Màn hình ngang và màn hình dọc đang bị avatar bên phải, để lại bên trái"*.
   *
   * Bản trước chọn dải RỘNG HƠN (`useLeft = leftBand >= rightBand`). Nghe hợp lý, nhưng `#stage` KHÔNG
   * canh giữa canvas tuyệt đối: ở 960×440 đo được `canvas.left = 112,5` còn dải phải `113` — lệch NỬA
   * PIXEL là đủ để `>=` sai ⇒ avatar nhảy sang PHẢI ở một số khổ màn (960×440, 1024×500 đo được), trong
   * khi 844×390 lại sang trái. Bên nào cũng "hợp lệ" nên không có gì đỏ — chỉ người dùng thấy sai.
   *
   * Luật mới: dải TRÁI đủ rộng ⇒ dùng TRÁI, bất kể phải rộng hơn bao nhiêu. Chỉ khi trái QUÁ HẸP mới
   * xét phải (màn bất đối xứng thật), và nếu phải cũng hẹp thì lùi về HÀNG NGANG mép dưới.
   */
  const useLeft = leftBand >= need || rightBand < need;
  const bandW = useLeft ? leftBand : rightBand;
  if (bandW < need) return rowFallback();

  const bandLeft = useLeft ? 0 : canvas.right;
  const bandRight = useLeft ? leftBand : vw;
  const gap = ICON_MIN_GAP_PX;
  const band = defaultIconBand(baseHalfY, size, blocked);
  const bandH = Math.max(0, band.bottom - band.top);

  /*
   * CHỌN CỠ: ưu tiên cỡ CSS. Chỉ thu nhỏ khi một cột cỡ CSS không đủ cao, và không nhỏ hơn ngưỡng chạm.
   * Icon trên màn là HÌNH VUÔNG nên dùng MỘT cạnh `d` cho cả hai chiều.
   */
  let d = 2 * Math.min(baseHalfX, baseHalfY);
  let cols = 1;
  if (bandH < n * d + (n - 1) * gap) {
    const dFit = Math.floor((bandH - (n - 1) * gap) / n);
    if (dFit >= ICON_MIN_SIZE_PX && dFit < d) {
      d = dFit;
    } else {
      const rows = Math.ceil(n / 2);
      if (bandW >= 2 * d + gap && bandH >= rows * d + (rows - 1) * gap) {
        cols = 2;
      } else {
        return rowFallback();
      }
    }
  }
  const dh = d / 2;
  const halfOut: Vec2 = { x: dh, y: dh };
  const positions: Vec2[] = [];
  if (cols === 1) {
    const blockH = n * d + (n - 1) * gap;
    const y0 = band.top + Math.max(0, (bandH - blockH) / 2);
    const x = Math.max(bandLeft + dh, Math.min(bandRight - dh, bandLeft + bandW / 2));
    for (let i = 0; i < n; i++) {
      positions.push(clampOutOfRects({ x, y: y0 + dh + i * (d + gap) }, halfOut, size, blocked));
    }
  } else {
    const rows = Math.ceil(n / 2);
    const stepX = d + gap;
    const blockW = cols * d + (cols - 1) * gap;
    const x0 = bandLeft + Math.max(dh, (bandW - blockW) / 2) + dh;
    const blockH = rows * d + (rows - 1) * gap;
    const y0 = band.top + Math.max(0, (bandH - blockH) / 2);
    for (let i = 0; i < n; i++) {
      const col = Math.floor(i / rows);
      const row = i % rows;
      positions.push(clampOutOfRects({ x: x0 + col * stepX, y: y0 + dh + row * (d + gap) }, halfOut, size, blocked));
    }
  }
  return { mode: 'column', half: halfOut, positions };
}

/**
 * KHE HỞ DỌC DÙNG ĐƯỢC CHO ICON — phần bù của bar/dock trên trục dọc, tính theo TÂM icon.
 *
 * ⚠️ `bottom` chính là chỗ `defaultIconPx()` đặt HÀNG avatar (sát dock nhất có thể); `top` là mép dưới
 * của bar. Hai số này là "dải trống" mà mọi vị trí mặc định phải nằm trong.
 */
function defaultIconBand(halfY: number, size: Vec2Size, blocked: readonly Rect[]): { top: number; bottom: number } {
  const w = finite(size?.w) ? size.w : 0;
  const h = finite(size?.h) ? size.h : 0;
  /*
   * Khe hở DỌC giữa các hình cấm (bar/dock trải hết bề ngang nên chỉ còn trục dọc để chia).
   * Mặc định: cả khung nhìn.
   */
  let top = halfY;
  let bottom = Math.max(halfY, h - halfY);
  for (const r of blocked) {
    if (!(r.w > 0 && r.h > 0)) continue;
    /*
     * ⚠️ Chỉ né hình cấm PHỦ HẾT bề ngang (bar/dock). Hình cấm cục bộ (một nút lẻ) không được thu hẹp
     * cả cột — cột này nằm ở mép phải, chưa chắc giao với nút đó.
     */
    if (r.x > 0 || r.x + r.w < w - 1) continue;
    if (r.y <= halfY) top = Math.max(top, r.y + r.h + halfY);
    else bottom = Math.min(bottom, r.y - halfY);
  }
  if (bottom < top) {
    // màn quá thấp: bar + dock phủ kín ⇒ đành đặt giữa khung (không có chỗ trống thật)
    top = halfY;
    bottom = Math.max(halfY, h - halfY);
  }
  return { top, bottom };
}

/*
 * ⚠️ `iconColumnsFor()` ĐÃ BỊ XOÁ (02/10/2026). Nó chọn số CỘT DỌC để avatar không chồng khi bố cục
 * mặc định còn là cột ở mép phải. Bố cục nay là MỘT HÀNG NGANG (`defaultIconPx`), nên "số cột" không
 * còn nghĩa gì; bất biến "không chồng nhau" được khoá ở tầng khác — `arrangeIconLayout()` dưới đây,
 * đúng cho CẢ vị trí người chơi đã kéo (thứ mà cách đếm cột không xử lý được).
 */

/** BỀ RỘNG MỘT VÙNG NEO (px) — dùng cho CẢ hai chiều: bề rộng 3 vùng ngang VÀ chiều cao 2 hàng. */
export const ZONE_SIDE_W = 96;
/** Bề rộng tối thiểu của một vùng mép — màn hẹp thì vùng giữa co lại, vùng mép không biến mất. */
export const ZONE_EDGE_MIN_W = 36;

/** Hình chữ nhật theo hệ toạ độ KHUNG NHÌN (gốc trên-trái). */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * CHIA KHUNG NHÌN THÀNH 6 VÙNG NEO — hàm thuần, test được bằng số.
 *
 * ⚠️ VÌ SAO PHẢI NEO THEO VÙNG: nếu lưu `x/y` TUYỆT ĐỐI thì xoay màn hình là nút rơi ra ngoài
 * (toạ độ của màn 844×390 vô nghĩa trên 390×844) và người chơi mất nút — đúng lỗi mà yêu cầu #5 của
 * task nhắc. Neo `(zone, fx, fy)` chỉ mã hoá "ở khu nào, lệch bao nhiêu phần trăm trong khu đó", nên
 * `resize`/`orientationchange` chỉ cần tính lại hình chữ nhật của vùng là vị trí luôn hợp lệ.
 *
 * ⚠️ `ZONE_SIDE_W` phải ĐỦ RỘNG CHO NÚT + KHE: 96px chứa nút 44px và còn 26px mỗi bên, nên icon ở
 * hai vùng cạnh nhau không bao giờ chồng — kể cả ở màn hẹp nhất (320px: ba vùng chia đều ≈ 106px).
 */
export function anchorZones(size: Vec2Size): Record<DragZone, Rect> {
  const w = Math.max(0, finite(size?.w) ? size.w : 0);
  const h = Math.max(0, finite(size?.h) ? size.h : 0);
  const side = Math.max(ZONE_EDGE_MIN_W, Math.min(ZONE_SIDE_W, w / 3));
  const midW = Math.max(0, w - side * 2);
  /* Khe trên/ dưới cùng khoá `side` để 3 cột × 2 hàng là một lưới thật, không phải 6 số rời. */
  const topH = Math.max(0, Math.min(side, h / 2));
  const botH = Math.max(0, Math.min(side, h / 2));
  return {
    tl: { x: 0, y: 0, w: side, h: topH },
    tc: { x: side, y: 0, w: midW, h: topH },
    tr: { x: side + midW, y: 0, w: side, h: topH },
    bl: { x: 0, y: h - botH, w: side, h: botH },
    bc: { x: side, y: h - botH, w: midW, h: botH },
    br: { x: side + midW, y: h - botH, w: side, h: botH },
  };
}

/**
 * ĐỔI `(zone, fx, fy)` THÀNH TOẠ ĐỘ TÂM ICON (px) — đã KẸP trong khung.
 *
 * ⚠️ Vùng giữa có thể co xuống 0 bề rộng (màn cực hẹp) ⇒ tâm rơi đúng `x + 0/2`; `clampIconPos`
 * vẫn giữ nút trong khung nên không bao giờ ra ngoài.
 */
export function anchorToPx(anchor: IconAnchor, size: Vec2Size, half: number | Vec2): Vec2 {
  const zones = anchorZones(size);
  const zone = zones[DRAG_ZONES.includes(anchor?.zone) ? anchor.zone : 'tc'];
  const fx = finite(anchor?.fx) ? clamp01(anchor.fx) : 0.5;
  const fy = finite(anchor?.fy) ? clamp01(anchor.fy) : 0.5;
  return clampIconPos({ x: zone.x + zone.w * fx, y: zone.y + zone.h * fy }, half, size);
}

/**
 * ĐỔI TOẠ ĐỘ TÂM ICON THÀNH `(zone, fx, fy)` — hàm NGƯỢC của `anchorToPx`.
 *
 * ⚠️⚠️ HIỆN KHÔNG CÓ CHỖ NÀO TRONG `src/` GỌI HÀM NÀY — ĐỌC TRƯỚC KHI "DỌN CHO GỌN":
 * từ khi avatar là NÚT CỐ ĐỊNH (người dùng: *"avatar di chuyển theo tay là sai"*), thao tác thả KHÔNG
 * ghi vị trí nữa nên không còn đường nào sinh neo mới. Hàm được giữ vì:
 *   · nó là phép ĐỐI XỨNG của `anchorToPx` (đường ĐỌC vẫn sống: neo cũ trong `localStorage`, neo do
 *     công cụ/ test gieo), và `tests/arena-hud-drag.test.ts` khoá vòng `anchor → px → anchor` — thứ
 *     chứng minh vị trí lưu sống sót khi xoay màn hình;
 *   · nếu sau này mở lại đường cho người chơi tự sắp avatar thì đây là hàm phải dùng, KHÔNG được lưu
 *     thẳng toạ độ con trỏ (xem bài học lệch 832px ở chú thích `commitIconPos` cũ trong git history).
 * ⚠️ Vì sao phải đi qua vùng chứ không lưu thẳng px: xem `anchorZones`. Và vì sao chọn vùng theo TÂM
 * nút (không theo con trỏ): nút bị kẹp ở mép màn hình thì con trỏ có thể ở ngoài khung, lấy theo con
 * trỏ là lưu ra vùng không tồn tại.
 */
export function pxToAnchor(pos: Vec2, size: Vec2Size, half: number | Vec2): IconAnchor {
  const zones = anchorZones(size);
  const p = clampIconPos(pos, half, size);
  let best: DragZone = 'tc';
  let bestArea = -1;
  for (const z of DRAG_ZONES) {
    const r = zones[z];
    if (p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h) {
      return { zone: z, fx: clamp01((p.x - r.x) / (r.w || 1)), fy: clamp01((p.y - r.y) / (r.h || 1)) };
    }
    const area = r.w * r.h;
    if (area > bestArea) {
      bestArea = area;
      best = z;
    }
  }
  // rơi vào khe giữa các vùng (chỉ xảy ra khi khung 0×0 hoặc số rác) ⇒ vùng lớn nhất, tỉ lệ 0.5
  return { zone: best, fx: 0.5, fy: 0.5 };
}

/**
 * TÌM CHỖ ĐẶT ICON SAO CHO KHÔNG ĐÈ LÊN VẬT CẢN — hàm THUẦN, test được.
 *
 * ⚠️⚠️ VÌ SAO CẦN (bug đã đo được trong Chrome, Lead báo + tôi tái hiện): icon là lớp phủ `z-index`
 * CAO HƠN bar/dock, nên icon nào giao với một nút của HUD là NÚT ĐÓ MẤT BẤM (`elementFromPoint` ở
 * tâm nút trả về `arena-drag-icon`). Mặc định đã né sẵn, nhưng NGƯỜI CHƠI kéo icon tới đó thì vẫn tự
 * làm hỏng nút mà không có cách nào biết — nên phải kẹp LÚC THẢ.
 *
 * ⚠️ `getBoundingClientRect` của bar/dock đổi theo màn hình (đo được: bar 51px ở màn ngang thấp /
 * 75px ở desktop; dock 68px / 140px) nên KHÔNG được hard-code chiều cao — hàm nhận thẳng hình chữ
 * nhật ĐO ĐƯỢC từ DOM.
 *
 * ⚠️⚠️ LUẬT MỚI THÊM **NHÀ** VÀO DANH SÁCH VẬT CẢN — VÀ ĐÓ LÀ LÝ DO HÀM NÀY ĐƯỢC VIẾT LẠI.
 * Bản cũ chỉ giải MỘT CHIỀU trên trục DỌC, đúng khi mọi vật cản đều phủ HẾT bề ngang (bar/dock).
 * Sân mới có Nhà TO GẤP ~1,9 LẦN nằm ở hai MÉP (`ARENA_CORE_DRAW_R` ≈ 45,8 đơn vị thế giới): vật cản
 * KHÔNG phủ hết bề ngang, nên né theo trục dọc là SAI — icon sẽ bị đẩy lên/xuống mà vẫn nằm trong bề
 * ngang của Nhà, tức vẫn đè art Nhà. Cần né được theo CẢ HAI trục.
 *
 * ⚠️⚠️ VÌ SAO **KHÔNG LẶP ĐẨY** (cách bản đầu — ĐÃ SAI, ĐÃ CÓ TEST BẮT): đẩy khỏi bar có thể ném nút
 * vào dock và ngược lại ⇒ dao động vô hạn, hoặc kết thúc ở chỗ vẫn đè (đo được: `y = 10` với bar cao
 * 75px vẫn đè bar sau 3 lượt). Vòng lặp là cách giải SAI cho bài toán này.
 *
 * CÁCH ĐÚNG (một lượt, không lặp, không thể dao động): **liệt kê ỨNG VIÊN BIÊN** — với mỗi vật cản,
 * chiếu điểm mong muốn ra 4 phía (trái/ phải/ trên/ dưới) và 4 góc của nó; lọc bỏ ứng viên còn nằm
 * trong bất kỳ vật cản nào hoặc ra ngoài khung nhìn; chọn ứng viên GẦN ĐIỂM MONG MUỐN NHẤT.
 *
 * ⚠️ Vì sao tập ứng viên này là đủ: điểm tự do gần nhất luôn nằm trên BIÊN của một vật cản (nếu không
 * thì dịch về phía điểm mong muốn vẫn tự do). Mỗi biên cho đúng một điểm gần nhất ⇒ chỉ cần 4 điểm
 * mỗi vật cản, thêm 4 góc cho ca hai vật cản chồng nhau. Với ≤ 4 vật cản, đây là phép liệt kê rẻ và
 * CHÍNH XÁC, không phải xấp xỉ lặp.
 */
export function clampOutOfRects(pos: Vec2, half: number | Vec2, size: Vec2Size, blocked: readonly Rect[]): Vec2 {
  const hx = finite((half as Vec2)?.x) ? (half as Vec2).x : (half as number);
  const hy = finite((half as Vec2)?.y) ? (half as Vec2).y : (half as number);
  const halfX = finite(hx) ? Math.max(0, hx) : 0;
  const halfY = finite(hy) ? Math.max(0, hy) : 0;
  const h = { x: halfX, y: halfY };
  const start = clampIconPos(pos, h, size);

  /*
   * ⚠️⚠️ "CHẠM NHAU ĐÚNG BIÊN" KHÔNG PHẢI "GIAO NHAU" — DUNG SAI 1e-6 LÀ BẮT BUỘC (BUG ĐÃ ĐO ĐƯỢC).
   *
   * SỐ ĐO THẬT (740×360, `verify-arena-hud` §5): cột avatar lệch nhau **13px** giữa 3 avatar đầu và 3
   * avatar cuối — ảnh chụp cho thấy cột bị "gãy khúc". Nguyên nhân: `arrangeIconLayout` coi avatar đã
   * đặt là vật cản nới `ICON_MIN_GAP_PX` mỗi phía, nên HỘP CỦA AVATAR SAU CHẠM ĐÚNG HỘP VẬT CẢN của
   * avatar trước (khe = đúng 8px). Về toán học hai hộp chỉ CHẠM nhau, NHƯNG toạ độ mặc định là số thực
   * (`y0 = band.top + …`) nên hiệu của chúng là ~1e-13 chứ không phải 0 ⇒ `p.y - halfY < r.y + r.h`
   * đúng ⇒ bị coi là giao ⇒ `clampOutOfRects` đẩy avatar sang ứng viên gần nhất (13px sang phải).
   *
   * Dung sai 1e-6px chỉ để loại sai số dấu phẩy động — nó KHÔNG nới bất biến "khe ≥ 8px" tới mức nhìn
   * thấy được (8px − 1e-6 vẫn là 8px).
   */
  const hits = (p: Vec2): boolean =>
    blocked.some(
      (r) =>
        r.w > 0 &&
        r.h > 0 &&
        p.x + halfX > r.x + RECT_EPS &&
        p.x - halfX < r.x + r.w - RECT_EPS &&
        p.y + halfY > r.y + RECT_EPS &&
        p.y - halfY < r.y + r.h - RECT_EPS,
    );
  if (!hits(start)) return start;

  /*
   * ⚠️ Chỉ ứng viên HỢP LỆ mới được xét: phải (a) tự do, (b) nằm trong khung nhìn. Ứng viên trùng
   * nhau (nhiều vật cản cho cùng một điểm) không sao — chỉ tốn một phép so.
   */
  const candidates: Vec2[] = [];
  for (const r of blocked) {
    if (!(r.w > 0 && r.h > 0)) continue;
    const left = r.x - halfX;
    const right = r.x + r.w + halfX;
    const up = r.y - halfY;
    const down = r.y + r.h + halfY;
    candidates.push(
      { x: left, y: start.y },
      { x: right, y: start.y },
      { x: start.x, y: up },
      { x: start.x, y: down },
      { x: left, y: up },
      { x: right, y: up },
      { x: left, y: down },
      { x: right, y: down },
    );
  }

  let best: Vec2 | null = null;
  let bestDistance = Infinity;
  for (const c of candidates) {
    const p = clampIconPos(c, h, size);
    if (hits(p)) continue;
    const d = Math.hypot(p.x - start.x, p.y - start.y);
    if (d < bestDistance) {
      bestDistance = d;
      best = p;
    }
  }
  /*
   * ⚠️ KHÔNG có ứng viên tự do nào (màn thấp tới mức bar + dock phủ kín, hoặc Nhà chiếm hết chỗ né)
   * ⇒ giữ vị trí kẹp ban đầu. Đây là lựa chọn CÓ CHỦ Ý: thà icon nằm đè lên vật cản còn hơn icon BIẾN
   * MẤT (người chơi mất hẳn tính năng kéo thả). Ghi rõ ở đây để không ai "sửa" thành `return null`.
   */
  return best ?? start;
}

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * BỐ CỤC AVATAR — BẤT BIẾN "KHÔNG BAO GIỜ 2 AVATAR GIAO NHAU"
 * ════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️⚠️ VÌ SAO CẦN HÀM NÀY CHỨ KHÔNG CHỈ "CHIA ĐỀU MẶC ĐỊNH": bố cục mặc định không phải đường duy
 * nhất sinh vị trí. Người chơi KÉO avatar rồi vị trí đó được lưu và nạp lại ở mọi lần vào màn (kể cả
 * sau khi xoay/ đổi kích thước) — mà hai neo đã lưu hoàn toàn có thể trỏ về CÙNG một chỗ (người chơi
 * kéo 2 avatar về một điểm, hoặc dữ liệu cũ rác). Chỉ "mặc định không chồng" là KHÔNG đủ: bất biến
 * phải đúng cho cả vị trí đã lưu, nếu không thì một lần kéo là nút nằm dưới vĩnh viễn không bấm được.
 *
 * Cách làm: xếp LẦN LƯỢT theo thứ tự avatar trong DOM; mỗi avatar được đặt rồi trở thành VẬT CẢN cho
 * các avatar sau (hộp của nó nới thêm `ICON_MIN_GAP_PX`). `clampOutOfRects` là phép giải CHÍNH XÁC
 * trên tập vật cản (không vòng lặp đẩy ⇒ không dao động), nên kết quả ổn định: nạp lại trang cho ra
 * ĐÚNG cùng bố cục, không "nhảy" giữa các lần vẽ.
 *
 * ⚠️ Vẫn ưu tiên vị trí NGƯỜI CHƠI ĐÃ LƯU (chỉ dịch khi nó vi phạm bất biến): tính năng "nhớ vị trí
 * avatar" là yêu cầu đã có, nên hàm này KHÔNG được biến thành "ép hết về hàng mặc định".
 */

/** Một avatar cần đặt chỗ — `saved = null` nghĩa là chưa từng kéo (dùng hàng mặc định). */
export interface IconLayoutItem {
  key: IconKey;
  saved: IconAnchor | null;
}

/** Kết quả đặt chỗ — `from` cho biết vị trí được TÔN TRỌNG hay phải rơi về mặc định. */
export interface IconLayoutPlacement {
  key: IconKey;
  pos: Vec2;
  from: 'saved' | 'default';
}

/** Hộp của icon nới thêm `gap` mỗi phía — vật cản dùng cho avatar đặt SAU (đảm bảo khe ≥ `gap`). */
function iconBlockerRect(pos: Vec2, half: Vec2, gap: number): Rect {
  return {
    x: pos.x - half.x - gap,
    y: pos.y - half.y - gap,
    w: 2 * half.x + 2 * gap,
    h: 2 * half.y + 2 * gap,
  };
}

/** Icon ở `pos` có giao một hình cấm nào không (đã tính nửa kích thước). */
function hitsAnyRect(pos: Vec2, half: Vec2, rects: readonly Rect[]): boolean {
  /* ⚠️ `RECT_EPS` — cùng lý do như `clampOutOfRects.hits`: CHẠM đúng biên KHÔNG phải giao (xem hằng số). */
  return rects.some(
    (r) =>
      r.w > 0 &&
      r.h > 0 &&
      pos.x + half.x > r.x + RECT_EPS &&
      pos.x - half.x < r.x + r.w - RECT_EPS &&
      pos.y + half.y > r.y + RECT_EPS &&
      pos.y - half.y < r.y + r.h - RECT_EPS,
  );
}

/**
 * ĐẶT CHỖ CHO MỌI AVATAR — hàm THUẦN, test được. Bảo đảm: mọi kết quả nằm TRONG khung nhìn, KHÔNG
 * giao `blocked` (bar/ dock/ 2 Nhà) và KHÔNG avatar nào giao avatar nào (khe ≥ `ICON_MIN_GAP_PX`).
 *
 * `saved` được ưu tiên; chỉ khi nó giao một vật cản (kể cả avatar đã đặt) mới rơi về ô mặc định của
 * slot đó. Thứ tự xét = thứ tự trong mảng ⇒ kết quả TẤT ĐỊNH với cùng đầu vào.
 *
 * ⚠️ `defaults` LÀ VỊ TRÍ MẶC ĐỊNH THEO SLOT (do `layoutDefaultAvatars()` tính: cột dọc trong dải đen
 * hoặc hàng ngang mép dưới). Bỏ trống ⇒ tự rơi về hàng ngang (`defaultIconPx`), giữ nguyên hành vi cũ
 * cho mọi chỗ gọi/ test cũ. Dù có `defaults` hay không, vị trí mặc định VẪN phải né vật cản đã đặt —
 * đó là điều kiện để bất biến "không chồng" đúng với cả bố cục cột.
 */
export function arrangeIconLayout(
  items: readonly IconLayoutItem[],
  size: Vec2Size,
  half: number | Vec2,
  blocked: readonly Rect[] = [],
  defaults?: readonly Vec2[],
): IconLayoutPlacement[] {
  const hx = finite((half as Vec2)?.x) ? (half as Vec2).x : (half as number);
  const hy = finite((half as Vec2)?.y) ? (half as Vec2).y : (half as number);
  const h: Vec2 = { x: finite(hx) ? Math.max(0, hx) : 0, y: finite(hy) ? Math.max(0, hy) : 0 };
  const n = Math.max(1, items.length);
  const placed: Rect[] = [];
  const out: IconLayoutPlacement[] = [];
  /** Vị trí mặc định của slot — có `defaults` thì dùng, không thì tự tính hàng ngang. */
  const defaultOf = (slot: number, obstacles: readonly Rect[]): Vec2 => {
    const base = defaults?.[slot] ?? defaultIconPx(slot, size, h, n, []);
    return clampOutOfRects(base, h, size, obstacles);
  };

  items.forEach((item, slot) => {
    /* Avatar đã đặt là vật cản cho avatar sau — nới `gap` để khe thật ≥ `gap`. */
    const obstacles = [...blocked, ...placed];
    let from: IconLayoutPlacement['from'] = item.saved ? 'saved' : 'default';
    let pos = item.saved
      ? clampOutOfRects(anchorToPx(item.saved, size, h), h, size, obstacles)
      : defaultOf(slot, obstacles);
    /*
     * ⚠️ LƯỚI AN TOÀN CUỐI: `clampOutOfRects` trả về vị trí KẸP BAN ĐẦU khi không tìm được ứng viên tự
     * do nào (xem chú thích ở hàm đó). Lúc đó vị trí đã lưu có thể vẫn giao avatar khác ⇒ thử ô MẶC
     * ĐỊNH của slot này (nơi luôn còn chỗ khi có ≤ vài avatar). Đây là lý do `from` tồn tại: công cụ đo
     * đọc được "vị trí này phải bỏ vì vi phạm bất biến".
     */
    if (hitsAnyRect(pos, h, obstacles)) {
      pos = defaultOf(slot, obstacles);
      from = 'default';
    }
    out.push({ key: item.key, pos, from });
    placed.push(iconBlockerRect(pos, h, ICON_MIN_GAP_PX));
  });

  return out;
}

/** Giá trị hợp lệ để ghi localStorage — `null` = bỏ (không lưu rác). */
function sanitizeAnchor(v: unknown): IconAnchor | null {
  const a = v as Partial<IconAnchor> | null;
  if (!a || typeof a !== 'object') return null;
  if (typeof a.zone !== 'string' || !DRAG_ZONES.includes(a.zone as DragZone)) return null;
  /*
   * ⚠️ `Number.isFinite` CHỨ KHÔNG `typeof === 'number'`: `typeof NaN === 'number'` và
   * `typeof Infinity === 'number'` đều ĐÚNG, nên chỉ kiểm `typeof` là `NaN` ĐI QUA SẴN.
   *
   * ⚠️⚠️ VÀ PHẢI CHẶN **TRƯỚC** KHI KẸP: `clamp01(NaN)` = `Math.max(0, Math.min(1, NaN))` = `NaN`
   * (vì mọi so sánh với `NaN` đều `false` ⇒ `Math.min` trả `NaN`), nên kẹp KHÔNG cứu được gì —
   * `NaN` chui thẳng vào `localStorage` và ở nhịp sau thành `left: NaNpx` ⇒ icon vô hình, người
   * chơi mất nút mà không có lỗi nào được ném ra. Đây là bẫy đã sập khi viết hàm này.
   */
  if (!finite(a.fx) || !finite(a.fy)) return null;
  return { zone: a.zone as DragZone, fx: clamp01(a.fx), fy: clamp01(a.fy) };
}

/**
 * KHOÁ LƯU VỊ TRÍ ICON — localStorage, KHOA RIÊNG, **`v2`**.
 *
 * ⚠️⚠️ VÌ SAO ĐANG Ở `v2` (bump 02/10/2026): khoá `hud.v1` chứa neo của các ĐỜI BỐ CỤC CŨ (5 icon land
 * → cột mép phải → khối 2×3 giữa sân). Người chơi đã chơi từ trước nên dữ liệu đó vẫn nằm trong máy;
 * kế thừa nó là kéo bố cục mới về đúng chỗ xấu cũ, mà KHÔNG có cách nào phân biệt "neo người chơi cố ý
 * đặt" với "rác của bố cục cũ". Đổi KHOÁ làm toàn bộ dữ liệu cũ bị bỏ ở tầng thấp nhất, không cần
 * migrate. `tests/arena-hud-drag.test.ts` khoá cả tên khoá lẫn hành vi "v1 bị bỏ qua".
 *
 * ⚠️⚠️ HIỆN KHÔNG CÓ ĐƯỜNG GHI NÀO TỪ THAO TÁC KÉO — ĐỌC TRƯỚC KHI "SỬA CHO CÓ VẺ ĐỦ":
 * avatar là NÚT CỐ ĐỊNH của HUD (người dùng: *"avatar di chuyển theo tay là sai"*), nên thả tay KHÔNG
 * ghi vị trí nữa (`commitIconPos()` đã bị xoá). Khoá này vẫn còn đường ĐỌC vì:
 *   · neo cũ (bất kỳ khoá nào trước đây, hoặc do công cụ/ test gieo) phải được `arrangeIconLayout`
 *     xử lý an toàn — đó là chỗ bất biến "không 2 avatar giao nhau" được kiểm với dữ liệu thật;
 *   · xoá hẳn kho + `sanitizeAnchor` là quên mất bài học "`NaN` chui vào `left: NaNpx` ⇒ mất nút im lặng".
 * Nếu sau này muốn cho người chơi TỰ SẮP avatar thì phải mở lại đường ghi (qua `arrangeIconLayout`),
 * KHÔNG được ghi thẳng toạ độ con trỏ như bản cũ.
 *
 * ⚠️⚠️ VÌ SAO KHÔNG NHÉT VÀO SAVE (`tdkungfu.save.v3` / `MetaState`): `src/save/storage.ts` ghi rõ
 * luật bất di bất dịch của repo — mọi trường mới trong `meta` là phải đụng `SAVE_VERSION` và
 * `migrateMeta()`, tức rủi ro MẤT TIẾN TRÌNH của người chơi thật (vàng/ class/ màn đã qua). Đây chỉ
 * là VỊ TRÍ NÚT TRÊN MÀN HÌNH — không phải tiến trình, không đáng để chạm vào save. Repo đã có
 * đúng khuôn mẫu này cho thứ cùng loại: `tdk_lang` (`i18n/index.ts`), `tdkungfu.sound`
 * (`ui/sound.ts`), `tdkungfu.prefs` (`game.ts`).
 *
 * ⚠️ VÀ KHÔNG NHÉT VÀO `tdkungfu.prefs`: khoá đó được `game.ts` ghi lại NGUYÊN KHỐI
 * (`JSON.stringify({ autoNextWave, autoSkill })`) mỗi lần đổi tuỳ chọn ⇒ trường thêm vào sẽ bị
 * NUỐT. Khoá riêng thì không ai ghi đè.
 *
 * Hình dạng payload: `{ v: 2, icons: { "<key>": { zone, fx, fy } } }`. Icon nào KHÔNG có trong
 * `icons` thì dùng bố cục MẶC ĐỊNH (một hàng ngang ở mép dưới, xem `defaultIconPx`).
 */
export const ARENA_ICON_POS_KEY = 'tdkungfu.arena.hud.v2';

export interface IconStore {
  get(key: IconKey): IconAnchor | null;
  set(key: IconKey, anchor: IconAnchor): void;
}

interface StoredShape {
  v: number;
  icons: Record<string, IconAnchor>;
}

/** localStorage có thể NÉM (chế độ riêng tư/ chặn cookie) ⇒ mọi truy cập đi qua try/catch. */
function readStore(): StoredShape {
  try {
    if (typeof localStorage === 'undefined') return { v: 2, icons: {} };
    const raw = localStorage.getItem(ARENA_ICON_POS_KEY);
    if (!raw) return { v: 2, icons: {} };
    const parsed = JSON.parse(raw) as Partial<StoredShape>;
    const icons: Record<string, IconAnchor> = {};
    if (parsed && typeof parsed.icons === 'object' && parsed.icons) {
      for (const [k, v] of Object.entries(parsed.icons)) {
        const a = sanitizeAnchor(v);
        if (a && k) icons[k] = a;
      }
    }
    return { v: 2, icons };
  } catch {
    // save rác ⇒ coi như chưa có gì; KHÔNG xoá khoá (có thể chỉ là JSON hỏng tạm thời)
    return { v: 2, icons: {} };
  }
}

function writeStore(store: StoredShape): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(ARENA_ICON_POS_KEY, JSON.stringify({ v: 2, icons: store.icons }));
  } catch {
    /* chế độ riêng tư: chỉ nhớ trong phiên, KHÔNG được làm sập HUD vì không ghi được vị trí nút */
  }
}

/** Ghi đè kho vị trí — CHỈ dùng cho test (vitest không có `localStorage` thật). */
let storeOverride: IconStore | null = null;

/** Kho vị trí ĐANG dùng: mặc định localStorage, có thể thay bằng `memoryIconStore()` cho test. */
export function arenaIconStore(): IconStore {
  if (storeOverride) return wrapIconStore(storeOverride);
  return {
    get: (key) => readStore().icons[key] ?? null,
    set: (key, anchor) => {
      const a = sanitizeAnchor(anchor);
      if (!a) return; // không bao giờ ghi rác xuống localStorage
      const store = readStore();
      store.icons[key] = a;
      writeStore(store);
    },
  };
}

/** Thay kho vị trí (test). Trả về `null` để khôi phục kho mặc định. */
export function setArenaIconStore(store: IconStore | null): void {
  storeOverride = store;
}

/**
 * BỌC MỘT KHO BẤT KỲ BẰNG CÙNG BỘ LỌC `sanitizeAnchor` — dùng cho kho do TEST cấp.
 *
 * ⚠️⚠️ VÌ SAO CẦN: nếu chỉ kiểm rác ở kho mặc định (localStorage) thì một kho thay thế (test, hoặc
 * bản demo dùng kho trong bộ nhớ) sẽ nhận thẳng neo `NaN`/ vùng lạ ⇒ HUD ghi `left: NaNpx` và nút
 * biến mất. Lọc ở CỬA VÀO của `arenaIconStore()` là chỗ DUY NHẤT mọi đường ghi đi qua — kho thay thế
 * không thể lách.
 *
 * ⚠️ Vế ĐỌC cũng lọc: `get()` trả `null` cho neo rác ⇒ HUD rơi về vị trí MẶC ĐỊNH thay vì vẽ ở
 * `NaN`. Đọc sai mà vẫn "có giá trị" là kiểu lỗi im lặng tệ nhất.
 */
function wrapIconStore(store: IconStore): IconStore {
  return {
    get: (key) => sanitizeAnchor(store.get(key)),
    set: (key, anchor) => {
      const a = sanitizeAnchor(anchor);
      if (a) store.set(key, a);
    },
  };
}

/** Kho trong BỘ NHỚ — khuôn mẫu test của `localStorage` khi môi trường node không có nó. */
export function memoryIconStore(initial?: Record<IconKey, IconAnchor>): IconStore {
  const map = new Map<string, IconAnchor>(Object.entries(initial ?? {}));
  return {
    get: (k) => map.get(k) ?? null,
    set: (k, a) => void map.set(k, a),
  };
}

/** Bỏ vị trí đã lưu của một icon (dùng khi người chơi muốn về mặc định). */
export function forgetArenaIcon(key: IconKey): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const store = readStore();
    delete store.icons[key];
    writeStore(store);
  } catch {
    /* không ghi được ⇒ thôi, không có gì phải làm */
  }
}

/**
 * PHẦN SCENE MÀ HUD THẬT SỰ GỌI — HỢP ĐỒNG HẸP, KHÔNG PHẢI "CHO QUA TYPECHECK".
 *
 * ⚠️ VÌ SAO KHÔNG IMPORT THẲNG `ArenaBattleScene` Ở ĐÂY: file đó `import Phaser`, mà Phaser cần
 * DOM/ WebGL ⇒ mọi module import nó KHÔNG chạy được trong môi trường node của vitest (bài học có
 * thật đã ghi ở `arena-battle.ts`: `ReferenceError: window is not defined`). HUD là DOM thuần, nên
 * khai kiểu tối thiểu ở đây giữ cho nó test được headless, VÀ nói rõ HUD chỉ được phép chạm đúng
 * 4 phương thức — mọi thứ khác của scene là việc của scene.
 *
 * ⚠️⚠️ BÀI HỌC ĐÃ TRẢ GIÁ: `activeLand` TỪNG ĐƯỢC KHAI SAI Ở ĐÂY.
 * Bản trước khai `readonly activeLand: Land` trong khi scene có getter cùng tên — chép đúng hình
 * dạng getter thì hợp lệ, nhưng nếu ai đó khai thành HÀM (`activeLand()`) thì hai kiểu không tương
 * thích và lúc chạy sẽ là `TypeError: not a function` GIỮA TRẬN. Hợp đồng này phải phản chiếu ĐÚNG
 * thứ scene thật sự cung cấp, kể cả getter/ hàm/ tham số.
 *
 * ⚠️ Nay `activeLand`/`setLand` ĐÃ BỊ XOÁ cùng khái niệm land, nên HUD KHÔNG còn được phép hỏi scene
 * "land nào đang chọn" — luật mới là thả ở TOẠ ĐỘ, không có dải nào để chọn.
 *
 * `ArenaBattleScene` thoả interface này một cách cấu trúc (structural typing), KHÔNG cần `implements`.
 */
export interface ArenaHudHost {
  /** dựng nút tướng: class đã unlock + giá (giá do scene lấy từ `data/arena.ts`) */
  heroOptions(heroClasses: readonly ClassId[]): Array<{ classId: ClassId; cost: number }>;
  /** triệu hồi tướng tại TOẠ ĐỘ THẾ GIỚI (x, y). Sim kiểm toạ độ/ tiền và trả `ok:false` nếu sai. */
  summonHero(classId: ClassId, x: number, y: number): { ok: boolean; message: string };
  /**
   * MUA NÔNG DÂN TẠI TOẠ ĐỘ THẾ GIỚI (x, y) — cùng luật toạ độ với `summonHero`.
   *
   * ⚠️ `autoMine` PHÂN BIỆT CÚ BẤM VỚI KÉO-THẢ (yêu cầu người dùng 02/10/2026: *"Khi click vào
   * avatar farmer để mua (không phải kéo thả), farmer sẽ tự chọn mine ý farmer hơn để tới"*):
   *   · `true` ⇒ sim gán sẵn mỏ "ưng" (`ArenaWorld.preferredMine` — mỏ vắng nhất nửa sân mình);
   *   · bỏ trống/ `false` ⇒ sim để nông dân tự chọn mỏ GẦN ĐIỂM THẢ như cũ (người chơi đã chỉ định
   *     chỗ đứng thì "mỏ gần nhất" mới đúng ý).
   * HUD chỉ TRUYỀN Ý ĐỊNH, không tự chọn mỏ: luật vẫn nằm một chỗ ở tầng sim.
   */
  buyFarmer(x: number, y: number, autoMine?: boolean): { ok: boolean; message: string };
  /**
   * MỞ CỔNG BẮT ĐẦU TRẬN — HUD gọi SAU khi đếm lùi 3-2-1 hiện xong.
   *
   * ⚠️ Yêu cầu người dùng 02/10/2026: *"khi tôi click vào Arena, màn hình hiển thị nút `Bắt đầu`, tôi
   * phải click Bắt đầu, sẽ đếm lùi 3s (hiển thị số xuất hiện rồi biến mất 3,2,1,Bắt đầu, thì lúc đó
   * game mới bắt đầu)"*. Scene giữ cổng (`ArenaBattleScene.arenaStarted`) và KHÔNG tick sim cho tới khi
   * hàm này được gọi; HUD chỉ chạy phần NHÌN THẤY của đếm lùi.
   *
   * ⚠️ TUỲ CHỌN (`?`): host giả trong test/ công cụ không cần cài. Thiếu nó thì HUD vẫn hiện nút và
   * đếm lùi, nhưng cổng của scene không bao giờ mở — hiện tượng là "trận đứng ở 0:00".
   */
  startArenaNow?(): void;
  /**
   * QUY ĐỔI TOẠ ĐỘ MÀN HÌNH → TOẠ ĐỘ THẾ GIỚI (1000×600).
   *
   * ⚠️⚠️ ĐÂY LÀ ĐƯỜNG DUY NHẤT ĐÚNG — HUD KHÔNG ĐƯỢC TỰ VIẾT CÔNG THỨC.
   * Canvas Phaser chạy `Scale.FIT` với cỡ thiết kế 1000×600, nên tỉ lệ phụ thuộc kích thước canvas
   * THẬT (và có viền đen letterbox khi tỉ lệ màn hình khác 5:3). Công thức chép tay trong HUD sẽ
   * đúng ở đúng một kích thước màn hình rồi lệch ở mọi kích thước khác — mà triệu chứng là "tướng ra
   * lệch chỗ", rất khó lần ra. Scene là nơi duy nhất biết camera/ zoom, nên scene là nơi quy đổi.
   *
   * Hình dạng: `worldFromScreen(clientX, clientY)` — HAI tham số, nhận toạ độ `clientX`/ `clientY`
   * của `PointerEvent` (KHÔNG phải `pageX`/ `screenX`), trả `{ x, y }` trong hệ 1000×600.
   * `ArenaBattleScene` cài bằng `this.cameras.main.getWorldPoint(clientX, clientY)`.
   */
  worldFromScreen(clientX: number, clientY: number): { x: number; y: number };

  /**
   * HÌNH HỌC SÂN Ở KHỔ DỌC (2 tầng) — `null` khi đang ở khổ ngang/ desktop (hành vi cũ).
   *
   * ⚠️⚠️ VÌ SAO HUD CẦN (yêu cầu người dùng: *"fit độ cao chứ đừng kéo dãn ngang"* + *"1 cột dọc 6 avatar
   * bên trái"*): ở khổ dọc sân fit CHIỀU CAO nên bề ngang chỉ dùng ~292/412px, hai bên thừa DẢI TRỐNG
   * ~60px. Đó là chỗ xếp avatar (đúng kiểu màn ngang: *"avatar xếp dọc ngoài ảnh sân"*).
   *
   * ⚠️ HUD KHÔNG ĐƯỢC TỰ ĐO DẢI NÀY: `#arena canvas` phủ kín khung nhìn (dải trống nằm BÊN TRONG canvas
   * vì hai camera có viewport hẹp hơn), nên `getBoundingClientRect()` của canvas luôn ra `left = 0` ⇒
   * đo kiểu đó là tưởng "không có dải" rồi xếp hàng ngang ĐÈ LÊN SÂN. Chỉ scene biết `bands.top.x/w`.
   *
   * ⚠️ `avatarColumns` là quyết định của SCENE (dải trống có đủ rộng cho avatar không — ngưỡng phải
   * khớp `layoutDefaultAvatars`); HUD chỉ THI HÀNH, không tự đo lại:
   *   · `true`  ⇒ dùng `{left, right}` làm dải đen ⇒ cột dọc ngoài sân;
   *   · `false` ⇒ scene đã chừa chỗ cho HÀNG NGANG mép dưới ⇒ HUD phải xếp hàng (đừng đặt cột).
   *
   * ⚠️ Có giá trị (khác `null`) cũng có nghĩa "2 TẦNG ĐANG BẬT" ⇒ HUD phải BỎ phép né Nhà: hình chữ
   * nhật Nhà được suy bằng cách lấy 2 điểm mẫu qua `worldFromScreen`, mà ở khổ dọc hai điểm đó rơi vào
   * HAI CAMERA khác nhau ⇒ phép biến đổi suy ra là rác (xem `coreRects`).
   */
  arenaPortraitField?(): { left: number; right: number; avatarColumns: boolean; splitY: number } | null;

  /**
   * HIỆN BÓNG XEM TRƯỚC (idle, hơi mờ) TẠI TOẠ ĐỘ **THẾ GIỚI** — gọi liên tục trong lúc kéo.
   *
   * ⚠️⚠️ VÌ SAO BÓNG DO SCENE VẼ CHỨ KHÔNG PHẢI HUD TỰ DỰNG DOM: nhân vật trong game KHÔNG phải một
   * ảnh đơn — nó được GHÉP từ các bộ phận (`*-parts.png`) lúc chạy. DOM không có cách nào ghép ra
   * "full hình nhân vật" cho đúng, và nếu HUD tự đoán thì bóng sẽ khác hình tướng thật. Scene đã có
   * sẵn rig + animation IDLE nên chỉ scene mới vẽ được bóng khớp.
   *
   * ⚠️ HUD chỉ được quyết định "gọi hay không gọi" (theo `isValidDeployPos`); HÌNH DẠNG/ độ mờ của
   * bóng là việc của scene. Hợp đồng cố ý KHÔNG có tham số `valid` — HUD không có bóng thì gọi
   * `hideDeployGhost()`, xem `src/style.css .arena-drag-icon.is-blocked` cho tín hiệu phía DOM.
   *
   * ⚠️⚠️ `kind` LÀ `UnitArtKind` (`ClassId | 'farmer'`), KHÔNG phải `ClassId` — NÔNG DÂN CŨNG CÓ BÓNG.
   * Bản đầu chữ ký chỉ nhận `ClassId` nên avatar nông dân không có bóng nào (nó không phải class tướng),
   * trong khi nông dân là một trong những avatar kéo thả chính. Scene đã nới `previewRig(kind)` lên
   * `UnitArtKind`; HUD truyền `'farmer'` cho avatar nông dân. Dùng CHUNG định nghĩa với scene (import
   * type) để hai bên không thể lệch.
   */
  showDeployGhost(kind: UnitArtKind, x: number, y: number): void;

  /**
   * XOÁ BÓNG XEM TRƯỚC — gọi khi nhả tay, khi con trỏ ra ngoài vùng hợp lệ, khi huỷ thao tác và khi
   * rời màn Arena. Thiếu lượt gọi ở đường RỜI MÀN là bóng nằm lại trên sân của trận sau (lỗi im lặng,
   * chỉ thấy được bằng mắt) — vì vậy `hide()`, `cancelDrag()` và `dispose()` đều gọi nó.
   */
  hideDeployGhost(): void;
}

/**
 * ẢNH CHỤP TRẠNG THÁI TRẬN ĐỂ HUD VẼ — do `Game` đọc từ `ArenaWorld` rồi truyền vào.
 *
 * ⚠️ VÌ SAO TRUYỀN ẢNH CHỤP THAY VÌ ĐỂ HUD CẦM `ArenaWorld`: `world` là state SỐNG của mô phỏng
 * 30Hz. HUD cầm nó là HUD có thể vô tình ghi vào sim (đổi tài nguyên, sửa unit) — và tầng UI ghi
 * vào tầng luật là đúng thứ kiến trúc repo đang chặn (`sim/` không được biết `ui/`). Ảnh chụp là
 * DỮ LIỆU CHẾT: HUD chỉ đọc.
 */
export interface ArenaSnapshot {
  /** tài nguyên VÍ ARENA của phe ta (KHÔNG phải `meta.gold`) */
  resource: number;
  /**
   * GIÁ MỘT NÔNG DÂN NGAY LÚC NÀY — để avatar nông dân có huy hiệu giá như avatar tướng.
   *
   * ⚠️⚠️ VÌ SAO PHẢI LÀ TRƯỜNG CỦA ẢNH CHỤP, KHÔNG ĐỂ HUD TỰ TÍNH: giá nông dân TĂNG DẦN theo số nông
   * dân đang sống và công thức nằm ở `ArenaWorld.farmerCost(side)` (tầng sim). HUD chép lại công thức là
   * tạo nguồn sự thật thứ hai, và huy hiệu sẽ nói SAI giá sau lần chỉnh cân bằng đầu tiên — cùng loại
   * lỗi mà `deploy-pos.ts`/ `formatArenaResource()` đã tách khỏi tầng UI để tránh.
   *
   * ⚠️ `Game` dựng ảnh chụp này (`src/game.ts`) và truyền `w.farmerCost('left')`. HUD CHỊU ĐƯỢC giá trị
   * thiếu/ rác (không vẽ huy hiệu) thay vì in `NaN` — xem `iconsHtml`.
   */
  farmerCost: number;
  /**
   * SỐ NÔNG DÂN TA ĐANG SỐNG + TRẦN NÔNG DÂN MỖI PHE — để avatar nông dân tự KHOÁ khi chạm trần.
   *
   * ⚠️ Yêu cầu người dùng 02/10/2026: *"để max mua được 4 farmer"*. Trần thật nằm ở tầng sim
   * (`ARENA_MAX_FARMERS`, `ArenaWorld.buyFarmer` từ chối khi đã đủ) — HUD chỉ ĐỌC LẠI hai con số đó để
   * báo trước cho người chơi (avatar xám + nhãn nói lý do), y như cách `resource`/`farmerCost` đang làm.
   * HUD KHÔNG tự đếm nông dân (nó không sở hữu `world.units`) và KHÔNG tự biết trần.
   */
  farmerCount: number;
  farmerMax: number;
  /** giây đã trôi của trận */
  seconds: number;
  /** độ dài trận (giây) — lấy từ `ARENA_TICK.matchSeconds`, KHÔNG hard-code */
  matchSeconds: number;
  /*
   * ⚠️⚠️ BỐN TRƯỜNG SỐ CỦA NHÀ ĐÃ BỊ XOÁ KHỎI ẢNH CHỤP (02/10/2026) — ĐỌC TRƯỚC KHI THÊM LẠI.
   *
   * Trước đây ở đây có `enemyResource`, `coreHpLeft/ coreMaxLeft/ coreHpRight/ coreMaxRight` và
   * `scoreLeft/ scoreRight`. Người dùng đã chốt: *"số điểm máu và tài nguyên địch để trên đầu nhà chính.
   * Bỏ số máu trên topbar"* + *"bỏ bảng Kinh tế nhịp theo trận"* ⇒ HUD **không còn vẽ** con số nào của
   * Nhà: máu 2 Nhà + tài nguyên địch do tầng VẼ hiện ngay trên đầu Nhà, và tầng vẽ đó đọc THẲNG
   * `world.cores`/ `world.resource` (xem `updateCoreLabels` trong `src/render/arena-battle.ts`).
   *
   * Giữ chúng lại là "dữ liệu chết gợi ý rằng HUD vẫn hiển thị máu/ điểm" — đúng loại chú thích đã bị
   * repo xoá một lần cho `hpPercent()` (xem khối chú thích ngay dưới `ArenaSnapshot`). Muốn hiện lại thì
   * thêm trường + chỗ vẽ CÙNG LÚC, không để trường mồ côi.
   */
  /** trạng thái trận: `prep` · `fighting` · `won` · `lost` · `draw` */
  status: string;
  /** class đã mở khoá để summon (nguồn: `meta.arena.unlockedHeroes`, đã lọc id hợp lệ) */
  heroClasses: ClassId[];
  /*
   * ⚠️ `heroSkills` ĐÃ BỊ XOÁ KHỎI ẢNH CHỤP (03/10/2026) — ĐỌC TRƯỚC KHI THÊM LẠI.
   *
   * Người dùng: *"Bỏ thẻ thông báo skill đang dùng, skill available"* ⇒ dải chiêu (`.arena-skill-strip`)
   * bị xoá hẳn, nên trường này không còn ai đọc. Giữ nó lại là "dữ liệu chết gợi ý rằng HUD vẫn hiển thị
   * chiêu kế tiếp" — đúng loại trường mồ côi mà khối chú thích ngay TRÊN đã xoá một lần cho 4 trường số
   * của Nhà. Muốn hiện lại thì thêm trường + chỗ vẽ CÙNG LÚC (và `Game` phải dựng lại nó từ
   * `arenaNextSkill`/ `u.lastCastId` như bản cũ).
   */
}

/** Kết quả trận vừa kết thúc, `Game` gọi để HUD hiện băng kết quả. `null` = xoá băng. */
export interface ArenaResultBanner {
  status: 'won' | 'lost' | 'draw';
  /** phần thưởng tài nguyên Arena được cộng vào ví riêng */
  reward: number;
}

/**
 * NHÃN TRẠNG THÁI TRẬN — tra qua ID trong `ARENA_TEXT`/ `ARENA_TEXT_EN`.
 *
 * ⚠️ KHÔNG viết `t('Chuẩn bị', 'Prep')` tại chỗ: bản VI đã có nhà ở `ARENA_TEXT.statusPrep` và có
 * test khoá (`tests/arena-data.test.ts` đối chiếu VI/EN từng cặp). Hai bản chữ cho cùng một trạng
 * thái là hai bản để lệch nhau.
 */
function statusLabel(status: string): string {
  switch (status) {
    case 'fighting':
      return t(ARENA_TEXT.statusFighting, ARENA_TEXT_EN.statusFighting);
    case 'won':
      return t(ARENA_TEXT.statusWon, ARENA_TEXT_EN.statusWon);
    case 'lost':
      return t(ARENA_TEXT.statusLost, ARENA_TEXT_EN.statusLost);
    case 'draw':
      return t(ARENA_TEXT.statusDraw, ARENA_TEXT_EN.statusDraw);
    default:
      // `prep` + mọi giá trị lạ (save/ sim đổi tên trạng thái) ⇒ hiện "Chuẩn bị", KHÔNG ném
      return t(ARENA_TEXT.statusPrep, ARENA_TEXT_EN.statusPrep);
  }
}

/** `mm:ss` — đồng hồ trận. Kẹp âm về 0 để giá trị rác không in ra `-1:-5`. */
export function formatMatchClock(seconds: number): string {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/*
 * ⚠️ `hpPercent()` ĐÃ BỊ XOÁ (02/10/2026) cùng `coreBar()` — nó chỉ phục vụ thanh máu Nhà trên thanh HUD,
 * mà người dùng đã yêu cầu *"Thanh máu đổi thành để ở circle nhà để thể hiện máu còn lại"* (pie máu dưới
 * chân Nhà do tầng sân vẽ). Để lại một hàm tính % máu không còn nơi dùng là code chết — và tệ hơn, nó
 * gợi ý rằng thanh máu VẪN tồn tại ở đâu đó.
 *
 * ⚠️ VÀ 4 TRƯỜNG MÁU/ ĐIỂM CỦA ẢNH CHỤP (`coreHp*` · `scoreLeft/Right`) CŨNG ĐÃ BỊ XOÁ CÙNG LÝ DO
 * (02/10/2026) — xem khối chú thích trong `ArenaSnapshot`. "Yêu cầu Lead giữ dữ liệu cho tầng vẽ" nay
 * KHÔNG còn đúng: tầng vẽ đọc thẳng `world.cores`/ `world.resource`, còn HUD thì không vẽ số nào của Nhà.
 */

/**
 * ẢNH CHỤP THAO TÁC KÉO ĐANG DỞ — khai thành interface riêng để `updateDeployGhost()` nhận được tham
 * số có kiểu mà không phải viết `NonNullable<ArenaHud['drag']>` (vừa khó đọc, vừa tự tham chiếu class).
 */
interface IconDrag {
  key: IconKey;
  pointerId: number;
  node: HTMLElement;
  /**
   * HÀNH ĐỘNG của icon đang kéo — chốt MỘT LẦN lúc chạm xuống.
   *
   * ⚠️ Vì sao không tra lại `data-icon` mỗi lần `pointermove`: node có thể bị thay giữa thao tác, và
   * tra lại là thêm một phép phân tích chuỗi cho mỗi sự kiện (hàng trăm lần/giây). Hành động của
   * một avatar không đổi trong lúc kéo.
   */
  action: IconAction | null;
  /**
   * Vị trí con trỏ của sự kiện GẦN NHẤT — CHỈ dùng để đo TỔNG đường đi (phân biệt tap/ drag).
   *
   * ⚠️ KHÔNG dùng để dời icon: icon là nút cố định (xem chú thích ở `startIconDrag`). `grabX`/`grabY`/
   * `half`/`startX`/`startY` của bản cũ đã bị xoá cùng `moveDragNode()`.
   */
  lastX: number;
  lastY: number;
  /** TỔNG đường đi của con trỏ (px) — cơ sở phân biệt tap/ drag */
  total: number;
  /** đã vượt ngưỡng kéo lần nào chưa (ký ức dính — xem `gestureKind`) */
  moved: boolean;
}

/*
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * FUSE SPARK — HỆ HẠT CỦA "NGÒI" Ở ĐẦU MÚT VẠCH THỜI GIAN (khổ DỌC).
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Người dùng (nguyên văn): *"thanh ngang đếm lùi thời gian ở màn hình dọc phần Fuse Spark đang rất
 * giả tạo. Không loé tia tự nhiên như ngòi quả bom đang cháy chậm thật"* → rồi gửi **ẢNH THAM CHIẾU**
 * (`art/ai/fuse-spark-reference.jpg`) và nói *"Fuse Spark đang to quá và chưa được giống tia như ảnh
 * đính kèm"*.
 *
 * NGUYÊN NHÂN ĐÃ ĐO ĐƯỢC: bản trước chỉ có MỘT phần tử `<u class="fuse-spark">` với lõi
 * `radial-gradient` nhấp nhô `scale(0.9 → 1.1)` theo `@keyframes arenaFuseCore` (nhịp sin đều 0,55s).
 * Nó KHÔNG có hạt nào: không tia bay ra, không quỹ đạo riêng, không hạt già đi rồi tắt. Vì mọi thứ
 * cùng phóng to theo MỘT nhịp nên mắt đọc ra ngay là hiệu ứng CSS giả, không phải ngòi đang cháy.
 *
 * ⚠️⚠️ **ĐỌC ẢNH THAM CHIẾU TRƯỚC KHI SỬA SỐ Ở DƯỚI** (`art/ai/fuse-spark-reference.jpg`): nền đen,
 * một LÕI TRẮNG-NÓNG **NHỎ** ở giữa, và hàng chục **TIA DÀI MẢNH NHƯ SỢI CHỈ** toả RA MỌI HƯỚNG; mỗi
 * tia sáng trắng-vàng ở gần lõi rồi ngả vàng-cam và mờ dần ở đầu xa; độ dài các tia RẤT KHÁC NHAU;
 * đầu tia có đốm sáng nhỏ. **KHÔNG có hạt tròn to.**
 *
 * CÁCH SỬA: phát xạ TỪNG HẠT trên một canvas 132×132 neo đúng tâm ngòi (`attachFuseFx`). Mỗi hạt có
 * `x/y/vx/vy/age/life/size/trail` RIÊNG, chịu trọng lực + lực cản rồi nguội dần theo tuổi. **MỌI hạt
 * được vẽ bằng ĐOẠN THẲNG MẢNH** dài theo `vận tốc × trail` (không còn chấm tròn), và hướng phát xạ là
 * **TOẢ 360°** — đúng ảnh. Trọng lực chỉ còn nhẹ (tia hơi rủ xuống) chứ không kéo hạt rơi thành mưa.
 *
 * ⚠️⚠️ HAI BẪY ĐÃ SẬP Ở ĐÚNG CHỖ NÀY (đừng lặp lại):
 *   1. dùng `repeating-conic-gradient` + `mask` cho tia và `box-shadow` + `scale` cho hạt ⇒ tia là MỘT
 *      HOA VĂN TĨNH và mọi hạt sinh/ chết CÙNG LÚC. Muốn tự nhiên thì bắt buộc phải có VÒNG LẶP TỪNG
 *      HẠT — đó là lý do có canvas này.
 *   2. bản canvas ĐẦU TIÊN phát xạ **BÁN CẦU HƯỚNG LÊN** + trọng lực 160 px/s²: đúng "ngòi thuốc nổ
 *      cháy chậm" theo nghĩa vật lý, nhưng NHÌN RA "vài hạt tròn rơi" — người dùng bác thẳng. Ảnh tham
 *      chiếu mới là chuẩn: tia toả tròn, mảnh, dài.
 *
 * ⚠️ MỘT canvas DUY NHẤT, TẠO LƯỜI (`this.fuseCanvas`): `render()` ghi lại `innerHTML` ~10 lần/giây
 * nên mọi node con bị huỷ liên tục; tạo canvas mỗi nhịp là rò rỉ + hàng chục vòng `rAF` chồng nhau.
 * Canvas được GẮN LẠI vào `.fuse-spark` mới sau mỗi `render()`, còn hạt thì sống xuyên qua các nhịp DOM.
 *
 * ⚠️ KHUNG VẼ 132 CSS px nhưng BACKING STORE = 132 × DPR (kẹp ≤ 3): vẽ bitmap ở toạ độ CSS rồi để
 * trình duyệt phóng lên màn retina là tia bị nhoè/ răng cưa — đo trên máy DPR 2 thấy rõ.
 */
const FUSE_FX_SIZE = 132;
/** Trần CỨNG số hạt sống — chặn một cú tóe lớn (hoặc tab bị treo rồi `dt` dồn) làm phình vòng vẽ. */
const FUSE_MAX_SPARKS = 180;
/** Dòng nền (hạt/giây) giữ ngòi "cháy liên tục" giữa các đợt tóe. Ảnh tham chiếu có RẤT nhiều tia. */
const FUSE_BASE_RATE = 42;
/**
 * Trọng lực (px/s²) — CHỈNH NHỎ CÓ CHỦ Ý so với bản đầu (160).
 *
 * Ảnh tham chiếu là pháo hoa cầm tay: tia bắn gần như THẲNG ra mọi hướng, chỉ hơi rủ xuống khi đã yếu.
 * Trọng lực lớn biến chùm tia thành "mưa hạt rơi" — đúng cái người dùng bác.
 */
const FUSE_GRAVITY = 40;
/** Lực cản (1/s) — hạt chậm dần rồi mới rủ xuống, đúng cảm giác tàn lửa. */
const FUSE_DRAG = 1.1;
/** Độ dài ĐUÔI TIA (giây) — nhân với tốc độ ra độ dài vệt. Hạt thường: ngắn; tia "lách tách": dài. */
const FUSE_TRAIL_MIN = 0.05;
const FUSE_TRAIL_MAX = 0.26;
/** TRẦN độ dài tia (px CSS) — tia dài quá sẽ phủ lên chip đồng hồ ngay cạnh đầu ngòi. */
const FUSE_TRAIL_CAP = 46;

/** MỘT HẠT LỬA. Mọi trường đều CỦA RIÊNG hạt — đó chính là thứ bản CSS không có. */
interface FuseSpark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** tuổi (giây) — tăng mỗi khung, chết khi `age >= life` */
  age: number;
  /** tuổi thọ RIÊNG từng hạt (giây) — không hạt nào chết cùng lúc với hạt nào */
  life: number;
  /** bề dày tia (px CSS) — mọi hạt đều vẽ bằng ĐOẠN THẲNG, không có chấm tròn to */
  size: number;
  /** ĐỘ DÀI ĐUÔI (giây): độ dài vệt vẽ = `tốc độ × trail`, kẹp `FUSE_TRAIL_CAP` */
  trail: number;
  /** `true` = TIA "nổ lách tách": sáng hơn, bay xa hơn, đuôi dài hơn (xem `spawnFuseSpark`) */
  crackle: boolean;
}

/** Bảng màu nguội dần: trắng-nóng → vàng → cam → đỏ thẫm (nội suy tuyến tính theo `heat`). */
const FUSE_HEAT_STOPS: ReadonlyArray<readonly [number, number, number, number]> = [
  [0, 120, 22, 10],
  [0.4, 255, 140, 40],
  [0.72, 255, 214, 110],
  [1, 255, 255, 240],
];

/** Màu của hạt theo độ nóng `heat` (0..1) và alpha. Tách hàm để chỗ vẽ không phải rẽ nhánh màu. */
function fuseSparkColor(heat: number, alpha: number): string {
  const h = heat <= 0 ? 0 : heat >= 1 ? 1 : heat;
  let i = 1;
  while (i < FUSE_HEAT_STOPS.length - 1 && h > FUSE_HEAT_STOPS[i][0]) i++;
  const a = FUSE_HEAT_STOPS[i - 1];
  const b = FUSE_HEAT_STOPS[i];
  const span = b[0] - a[0];
  const k = span > 0 ? (h - a[0]) / span : 0;
  return `rgba(${Math.round(a[1] + (b[1] - a[1]) * k)},${Math.round(a[2] + (b[2] - a[2]) * k)},${Math.round(
    a[3] + (b[3] - a[3]) * k,
  )},${alpha.toFixed(3)})`;
}

/**
 * LỚP THÔNG BÁO GIỮA SÂN CỦA ARENA — chữ lớn ở giữa màn hình + nháy sáng toàn màn + nút `Bắt đầu`.
 *
 * Ba yêu cầu người dùng 02/10/2026 (nguyên văn):
 *   1. *"khi tôi muốn khi mà X2 speed, màn hình nháy lên và có chữ X2 hiện ra thông báo rất nổi bậy, gây
 *      chú ý giữa sông để người chơi nắm được. X3 cũng thế nhé"*;
 *   2. *"khi còn 10s, màn hình nháy nhẹ alert và giữa màn hình chỗ sông sẽ nhảy lên số đếm lùi 10, 9, 8,
 *      … đến end"*;
 *   3. *"khi tôi click vào Arena, màn hình hiển thị nút `Bắt đầu`, tôi phải click Bắt đầu, sẽ đếm lùi 3s
 *      (hiển thị số xuất hiện rồi biến mất 3,2,1,Bắt đầu, thì lúc đó game mới bắt đầu)"*.
 *
 * ⚠️⚠️ VÌ SAO LÀ MỘT NODE **NGOÀI `#arenaHud`** — ĐÂY LÀ QUYẾT ĐỊNH QUAN TRỌNG NHẤT CỦA FILE NÀY.
 * `ArenaHud.render()` ghi lại `this.root.innerHTML` ~10 lần/giây (xem chú thích dài ở `flash()`), nên
 * mọi node con bị HUỶ theo mỗi nhịp. Với toast thì cách xử là "giữ trạng thái ở JS rồi vẽ lại sau mỗi
 * `render()`" — nhưng với MỘT HOẠT ẢNH (chữ nảy lên, màn hình nháy) thì vẽ lại 10 lần/giây sẽ RESTART
 * animation liên tục: chữ đứng im ở khung đầu và nháy giật. Cách đúng là để lớp này SỐNG NGOÀI `root`
 * (`document.body`), cập nhật bằng JS thuần, và KHÔNG bị `innerHTML` đụng tới.
 *
 * ⚠️ `pointer-events: none` trên vỏ (CSS) + `auto` trên MỖI nút: lớp này phủ toàn màn hình nên nếu nó
 * nuốt cú chạm thì người chơi không kéo/ bấm được avatar nào (lỗi "bấm không ăn" đã sập một lần ở repo).
 */
class ArenaStageFx {
  private readonly root: HTMLElement;
  private readonly flashEl: HTMLElement;
  private readonly textEl: HTMLElement;
  private readonly startEl: HTMLElement;
  private readonly startBtn: HTMLButtonElement;
  /** handle ẩn chữ lớn — `null` = không có hẹn nào */
  private hideTimer: number | null = null;
  /** handle tắt nháy — `null` = không có hẹn nào */
  private flashTimer: number | null = null;
  /**
   * KHOÁ CỦA NỘI DUNG ĐANG HIỆN (`kind:text`). `render()` chạy 10 lần/giây nên nếu mỗi lần đều gọi
   * `announce()` thì cùng một con số bị "nảy" 10 lần và animation không bao giờ xong. Khoá này chặn
   * đúng trường hợp đó mà vẫn cho phép đổi nội dung (10 → 9 → 8…).
   */
  private shownKey = '';

  constructor(onStart: () => void, startLabel: string) {
    const root = document.createElement('div');
    root.id = 'arenaStageFx';
    root.className = 'arena-stage-fx hidden';
    /*
     * `aria-live="polite"`: các thông báo này là thông tin người chơi CẦN biết (tốc độ tăng, sắp hết
     * giờ) nên trình đọc màn hình phải đọc được — không chỉ là hiệu ứng hình ảnh.
     */
    root.setAttribute('aria-live', 'polite');
    root.innerHTML = `
      <div class="arena-stage-flash" aria-hidden="true"></div>
      <div class="arena-stage-text"></div>
      <div class="arena-stage-start hidden">
        <button type="button" class="arena-stage-start-btn" data-arena-start="1"></button>
      </div>`;
    document.body.appendChild(root);
    this.root = root;
    this.flashEl = root.querySelector<HTMLElement>('.arena-stage-flash')!;
    this.textEl = root.querySelector<HTMLElement>('.arena-stage-text')!;
    this.startEl = root.querySelector<HTMLElement>('.arena-stage-start')!;
    this.startBtn = root.querySelector<HTMLButtonElement>('.arena-stage-start-btn')!;
    this.setStartLabel(startLabel);
    /*
     * `pointerdown` (KHÔNG `click`) — cùng lý do như nút trong HUD: `render()`/ nhịp DOM không được
     * phép làm mất cú bấm. Ở đây node không bị dựng lại, nhưng giữ MỘT quy ước cho cả file.
     */
    this.startBtn.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onStart();
    });
  }

  /** Đổi nhãn nút theo ngôn ngữ hiện tại (gọi khi render, rẻ — chỉ ghi khi chuỗi đổi). */
  setStartLabel(label: string): void {
    if (this.startBtn.textContent !== label) this.startBtn.textContent = label;
  }

  /** Hiện/ ẩn toàn bộ lớp (rời màn Arena là ẩn). */
  show(on: boolean): void {
    this.root.classList.toggle('hidden', !on);
    if (!on) this.clear();
  }

  /** Hiện/ ẩn RIÊNG màn `Bắt đầu`. */
  showStart(on: boolean): void {
    this.startEl.classList.toggle('hidden', !on);
  }

  /**
   * NEO DỌC: `#app[data-screen='arena']` ở khổ dọc chia 2 tầng, tâm sân ("giữa sông") là đường VẠCH
   * CHIA — không phải `50%` của khung nhìn. HUD đọc toạ độ đó từ `.arena-band-seam` và truyền xuống
   * đây; khổ ngang truyền `null` ⇒ dùng `50%`.
   */
  setAnchorY(y: number | null): void {
    this.root.style.setProperty('--fx-y', y === null ? '50%' : `${y.toFixed(1)}px`);
  }

  /**
   * HIỆN CHỮ LỚN Ở GIỮA SÂN + nháy màn hình.
   *
   * `kind`: `'alert'` = nháy NHẸ (đếm lùi cuối trận, màu vàng), `'speed'` = nháy MẠNH (×2/×3, màu sáng
   * hơn) — hai mức này là chủ ý: mốc tốc độ là tin "đáng chú ý", còn nhịp từng giây mà nháy mạnh liên
   * tục 10 lần sẽ gây mỏi mắt.
   */
  announce(text: string, kind: 'alert' | 'speed', holdMs: number): void {
    const key = `${kind}:${text}`;
    if (this.shownKey === key) return;
    this.shownKey = key;
    this.textEl.textContent = text;
    this.textEl.classList.toggle('is-speed', kind === 'speed');
    this.textEl.classList.remove('is-pop', 'is-count');
    /*
     * `void offsetWidth` = ÉP TRÌNH DUYỆT TÍNH LẠI LAYOUT để animation chạy lại từ đầu. Bỏ dòng này thì
     * lớp `is-pop` được thêm nhưng animation không restart (cùng một lớp, cùng một node).
     */
    void this.textEl.offsetWidth;
    this.textEl.classList.add(kind === 'speed' ? 'is-pop' : 'is-count');
    this.pulse(kind);
    if (this.hideTimer !== null) window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      this.hideTimer = null;
      this.textEl.textContent = '';
      this.shownKey = '';
    }, holdMs);
  }

  /** NHÁY MÀN HÌNH — thêm lớp rồi tự gỡ sau ~320ms để lần sau còn chạy lại được. */
  private pulse(kind: 'alert' | 'speed'): void {
    this.flashEl.classList.remove('is-on', 'is-soft');
    void this.flashEl.offsetWidth;
    this.flashEl.classList.add(kind === 'speed' ? 'is-on' : 'is-soft');
    if (this.flashTimer !== null) window.clearTimeout(this.flashTimer);
    this.flashTimer = window.setTimeout(() => {
      this.flashTimer = null;
      this.flashEl.classList.remove('is-on', 'is-soft');
    }, 340);
  }

  /** Xoá chữ + mọi hẹn giờ (rời màn/ kết thúc trận) — KHÔNG xoá nút `Bắt đầu`. */
  clear(): void {
    if (this.hideTimer !== null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.flashTimer !== null) {
      window.clearTimeout(this.flashTimer);
      this.flashTimer = null;
    }
    this.shownKey = '';
    this.textEl.textContent = '';
    this.flashEl.classList.remove('is-on', 'is-soft');
  }

  /** Gỡ hẳn khỏi DOM (HUD `dispose`). */
  dispose(): void {
    this.clear();
    this.root.remove();
  }
}

/**
 * HUD ARENA.
 *
 * Vòng đời: `new ArenaHud(root)` → `bind(host)` → `show()`/`hide()` theo màn hình →
 * `render(snapshot)` mỗi nhịp HUD → `dispose()` khi rời hẳn.
 */
export class ArenaHud {
  /** phần tử gốc `#arenaHud` — HUD chỉ vẽ BÊN TRONG nó, không đụng phần còn lại của DOM */
  private readonly root: HTMLElement;
  private host: ArenaHudHost | null = null;
  /** đã gắn sự kiện chưa — chặn gắn 2 lần (mỗi lần `show()` lại gắn là 1 cú bấm chạy 2 lần) */
  private bound = false;
  /** ảnh chụp gần nhất, để vẽ lại khi đổi NGÔN NGỮ mà không cần chờ nhịp sim kế tiếp */
  private last: ArenaSnapshot | null = null;
  private banner: ArenaResultBanner | null = null;
  private toastTimer: number | null = null;
  /**
   * TOAST ĐANG HIỆN (`null` = đang ẩn) — GIỮ NGOÀI DOM vì `render()` dựng lại `innerHTML` ~10 lần/giây
   * và sẽ xoá phần tử toast. Xem chú thích dài ở `flash()`.
   */
  private toast: { text: string; bad: boolean } | null = null;

  /**
   * LỚP THÔNG BÁO GIỮA SÂN (`ArenaStageFx`) — tạo LƯỜI trong `bind()`, SỐNG NGOÀI `root` nên không bị
   * `render()` xoá. Xem chú thích dài ở class đó.
   */
  private stageFx: ArenaStageFx | null = null;

  /**
   * TRẠNG THÁI MÀN `Bắt đầu`: `idle` = đang chờ bấm · `counting` = đang đếm 3-2-1 · `running` = trận đã
   * chạy (không hiện nút nữa, bật thông báo ×2/×3 + đếm lùi 10s).
   */
  private startPhase: 'idle' | 'counting' | 'running' = 'idle';
  /** handle của nhịp đếm lùi 3-2-1 (`null` = không đếm) */
  private startTimer: number | null = null;
  /** hệ số tốc độ ĐÃ thông báo — so với `arenaTimeSpeedMul(snapshot.seconds, snapshot.matchSeconds)` */
  private lastSpeedMul = 1;
  /**
   * MỐC THỜI GIAN THỰC (ms, `performance.now`) ĐẾN KHI NHƯỜNG CHỖ CHO THÔNG BÁO TỐC ĐỘ — 0 = không giữ.
   *
   * ⚠️⚠️ VÌ SAO CẦN (ĐO ĐƯỢC 03/10/2026, khi thêm mốc **×5 ở 10 giây cuối**): `paintStageFx` chạy hai
   * thông báo trên CÙNG một phần tử chữ: (1) mốc tốc độ ×N, (2) số đếm lùi 10 giây cuối. Ở ×5, mỗi giây
   * trận chỉ còn ~0,2 giây thực ⇒ đếm lùi ghi đè chữ "×5" NGAY TRONG cùng nhịp vẽ, và thông báo ×5 (vốn
   * giữ 1,6s) trở thành VÔ HÌNH. Số đo trên Chrome: `#arenaStageFx .arena-stage-text` = "10"/"1", KHÔNG
   * bao giờ là "×5". Nay trong lúc thông báo tốc độ còn hiệu lực thì NHỊN nhịp đếm lùi — nó chỉ lùi một
   * nhịp, và số đếm lùi vẫn hiện đủ (10 → 1) sau đó.
   */
  private speedFxHoldUntil = 0;
  /** số giây ĐANG hiện ở đếm lùi cuối trận (`null` = chưa vào 10s cuối) */
  private lastCountdownSec: number | null = null;

  /** Thao tác kéo đang dở (`null` = không kéo). Xem `startIconDrag`/`onPointerUp`. */
  private drag: IconDrag | null = null;

  /**
   * BÓNG XEM TRƯỚC CỦA SCENE ĐANG HIỆN (`true` = đã gọi `showDeployGhost`, chưa gọi hide).
   *
   * ⚠️⚠️ VÌ SAO CẦN CỜ Ở TẦNG HUD: `pointermove` bắn hàng trăm lần/giây. Nếu nhánh "không hợp lệ" cứ
   * gọi `hideDeployGhost()` mỗi sự kiện thì scene bị yêu cầu dựng/ xoá bóng liên tục — mà mỗi lượt gọi
   * có thể là một phép dựng rig. Cờ này cũng là thứ giữ cho đường NHẢ TAY gọi `hideDeployGhost()`
   * đúng MỘT lần (hợp đồng: gọi LUÔN khi nhả tay, xem `ArenaHudHost.hideDeployGhost`).
   */
  private ghostOn = false;

  /** Handle của lượt đặt lại toạ độ icon ở khung hình kế tiếp (xem `scheduleIconRelayout`). */
  private relayoutHandle: number | null = null;

  /** Nút THƯỜNG đang chờ nhả tay (`pointerup` mới chạy lệnh — xem `onPointerDown`). */
  private pendingBtn: HTMLElement | null = null;

  /**
   * CANVAS CỦA NGÒI — tạo LƯỜI, sống suốt vòng đời HUD (xem khối `FUSE SPARK` ở đầu file).
   *
   * ⚠️⚠️ KHÔNG tạo trong `render()`: `render()` chạy ~10 lần/giây, mỗi lần một canvas mới là 10 canvas/
   * giây bị bỏ rơi (GC không kịp) và 10 vòng `rAF` cùng vẽ ⇒ tụt khung hình. Node canvas bị `innerHTML`
   * xoá khỏi DOM mỗi nhịp, nhưng BẢN THÂN đối tượng canvas vẫn sống ở đây và được gắn lại.
   */
  private fuseCanvas: HTMLCanvasElement | null = null;
  private fuseCtx: CanvasRenderingContext2D | null = null;
  /** Handle vòng `rAF` — `null` = vòng đang DỪNG (rời màn/ dispose/ reduced-motion). */
  private fuseRaf: number | null = null;
  /** Mốc thời gian khung trước (ms) — `0` = khung đầu, lấy `dt` mặc định 1/60. */
  private fuseLast = 0;
  /** Kho hạt sống. Duyệt NGƯỢC + `splice` tại chỗ để không cấp phát mảng mới mỗi khung. */
  private fuseSparks: FuseSpark[] = [];
  /** Bộ đếm dòng nền (hạt lẻ) — cộng dồn `rate*dt` rồi nhả từng hạt. */
  private fuseSpawnAcc = 0;
  /** Đếm ngược tới đợt TÓE kế tiếp (giây) — phát xạ KHÔNG đều, đây là "nhịp" ngẫu nhiên. */
  private fuseBurstIn = 0;
  /** Hệ số sáng của lõi, đi theo bước NGẪU NHIÊN (không phải sin đều — xem `fuseFlicker`). */
  private fuseFlicker = 1;
  /** Gradient lõi tạo một lần cho mỗi context (tạo lại mỗi khung là cấp phát vô ích). */
  private fuseCoreGrad: CanvasGradient | null = null;
  /** `matchMedia` cho `prefers-reduced-motion` — tạo lười, MỘT lần (xem `fusePrefersReduced`). */
  private fuseMotionQuery: MediaQueryList | null = null;

  constructor(root?: HTMLElement | null) {
    const el = root ?? document.getElementById('arenaHud');
    if (!el) throw new Error('Thiếu #arenaHud trong index.html — HUD Arena không có chỗ để vẽ');
    this.root = el;
  }

  /** GẮN HOST (scene). Gọi lại nhiều lần được — scene thay khi vào trận mới. */
  bind(host: ArenaHudHost): void {
    this.host = host;
    if (this.bound) return;
    this.bound = true;
    /*
     * ⚠️⚠️ UỶ QUYỀN SỰ KIỆN Ở ĐÚNG MỘT CHỖ (`root`), KHÔNG gắn cho từng nút.
     *
     * Vì sao: `render()` ghi lại `innerHTML` mỗi nhịp ⇒ mọi listener gắn cho nút con bị HUỶ theo
     * node cũ. Gắn lại trong `render()` thì mỗi nhịp lại thêm một listener mới (rò rỉ), và một cú
     * bấm có thể chạy N lần — repo đã dính đúng lỗi này ("bấm 1 nút ⇒ `openHomeFeature` chạy 2 lần",
     * xem chú thích trong `src/ui/hud.ts`). Uỷ quyền trên `root` là listener DUY NHẤT, sống qua mọi
     * lần vẽ lại.
     *
     * ⚠️⚠️ VÀ DÙNG `pointerdown` CHỨ KHÔNG `click` — BUG ĐÃ ĐO ĐƯỢC, VÁ 01/10/2026.
     *
     * Chính việc `render()` ghi lại `innerHTML` MỖI NHỊP (~6–10 lần/giây, xem `Game.onArenaFrame`)
     * làm sự kiện `click` BỊ NUỐT. `click` chỉ bắn khi `mousedown` VÀ `mouseup` cùng xảy ra trên
     * CÙNG MỘT phần tử; HUD dựng lại DOM giữa hai thời điểm đó ⇒ phần tử bị huỷ ⇒ KHÔNG có `click`.
     *
     * SỐ ĐO THẬT (Chrome headless, bấm đúng toạ độ nút summon, `MutationObserver` xác nhận HUD dựng
     * lại ~6 lần/giây; chọn 12 lần mỗi mức):
     *     giữ 0ms   (bấm tức thì)   ⇒  0/12 bị nuốt
     *     giữ 60ms  (bấm nhanh)     ⇒  4/12 bị nuốt  (33%)
     *     giữ 120ms (bấm bình thường) ⇒ 7/12 bị nuốt  (58%)
     * ⇒ NGƯỜI CHƠI THẬT bấm "Triệu hồi" và phần lớn lần KHÔNG có gì xảy ra. Đây đúng loại lỗi mà
     * "test xanh + tsc 0 lỗi" không bắt được, và nó sẽ làm hỏng buổi chơi thử (phản hồi sẽ là
     * "bấm không ăn" chứ không phải về cân bằng).
     *
     * BẰNG CHỨNG NHÂN QUẢ (không phải suy đoán): chặn `render()` bằng cách gán
     * `window.__TDK__.renderArenaHud = () => {}` rồi đo lại ⇒ **0/12 bị nuốt ở CẢ 60ms và 120ms**.
     * Tức nguyên nhân là DOM bị thay giữa lúc bấm, không phải nút hỏng hay sim từ chối.
     *
     * Vì sao `pointerdown` sửa dứt điểm: nó bắn NGAY khi ngón tay/ chuột chạm xuống, chỉ cần MỘT
     * thời điểm thay vì một CẶP — nên việc DOM dựng lại sau đó không thể làm mất sự kiện. Cũng đúng
     * cảm giác game (bấm là ăn ngay, không phải chờ nhả tay).
     *
     * ⚠️⚠️ KÉO THẢ ĐI CÙNG ĐƯỜNG NÀY — VÀ PHẢI NGẮT ĐƯỢC LỆNH MUA. Xem `onPointerDown`/`onPointerUp`.
     */
    this.root.addEventListener('pointerdown', this.onPointerDown);
    /*
     * `move`/`up` gắn trên `window` (KHÔNG phải `root`) — chú thích dài ở `onPointerMove`.
     */
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    this.root.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('orientationchange', this.onViewportChange);
    this.layoutIcons();
    /*
     * LỚP THÔNG BÁO GIỮA SÂN (`Bắt đầu` · ×2/×3 · đếm lùi 10s) — tạo ở ĐÂY vì `bind()` chạy trong
     * trình duyệt thật (HUD này còn được import trong node để test cấu trúc, khi đó `document` có thể
     * không tồn tại — `ensureStageFx` tự trả `null`).
     */
    this.ensureStageFx();
  }

  /**
   * BẤM/ CHẠM XUỐNG — mở thao tác và quyết định sau (`pointerup`) là CÚ BẤM hay KÉO.
   *
   * ⚠️⚠️ VÌ SAO KHÔNG CHẠY LỆNH NGAY Ở ĐÂY NHƯ BẢN CŨ: yêu cầu #3 của task — kéo icon không được
   * kích hoạt lệnh mua/chọn. Nếu bắn lệnh ngay lúc chạm xuống thì MỌI lần kéo icon đều mua tướng
   * (hoặc đổi land) trước khi người chơi kịp kéo ⇒ vừa mất tiền vừa mất vị trí.
   *
   * ⚠️ Ngưỡng `DRAG_TAP_SLOP_PX` là 8px TỔNG dịch chuyển ⇒ bấm bình thường (run tay vài px) vẫn ăn.
   * Cú bấm cũ ở nhịp người thật (`mouse.down()` + 120ms + `mouse.up()`) đi qua đúng đường này và
   * vẫn phải ăn — nên KHÔNG được thêm điều kiện nào khác (thời gian giữ, `click`, …).
   *
   * ⚠️ Bản cũ chạy lệnh ở `pointerdown` (không có ngưỡng) nên nó MIỄN NHIỄM với việc DOM bị dựng
   * lại giữa lúc bấm. Bản này vẫn giữ tính chất đó: `pointerup` bắn trên `window` (xem `onPointerMove`),
   * nên node bị thay giữa chừng KHÔNG làm mất sự kiện — thứ duy nhất bị mất là tham chiếu tới node,
   * và ta giữ node bằng `setPointerCapture` + tra lại theo `data-icon`.
   */
  private onPointerDown = (ev: PointerEvent): void => {
    // ⚠️ CHỈ CHUỘT TRÁI/ CHẠM — xem chú thích `onContextMenu` cho nút phải.
    if (ev.button !== 0) return;

    const target = ev.target as HTMLElement | null;
    const icon = target?.closest<HTMLElement>('[data-icon]') ?? null;
    if (icon) {
      /*
       * ⚠️ AVATAR BỊ KHOÁ (không đủ tài nguyên) ⇒ KHÔNG mở thao tác nào: không kéo được, không bấm mua
       * được. Người dùng: *"nếu không đủ tiền mua nhân vật thì avatar nhân vật xám đi không cho kéo hay
       * click vào để mua"*.
       *
       * ⚠️ CHẶN Ở ĐÂY (`pointerdown`), KHÔNG chỉ ở `dropCommand`/`tapIconCommand`: chặn sớm thì KHÔNG
       * có bóng xem trước và KHÔNG có trạng thái `dragging` — người chơi không phải thấy tín hiệu "sắp
       * mua được" rồi mới bị từ chối. Vẫn có chốt thứ hai trong `startIconDrag`/`tapIconCommand` để
       * một đường gọi khác (test/ công cụ) không lách qua được.
       */
      if (icon.dataset.locked) return;
      const key = icon.dataset.icon ?? '';
      if (key) this.startIconDrag(ev, icon, key);
      return;
    }

    /*
     * KHÔNG phải icon ⇒ nút THƯỜNG (thoát · cài đặt · THOÁT TRÊN BĂNG KẾT QUẢ). Vẫn phải đi qua
     * `pointerup` để giữ đúng ngữ nghĩa "bấm = nhấc tay", nhưng ở đây KHÔNG có ngưỡng kéo: nút thường
     * không kéo được nên mọi cú chạm đều là bấm.
     *
     * ⚠️ `#btnArenaResultExit` là nút MỚI (yêu cầu *"khi chiến thắng hiển thị nút thoát trên thông báo
     * đó"*): nó nằm trong băng kết quả, mà băng đó là CON của `root` và bị `innerHTML` dựng lại mỗi nhịp
     * vẽ — nên nó BẮT BUỘC phải đi qua uỷ quyền ở đây, gắn listener trực tiếp cho nút là listener chết
     * theo node đầu tiên.
     *
     * ⚠️ MỌI lệnh MUA nay đi qua avatar (`[data-icon]`), KHÔNG còn nút mua nào trong dock — xem
     * `iconsHtml`/`render`.
     */
    const btn = target?.closest<HTMLElement>('#btnArenaExit,#btnArenaSettings,#btnArenaResultExit');
    if (!btn) return;
    this.pendingBtn = btn;
  };

  /**
   * DI CHUYỂN — chỉ đổi toạ độ icon đang kéo.
   *
   * ⚠️⚠️ GẮN TRÊN `window` CHỨ KHÔNG TRÊN `root`: `setPointerCapture` giữ cho sự kiện tới được node
   * đã capture, nhưng node đó CÓ THỂ BỊ HUỶ giữa thao tác (mọi lần `render()` ghi lại `innerHTML`
   * đều thay node — trừ khi đang kéo, xem `render()`). Listener trên `window` không phụ thuộc node
   * nào, và `ev.clientX/Y` lấy từ `window` không cần node đích ⇒ kéo vẫn mượt kể cả khi DOM đổi.
   */
  private onPointerMove = (ev: PointerEvent): void => {
    const drag = this.drag;
    if (!drag) return;
    if (Number.isFinite(ev.pointerId) && drag.pointerId !== ev.pointerId) return;
    drag.total += Math.hypot(ev.clientX - drag.lastX, ev.clientY - drag.lastY);
    drag.lastX = ev.clientX;
    drag.lastY = ev.clientY;
    if (!drag.moved && crossedDragSlop(drag.total)) {
      drag.moved = true;
      this.root.classList.add('is-dragging');
      drag.node.classList.add('dragging');
    }
    if (!drag.moved) return; // chưa vượt ngưỡng ⇒ CHƯA làm gì (bấm vẫn phải ăn đúng chỗ)
    /*
     * ⚠️⚠️ KHÔNG DỜI ICON — chỉ BÓNG đi theo tay.
     * Người dùng: *"Ý tôi là avatar di chuyển theo tay là sai"*. Vì vậy ở đây KHÔNG có `style.left/top`
     * nào được ghi: icon đứng nguyên tại vị trí bố cục, còn `updateDeployGhost()` đặt bóng của scene
     * tại điểm con trỏ và bật tín hiệu "thả được"/ "cấm" trên chính icon đó.
     *
     * ⚠️ Vẫn chỉ chạy từ ngưỡng KÉO trở đi: cú TAP (không nhích tay) không được hiện bóng — yêu cầu
     * người dùng *"khi tôi thả tay nhân vật mới được triệu hồi"*; bóng là để ƯỚM vị trí, không phải
     * để báo đã mua.
     */
    this.updateDeployGhost(drag, ev.clientX, ev.clientY);
  };

  /**
   * QUYẾT ĐỊNH CÓ BÓNG XEM TRƯỚC HAY KHÔNG — và báo "không thả được" bằng HAI tín hiệu.
   *
   * ⚠️⚠️ LUẬT VỊ TRÍ LẤY TỪ `isValidDeployPos` CỦA SIM, KHÔNG TỰ VIẾT LẠI: HUD chỉ HỎI luật rồi hiện
   * bóng; lệnh mua vẫn do `summonHero`/ `buyFarmer` phán quyết cuối (xem `dropCommand`). Nhờ vậy bóng
   * và kết quả thả không thể lệch nhau, mà cũng không có "bản luật thứ hai" trong tầng UI.
   *
   * ⚠️ `DEPLOY_MARGIN` là bán kính TƯỚNG (lớn nhất trong các loại người chơi thả được) — dùng cho cả
   * nông dân là cố ý, giống `homeDeployPoint`: nông dân chỉ bị coi là "gần mép" sớm hơn vài px (vô hại).
   *
   * ⚠️⚠️ NÔNG DÂN CŨNG CÓ BÓNG: chữ ký scene nay là `showDeployGhost(kind: UnitArtKind, …)` với
   * `UnitArtKind = ClassId | 'farmer'`, nên avatar nông dân truyền `'farmer'` và có bóng THẬT (art
   * riêng). Trước đây chữ ký chỉ nhận `ClassId` nên nông dân — một trong các avatar kéo thả chính —
   * không có bóng nào; đó là lỗ hổng của hợp đồng, không phải của tính năng.
   *
   * ⚠️ HAI TÍN HIỆU "KHÔNG THẢ ĐƯỢC" (chuẩn của repo, xem `src/style.css .is-blocked`): (1) viền đỏ +
   * quầng đỏ, (2) ảnh đầu giảm độ hiện/ mất màu + GẠCH CHÉO. Cố ý KHÔNG chỉ dùng màu: ~8% nam giới bị
   * mù màu đỏ-lục nên tín hiệu chỉ-màu là mất thông tin với họ.
   *
   * ⚠️ Vị trí hợp lệ thì thêm `drop-target` (viền sáng, CSS đã có) — cũng là tín hiệu nhìn thấy được
   * ngoài bóng của scene, để khi scene chưa kịp vẽ bóng người chơi vẫn biết chỗ đó thả được.
   */
  private updateDeployGhost(drag: IconDrag, clientX: number, clientY: number): void {
    const host = this.host;
    const action = drag.action;
    const node = drag.node;
    if (!host || !action) {
      /* Khoá lạ/ scene chưa gắn: không biết luật ⇒ không hứa gì, nhưng vẫn dọn bóng cũ nếu có. */
      node.classList.remove('drop-target', 'is-blocked');
      if (this.ghostOn) this.hideDeployGhost();
      return;
    }
    const p = host.worldFromScreen(clientX, clientY);
    const valid =
      !!p && Number.isFinite(p.x) && Number.isFinite(p.y) && isValidDeployPos(p.x, p.y, DEPLOY_MARGIN).ok;
    if (valid) {
      if (typeof host.showDeployGhost === 'function') {
        /*
         * `IconAction` → `UnitArtKind`: tướng dùng classId, nông dân dùng `'farmer'`. Cả hai đều có art.
         */
        const kind: UnitArtKind = action.kind === 'hero' ? action.classId : 'farmer';
        host.showDeployGhost(kind, p.x, p.y);
        this.ghostOn = true;
      } else if (this.ghostOn) {
        /* Scene thiếu hàm: không có bóng để hiện, nhưng bóng cũ của avatar trước phải được dọn. */
        this.hideDeployGhost();
      }
      node.classList.add('drop-target');
      node.classList.remove('is-blocked');
      return;
    }
    if (this.ghostOn) this.hideDeployGhost();
    node.classList.remove('drop-target');
    node.classList.add('is-blocked');
  }

  /**
   * XOÁ BÓNG XEM TRƯỚC (gọi scene) + hạ cờ. LUÔN gọi được, kể cả khi chưa từng hiện — scene tự bỏ qua
   * nếu không có gì để xoá, còn HUD thì cần đường này để không sót bóng khi rời màn.
   */
  private hideDeployGhost(): void {
    this.ghostOn = false;
    const host = this.host;
    if (host && typeof host.hideDeployGhost === 'function') host.hideDeployGhost();
  }

  /**
   * NHẢ TAY — chốt vị trí nếu là KÉO, chạy lệnh nếu là CÚ BẤM.
   *
   * ⚠️ PHÂN BIỆT TAP/DRAG BẰNG NGƯỠNG TỔNG ĐƯỜNG ĐI (`gestureKind`), KHÔNG bằng thời gian giữ:
   * người chơi giữ nút 2 giây mà không nhích tay vẫn phải mua được (đó là "giữ", không phải "kéo").
   */
  private onPointerUp = (ev: PointerEvent): void => {
    const drag = this.drag;
    if (drag) {
      if (Number.isFinite(ev.pointerId) && drag.pointerId !== ev.pointerId) return;
      this.drag = null;
      /*
       * ⚠️⚠️ XOÁ BÓNG XEM TRƯỚC **LUÔN**, TRƯỚC MỌI LỆNH KHÁC — đây là hợp đồng của scene (bóng chỉ
       * sống trong lúc kéo) và cũng là thứ giữ cho trạng thái "cấm" không dính sang lần kéo sau.
       * Không phụ thuộc `wasDrag`: cú tap cũng phải đi qua đây để dọn sạch nếu trước đó có bóng sót.
       */
      this.hideDeployGhost();
      drag.node.classList.remove('drop-target', 'is-blocked');
      const wasDrag = gestureKind(drag.total, DRAG_TAP_SLOP_PX, drag.moved) === 'drag';
      try {
        if (drag.node.hasPointerCapture?.(drag.pointerId)) drag.node.releasePointerCapture(drag.pointerId);
      } catch {
        /* node đã bị gỡ khỏi DOM ⇒ capture tự mất, không cần làm gì */
      }
      drag.node.classList.remove('dragging');
      this.root.classList.remove('is-dragging');
      if (wasDrag) {
        /*
         * ⚠️ THỨ TỰ Ở ĐÂY LÀ LUẬT, KHÔNG ĐỔI:
         *   1. `dropCommand()` — MUA tại ĐIỂM THẢ (quy đổi qua scene), sim từ chối thì toast lỗi;
         *   2. `render(this.last)` — vẽ lại HUD (bị chặn suốt lúc kéo).
         *
         * ⚠️⚠️ KHÔNG CÒN `commitIconPos()` — ĐỌC TRƯỚC KHI THÊM LẠI: người dùng chốt *"avatar di chuyển
         * theo tay là sai"*, nên icon KHÔNG đổi vị trí và KHÔNG ghi vị trí khi thả. Thứ tự cũ
         * (`commitIconPos` rồi `dropCommand`) tồn tại chỉ để giữ vị trí nút — việc đó nay không còn.
         */
        this.dropCommand(drag.key, ev.clientX, ev.clientY);
        this.render(this.last);
        return;
      }
      /*
       * KHÔNG phải kéo ⇒ đây là CÚ BẤM trên icon. Chạy đúng lệnh của icon rồi thoát; icon KHÔNG đi
       * qua `pendingBtn` (nhánh đó dành cho các nút thường trong bar/ dock).
       */
      this.tapIconCommand(drag.node);
      this.render(this.last);
      return;
    }

    const btn = this.pendingBtn;
    this.pendingBtn = null;
    if (!btn) return;
    /*
     * ⚠️ NODE CÓ THỂ ĐÃ BỊ THAY giữa `down` và `up` (HUD vẽ lại ~10 lần/giây). Node cũ vẫn giữ
     * `dataset` nên đọc `data-*` trên nó vẫn ĐÚNG — nhưng nếu nó đã rời DOM thì cú bấm này thuộc về
     * một HUD cũ; chạy lệnh vẫn đúng ý người chơi (họ đã bấm), nên không chặn.
     */
    this.runButtonCommand(btn);
  };

  /**
   * CHẶN MENU NGỮ CẢNH CHUỘT PHẢI TRÊN ICON — để nút phải không vừa mở menu vừa mở thao tác kéo.
   *
   * ⚠️ Vì sao KHÔNG dùng `ev.preventDefault()` trong `pointerdown`: làm vậy sẽ chặn luôn việc sinh
   * sự kiện `click`/ `contextmenu` của nút phải ở những chỗ khác, và trên một số trình duyệt còn
   * chặn cả việc focus. Chặn đúng `contextmenu` là đủ và hẹp nhất. (Bản cũ chỉ lọc `ev.button !== 0`
   * trong handler — vẫn đúng, giữ nguyên ở `onPointerDown`.)
   */
  private onContextMenu = (ev: Event): void => {
    const t = ev.target as HTMLElement | null;
    if (t?.closest('[data-icon]')) ev.preventDefault();
  };

  /**
   * ĐỔI KÍCH THƯỚC MÀN HÌNH/ XOAY — TÍNH LẠI VỊ TRÍ MỌI ICON.
   *
   * ⚠️⚠️ VÌ SAO BẮT BUỘC (yêu cầu #5): toạ độ px của màn 844×390 vô nghĩa trên màn 390×844. Không
   * tính lại thì sau khi xoay, icon nằm NGOÀI khung nhìn và người chơi mất nút — mà không có lỗi nào
   * được ném ra, nên rất khó lần.
   *
   * ⚠️ CHỈ CẬP NHẬT TOẠ ĐỘ, KHÔNG `render()`: `render()` ghi lại `innerHTML` và sẽ huỷ node icon
   * đang được thao tác (nếu người chơi đang kéo mà màn hình xoay). `layoutIcons()` là thao tác
   * idempotent trên DOM hiện có.
   */
  private onViewportChange = (): void => {
    this.layoutIcons();
    /*
     * ⚠️ VÀ hẹn thêm một lượt: khi vừa xoay máy, bar/dock có thể chưa kịp đổi chiều cao (font/ chữ
     * xuống dòng lại) ⇒ `blockedRects()` đo ra số cũ và trục dọc chia sai.
     */
    this.scheduleIconRelayout();
  };

  /**
   * CHẠY LỆNH CỦA MỘT NÚT THƯỜNG — chỉ còn THOÁT và CÀI ĐẶT.
   *
   * ⚠️ Mọi lệnh MUA đã chuyển sang avatar (`[data-icon]`, xem `tapIconCommand`/`dropCommand`) nên
   * hai nhánh `data-land`/ `data-summon`/ `data-farmer` cũ ĐÃ BỊ XOÁ. Để lại chúng là giữ một đường
   * mua thứ hai không còn DOM nào tạo ra — và nếu ai đó vô tình dựng lại, nó sẽ gọi API cũ.
   */
  private runButtonCommand(btn: HTMLElement): void {
    if (btn.id === 'btnArenaExit' || btn.id === 'btnArenaResultExit') {
      /*
       * ⚠️ HAI NÚT, MỘT LỆNH: `#btnArenaExit` (BACK trên thanh trên) và `#btnArenaResultExit` (nút mới
       * trên BĂNG KẾT QUẢ — yêu cầu *"khi chiến thắng hiển thị nút thoát trên thông báo đó"*). Gộp về
       * MỘT `onExit` để luật "trận đã kết thúc thì thoát thẳng, đang chơi thì hỏi xác nhận" chỉ có một
       * chỗ (xem `Game.exitArena`); nút băng kết quả không được tự ý bỏ qua bước hỏi của nút BACK.
       */
      this.onExit?.();
      return;
    }

    // ⚙️ mở bảng Cài đặt dùng chung (ngôn ngữ + âm thanh). Xem `onSettings`.
    if (btn.id === 'btnArenaSettings') this.onSettings?.();
  }

  /**
   * CÚ BẤM TRÊN AVATAR — MUA TẠI NHÀ ("tướng chạy ra ngay từ nhà").
   *
   * ⚠️ TOẠ ĐỘ LẤY TỪ `homeDeployPoint()` (suy từ `ARENA_CORE`), KHÔNG hard-code và KHÔNG dùng tâm
   * Nhà — tâm Nhà bị luật `isValidDeployPos` cấm (`in-core`), xem chú thích hàm đó.
   *
   * ⚠️ KHOÁ LẠ ⇒ KHÔNG làm gì và KHÔNG ném: `data-icon` là chuỗi tự do trong DOM, còn `land:0` từ
   * bản cũ có thể còn trong DOM của một nhịp vẽ cũ. `iconAction()` lọc hộ.
   */
  private tapIconCommand(node: HTMLElement): void {
    const host = this.host;
    if (!host) return;
    // avatar KHOÁ (thiếu tài nguyên) ⇒ không mua; xem chú thích dài ở `onPointerDown`
    if (node.dataset.locked) return;
    const action = iconAction(node.dataset.icon ?? '');
    if (!action) return; // khoá lạ (save cũ/ DOM cũ) ⇒ bỏ qua, không mua bừa
    const home = homeDeployPoint();
    /*
     * ⚠️ `autoMine = true` CHỈ Ở ĐƯỜNG BẤM NÀY (yêu cầu người dùng 02/10/2026): bấm avatar nông dân
     * thì nông dân ra ở Nhà và tự đi tới mỏ "ưng" (mỏ vắng nhất nửa sân mình) — xem `buyFarmer`.
     * Đường KÉO-THẢ (`dropCommand`) KHÔNG truyền cờ này: người chơi đã chỉ chỗ đứng thì nông dân đi
     * mỏ GẦN ĐIỂM THẢ như cũ.
     */
    this.reportDeploy(
      action.kind === 'hero'
        ? host.summonHero(action.classId, home.x, home.y)
        : host.buyFarmer(home.x, home.y, true),
      action.kind === 'hero',
    );
  }

  /**
   * BÁO KẾT QUẢ MỘT LỆNH MUA/ THẢ QUÂN.
   *
   * ⚠️⚠️ YÊU CẦU NGƯỜI DÙNG 03/10/2026: *"Bỏ thông báo thả tướng nào ra sân trên màn hình"* ⇒ lệnh THẢ
   * TƯỚNG thành công **KHÔNG hiện toast** nữa (tướng đã hiện ra trên sân ngay chỗ bóng ướm — thông báo
   * chỉ che sân và lặp lại điều mắt vừa thấy).
   *
   * ⚠️ LỆNH LỖI VẪN PHẢI HIỆN TOAST: đó là cách DUY NHẤT người chơi biết vì sao cú thả không ăn (thả
   * xuống sông/ nửa sân địch/ thiếu tiền). Bỏ luôn nhánh lỗi là biến "thả sai" thành "game đơ".
   * ⚠️ NÔNG DÂN giữ nguyên toast: không phải "thả tướng", và nông dân ra ở Nhà rồi tự đi mỏ nên không
   * nhìn thấy ngay như tướng.
   */
  private reportDeploy(res: { ok: boolean; message: string }, isHero: boolean): void {
    if (res.ok && isHero) {
      this.clearToast(); // dọn toast cũ (nếu có) — lần thả này thành công nên không có gì để báo
      return;
    }
    this.flash(res);
  }

  /**
   * NHẢ TAY SAU KHI KÉO — MUA TẠI ĐIỂM THẢ.
   *
   * ⚠️⚠️ QUY ĐỔI TOẠ ĐỘ QUA SCENE (`host.worldFromScreen`), KHÔNG tự tính: canvas `Scale.FIT` làm
   * tỉ lệ phụ thuộc kích thước canvas thật + có letterbox khi màn hình khác tỉ lệ 5:3. Xem chú thích
   * dài ở `ArenaHudHost.worldFromScreen` về lý do đây là đường DUY NHẤT đúng.
   *
   * ⚠️ SIM LÀ NƠI PHÁN QUYẾT CUỐI: HUD KHÔNG kiểm "toạ độ hợp lệ" trước khi gọi. Thả xuống sông/ nửa
   * địch ⇒ sim trả `ok:false` ⇒ `flash()` hiện toast lỗi và **KHÔNG trừ tài nguyên** (thứ tự kiểm
   * trong `summonHero` đã đặt toạ độ TRƯỚC tiền — xem chú thích ở đó).
   *
   * ⚠️ `worldFromScreen` trả số rác (scene lỗi/ camera chưa sẵn sàng) ⇒ chặn bằng `Number.isFinite`
   * và báo lỗi rõ, KHÔNG gọi sim với `NaN` (sim sẽ từ chối nhưng thông báo sẽ là "toạ độ không hợp
   * lệ", che mất nguyên nhân thật là tầng quy đổi).
   */
  private dropCommand(key: IconKey, clientX: number, clientY: number): void {
    const host = this.host;
    if (!host) return;
    const action = iconAction(key);
    if (!action) return;
    const p = host.worldFromScreen(clientX, clientY);
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) {
      this.flash({ ok: false, message: t('Không xác định được vị trí thả.', 'Could not resolve the drop position.') });
      return;
    }
    this.reportDeploy(
      action.kind === 'hero' ? host.summonHero(action.classId, p.x, p.y) : host.buyFarmer(p.x, p.y),
      action.kind === 'hero',
    );
  }

  // ────────────────────────────────────────────────────────────── KÉO THẢ ICON (DOM)

  /**
   * MỞ THAO TÁC KÉO cho một icon.
   *
   * ⚠️⚠️ ICON LÀ MỘT NÚT CỐ ĐỊNH CỦA HUD — NÓ **KHÔNG** DI CHUYỂN THEO TAY.
   * Người dùng đã nói rõ (02/10/2026): *"Ý tôi là avatar di chuyển theo tay là sai"*. Hành vi đúng:
   *   · ICON đứng yên tại vị trí bố cục của nó trong suốt thao tác (chỉ đổi TRẠNG THÁI: `drop-target`/
   *     `is-blocked` — xem `updateDeployGhost`);
   *   · chỉ BÓNG XEM TRƯỚC đi theo tay (scene vẽ, chân bóng tại điểm sẽ thả);
   *   · nhả tay ⇒ nhân vật ra tại chỗ bóng (xem `dropCommand`), icon KHÔNG đổi chỗ và KHÔNG ghi vị trí.
   *
   * ⚠️ VÌ SAO VẪN CẦN `setPointerCapture` DÙ ICON KHÔNG DI CHUYỂN: `pointermove`/`pointerup` phải bắn
   * liên tục kể cả khi con trỏ đã đi XA khỏi hộp của nút (kéo ra giữa sân). Không capture thì sự kiện
   * ngừng ở mép nút ⇒ bóng đứng im giữa đường. Đây là lý do capture, KHÔNG phải để dời nút.
   *
   * ⚠️ Nút kéo được phải có `pointer-events: auto` — vỏ `#arenaHud` là lớp phủ `pointer-events:none`
   * (để không nuốt cú bấm chọn land trên sân), nên icon phải TỰ bật lại. CSS `.arena-drag-icon` lo
   * việc đó; ở đây ghi thêm bằng inline style để nút sống sót kể cả khi CSS chưa kịp có class đó.
   */
  private startIconDrag(ev: PointerEvent, node: HTMLElement, key: IconKey): void {
    // avatar KHOÁ (thiếu tài nguyên) ⇒ không mở thao tác kéo; xem chú thích dài ở `onPointerDown`
    if (node.dataset.locked) return;
    /*
     * ⚠️ HỦY THAO TÁC CŨ trước khi mở thao tác mới: `activePointers: 3` trong Phaser + chạm đa điểm
     * nghĩa là có thể có 2 ngón cùng lúc. Không huỷ thì 2 thao tác cùng sửa `this.drag` và bóng nhảy
     * loạn giữa hai ngón tay.
     */
    this.cancelDrag();
    this.pendingBtn = null;
    this.drag = {
      key,
      pointerId: ev.pointerId,
      node,
      /*
       * ⚠️ CHỐT HÀNH ĐỘNG NGAY TẠI ĐÂY (không tra lại mỗi `pointermove`): `data-icon` là chuỗi tự do,
       * và node có thể bị `render()` thay giữa thao tác. `iconAction()` trả `null` cho khoá lạ ⇒
       * `updateDeployGhost` bỏ qua bóng thay vì ném `TypeError` giữa trận.
       */
      action: iconAction(key),
      lastX: ev.clientX,
      lastY: ev.clientY,
      total: 0,
      moved: false,
    };
    try {
      node.setPointerCapture(ev.pointerId);
    } catch {
      /* pointer đã kết thúc (chạm rất nhanh) ⇒ không sao, `window` vẫn nhận được `pointerup` */
    }
    this.root.classList.add('is-dragging');
    node.classList.add('dragging');
  }

  /*
   * ⚠️⚠️ `moveDragNode()` VÀ `commitIconPos()` ĐÃ BỊ XOÁ (02/10/2026) — ĐỌC ĐỂ KHÔNG KHÔI PHỤC.
   *
   * Hai hàm đó phục vụ mô hình "KÉO NÚT ĐI CHỖ KHÁC": icon bám con trỏ và vị trí thả được lưu xuống
   * `localStorage`. Người dùng đã bác bỏ mô hình đó bằng đúng một câu: *"Ý tôi là avatar di chuyển theo
   * tay là sai"* — avatar là MỘT NÚT CỦA HUD, phải đứng yên tại vị trí bố cục.
   *
   * Hệ quả (đã kiểm lại từng đường):
   *   · `pointermove` KHÔNG ghi `style.left/top` nữa ⇒ icon đứng yên tuyệt đối trong thao tác;
   *   · nhả tay KHÔNG ghi kho vị trí ⇒ không còn "vị trí lưu" nào sinh ra từ thao tác kéo;
   *   · kho vị trí (`arenaIconStore`) VẪN CÒN đường ĐỌC: nó là chỗ `arrangeIconLayout` lấy neo (dữ liệu
   *     cũ trong `localStorage`, hoặc neo do công cụ/ test gieo) và bất biến "không chồng" vẫn phải
   *     đúng với chúng. Chỉ đường GHI từ thao tác kéo bị bỏ.
   *
   * ⚠️ `setPointerCapture` (trong `startIconDrag`) VẪN BẮT BUỘC dù nút không di chuyển: nó giữ cho
   * `pointermove`/`pointerup` bắn tiếp khi con trỏ đã đi xa khỏi hộp của nút.
   */

  /** Huỷ thao tác kéo đang dở (đổi màn hình/ gọi lại `bind()`/ mất capture). */
  private cancelDrag(): void {
    const drag = this.drag;
    if (!drag) return;
    this.drag = null;
    /*
     * ⚠️ BÓNG PHẢI ĐƯỢC XOÁ Ở ĐƯỜNG HUỶ NÀY NỮA: `cancelDrag()` chạy khi xoay màn hình/ rời màn/ gọi
     * lại `bind()`. Bỏ sót là bóng nằm lại trên sân (hoặc trên sân TRẬN SAU) mà không có gì chỉ ra
     * nguyên nhân — lỗi chỉ thấy được bằng mắt.
     */
    this.hideDeployGhost();
    drag.node.classList.remove('dragging', 'drop-target', 'is-blocked');
    try {
      if (drag.node.hasPointerCapture?.(drag.pointerId)) drag.node.releasePointerCapture(drag.pointerId);
    } catch {
      /* node đã biến mất */
    }
    drag.node.classList.remove('dragging');
    this.root.classList.remove('is-dragging');
    this.layoutIcons();
  }

  // ────────────────────────────────────────────────────── DỰNG & ĐẶT CHỖ ICON

  /** Kích thước nút icon mặc định (px) — dùng khi `getBoundingClientRect()` chưa có số. */
  private iconSize(): number {
    return 44;
  }

  /** Khung nhìn hiện tại. Tách hàm để test/ thay thế được và để `clampIconPos` luôn nhận số hợp lệ. */
  private viewport(): Vec2Size {
    const w = typeof window !== 'undefined' ? window.innerWidth : 0;
    const h = typeof window !== 'undefined' ? window.innerHeight : 0;
    return {
      w: Number.isFinite(w) && w > 0 ? w : 0,
      h: Number.isFinite(h) && h > 0 ? h : 0,
    };
  }

  /** Nửa kích thước của một node icon (đo thật; chưa có layout ⇒ dùng mặc định). */
  private iconHalf(node?: HTMLElement | null): Vec2 {
    const r = node?.getBoundingClientRect();
    const s = this.iconSize();
    return {
      x: r && r.width > 0 ? r.width / 2 : s / 2,
      y: r && r.height > 0 ? r.height / 2 : s / 2,
    };
  }

  /** Toàn bộ node icon đang có trong DOM, tra theo `data-icon`. */
  private iconNodes(): Map<IconKey, HTMLElement> {
    const map = new Map<IconKey, HTMLElement>();
    this.root.querySelectorAll<HTMLElement>('[data-icon]').forEach((el) => {
      const k = el.dataset.icon;
      if (k) map.set(k, el);
    });
    return map;
  }

  /**
   * HTML CỦA LỚP AVATAR KÉO THẢ — MỘT AVATAR MỖI CLASS ĐÃ MỞ KHOÁ + NÔNG DÂN.
   *
   * ⚠️⚠️ MỌI CHUỖI ĐI QUA `t('vi','en')` — kể cả `aria-label`. Đây là chỗ dễ sót nhất của file này:
   * một `title` tiếng Việt viết thẳng thì `tests/i18n.test.ts` KHÔNG bắt được (nó kiểm bảng dịch,
   * không kiểm DOM), còn các bài kiểm Chrome quét `innerText` tìm ký tự có dấu.
   *
   * ⚠️ AVATAR PHẢI "NHÌN RA ĐƯỢC LÀ BẤM ĐƯỢC": ô VUÔNG BO GÓC chứa ẢNH ĐẦU nhân vật (`.adi-face`,
   * xem `src/style.css`), viền + nền tint theo `color` của class (inline style, một nguồn sự thật là
   * `CLASSES[id].color`), bóng đổ và `cursor: grab` (CSS). Nông dân không có màu class nên giữ nền mặc
   * định của CSS.
   *
   * ⚠️ KHI CLASS CHƯA CÓ ẢNH ĐẦU (`headImageUrl()` trả `null`) ⇒ rơi về GLYPH emoji. Đây là lưới an
   * toàn cho `carpenter`/ `diver` (chưa có sheet bộ phận) — KHÔNG phải đường chính.
   *
   * ⚠️⚠️ VÌ SAO KHÔNG CÒN CLASS `on`/ `aria-pressed`: bản cũ đánh dấu ICON LAND ĐANG CHỌN. Luật mới
   * không có "đang chọn" — mỗi avatar là một lệnh mua độc lập, không có trạng thái nào để sáng đèn.
   * Giữ lại `aria-pressed` sẽ nói với trình đọc màn hình rằng nút này là công tắc (SAI).
   *
   * ⚠️ KHOÁ LẠ BỊ BỎ QUA (`iconAction` trả `null`) ⇒ KHÔNG dựng nút rỗng. Nút rỗng bấm vào không có
   * gì xảy ra là kiểu lỗi tệ nhất khi chơi thử; bỏ hẳn nút thì người chơi không thấy nó.
   *
   * ⚠️ KHÔNG DÙNG DẤU BACKTICK TRONG COMMENT BÊN TRONG HÀM NÀY khi nó nằm trong template literal.
   *
   * ⚠️⚠️ `resource` LÀ SỐ TÀI NGUYÊN HIỆN CÓ, VÀ NÓ QUYẾT ĐỊNH AVATAR CÓ BỊ KHOÁ HAY KHÔNG.
   * Người dùng: *"nếu không đủ tiền mua nhân vật thì avatar nhân vật xám đi không cho kéo hay click vào
   * để mua"*. Khoá ở đây chỉ là TÍN HIỆU + chặn ở tầng DOM; sim vẫn là nơi phán quyết cuối (nó từ chối
   * khi thiếu tiền — xem `summonHero`), nên hai tầng không thể lệch nhau.
   *
   * ⚠️⚠️ HAI LÝ DO KHOÁ, KHÁC NHAU VỀ CÂU CHỮ: (1) không đủ tài nguyên; (2) nông dân đã CHẠM TRẦN
   * (`farmerCount >= farmerMax`, yêu cầu *"để max mua được 4 farmer"*). Cả hai đều khoá DOM (`is-locked`
   * + `data-locked`), nhưng nhãn phải nói ĐÚNG lý do — người chơi đủ tiền mà vẫn không mua được sẽ
   * tưởng game lỗi nếu câu chữ vẫn là "không đủ tài nguyên".
   */
  private iconsHtml(
    keys: readonly IconKey[],
    heroes: ReadonlyArray<{ classId: ClassId; cost: number }>,
    farmerCost: number,
    resource: number,
    farmerCount: number,
    farmerMax: number,
  ): string {
    const price = new Map<ClassId, number>();
    for (const h of heroes) price.set(h.classId, h.cost);

    const parts: string[] = [];
    for (const key of keys) {
      const action = iconAction(key);
      if (!action) continue;
      const isHero = action.kind === 'hero';
      const glyph = isHero ? CLASS_AVATAR_GLYPH[action.classId] : FARMER_GLYPH;
      /*
       * ẢNH ĐẦU NHÂN VẬT — đường CHÍNH của avatar (yêu cầu người dùng). `null` (class chưa có art)
       * ⇒ rơi về glyph bên dưới, KHÔNG dựng `<img>` trỏ file 404.
       */
      const face = headImageUrl(action);
      const name = isHero ? className(action.classId) : t(ARENA_TEXT.farmer, ARENA_TEXT_EN.farmer);
      /*
       * ⚠️ NHÃN NÓI RÕ CẢ HAI CÁCH DÙNG (chạm/ kéo): người dùng yêu cầu ĐÚNG hai đường này, và người
       * chơi không tự đoán được rằng kéo avatar ra sân là "mua tại chỗ".
       *
       * ⚠️ Khi ĐÃ có ảnh đầu thì nhãn KHÔNG còn emoji: yêu cầu là *"avatar là hình đầu nhân vật"*, còn
       * để emoji trong `title`/ `aria-label` là giữ lại đúng thứ vừa bị thay (và trình đọc màn hình sẽ
       * đọc "💪 Dân tập gym" chồng lên tên class). Glyph chỉ còn trong nhãn khi ảnh CHƯA có.
       */
      const iconPart = face ? '' : `${glyph} `;
      /*
       * ⚠️⚠️ GIÁ + "CÓ MUA ĐƯỢC KHÔNG" PHẢI TÍNH TRƯỚC NHÃN, vì nhãn phải NÓI RA lý do khoá.
       *
       * Người dùng: *"nếu không đủ tiền mua nhân vật thì avatar nhân vật xám đi không cho kéo hay click
       * vào để mua"*. `locked` ở đây chỉ là TÍN HIỆU + chặn ở tầng DOM; `summonHero`/`buyFarmer` của sim
       * vẫn là nơi phán quyết cuối (nó từ chối khi thiếu tiền), nên hai tầng không thể lệch nhau.
       *
       * ⚠️ `hasCost` là điều kiện TIÊN QUYẾT: ảnh chụp có thể tới sớm (chưa nối `heroes`/`farmerCost`)
       * hoặc giá rác ⇒ KHÔNG có giá thì KHÔNG khoá (khoá oan một avatar không có giá là chặn người chơi
       * vô cớ). Không có giá ⇒ cũng không vẽ huy hiệu, tuyệt đối không in `NaN`/ số âm lên avatar.
       *
       * ⚠️ Nguồn giá: tướng lấy từ `heroes` của scene, nông dân lấy `snapshot.farmerCost` (do `Game`
       * lấy từ `ArenaWorld.farmerCost('left')` — MỘT nguồn sự thật với `buyFarmer`), KHÔNG tự tính ở HUD.
       */
      const cost = isHero ? price.get(action.classId) : farmerCost;
      const hasCost = typeof cost === 'number' && Number.isFinite(cost) && cost > 0;
      /*
       * ⚠️ `farmerFull` CHỈ áp cho avatar NÔNG DÂN, và chỉ khi ảnh chụp có ĐỦ hai số (trần > 0).
       * Thiếu số (ảnh chụp cũ/ scene chưa nối) ⇒ KHÔNG khoá oan — cùng nguyên tắc với `hasCost`.
       */
      const farmerFull =
        !isHero && Number.isFinite(farmerMax) && farmerMax > 0 && Number.isFinite(farmerCount) && farmerCount >= farmerMax;
      const locked = farmerFull || (hasCost && (cost as number) > resource);
      /*
       * ⚠️ NHÃN NÓI RÕ CẢ HAI CÁCH DÙNG (chạm/ kéo): người dùng yêu cầu ĐÚNG hai đường này, và người
       * chơi không tự đoán được rằng kéo avatar ra sân là "mua tại chỗ". Avatar bị khoá thì nhãn đổi
       * sang nói LÝ DO — người chơi nhìn số tài nguyên trên thanh HUD là hiểu ngay.
       *
       * ⚠️ Khi ĐÃ có ảnh đầu thì nhãn KHÔNG còn emoji: yêu cầu là *"avatar là hình đầu nhân vật"*, còn
       * để emoji trong `title`/ `aria-label` là giữ lại đúng thứ vừa bị thay (và trình đọc màn hình sẽ
       * đọc "💪 Dân tập gym" chồng lên tên class). Glyph chỉ còn trong nhãn khi ảnh CHƯA có.
       */
      const label = farmerFull
        ? t(
            '{icon}{name} — đã đủ {max} nông dân',
            '{icon}{name} — farmer limit reached ({max})',
            { icon: iconPart, name, max: farmerMax },
          ).trim()
        : locked
          ? t(
              '{icon}{name} — không đủ tài nguyên để mua',
              '{icon}{name} — not enough resource to buy',
              { icon: iconPart, name },
            ).trim()
          : t(
              '{icon}{name} — chạm để mua tại Nhà, kéo để thả vào sân',
              '{icon}{name} — tap to buy at Home, drag to deploy on the field',
              { icon: iconPart, name },
            ).trim();
      /*
       * HUY HIỆU GIÁ — người dùng: *"Avatar Nông dân chưa hiển thị số tiền như các nhân vật khác"* ⇒
       * nông dân CŨNG có huy hiệu. Chỉ vẽ khi giá hợp lệ (`hasCost`: `Number.isFinite` + `> 0`) — không
       * có giá thì không vẽ, tuyệt đối không in `NaN`/ số âm lên avatar.
       */
      const badge = hasCost ? `<span class="adi-cost">${formatArenaResource(cost as number)}</span>` : '';
      const dataAttr = isHero
        ? `data-icon="${key}" data-summon="${action.classId}"${locked ? ' data-locked="1" aria-disabled="true"' : ''}`
        : `data-icon="${key}" data-farmer="1"${locked ? ' data-locked="1" aria-disabled="true"' : ''}`;
      /*
       * ⚠️⚠️ KHÔNG ĐƯỢC ĐẶT `border-color` Ở ĐÂY — BUG ĐÃ ĐO ĐƯỢC TRONG CHROME (script §7).
       *
       * Inline style THẮNG mọi rule trong `src/style.css`. Bản đầu tôi nhét `border-color:#<màu class>`
       * để avatar có viền riêng; hệ quả: `.arena-drag-icon.is-blocked { border-color:#f87171 }` và
       * `.drop-target { border-color:#fff3c4 }` KHÔNG BAO GIỜ hiện ⇒ mất hẳn TÍN HIỆU MÀU của trạng
       * thái "không thả được"/ "thả được". Số đo thật: viền đọc ra `rgb(217,119,6)` (cam của strength)
       * trong khi `is-blocked` đang bật.
       *
       * Nên tint theo class CHỈ đặt `background`; viền để CSS quyết (nó là nơi duy nhất biết hai
       * trạng thái kéo thả). Phân biệt class đã có ẢNH ĐẦU + nền tint — đủ, không cần viền.
       */
      const tint = isHero
        ? `pointer-events:auto;background:${classTintBackground(CLASSES[action.classId].color)}`
        : 'pointer-events:auto';
      parts.push(`
        <button class="arena-drag-icon${locked ? ' is-locked' : ''}" ${dataAttr} style="${tint}"
          title="${label}" aria-label="${label}">${face ? `<img class="adi-face" src="${face}" alt="" aria-hidden="true" draggable="false">` : glyph}${badge}</button>`);
    }

    /*
     * ⚠️ `role="group"` + `aria-label`: cả nhóm avatar là MỘT cụm điều khiển nổi, không phải nội dung
     * rời. `pointer-events: none` trên lớp bọc để cú bấm TRÊN SÂN xuyên qua vùng trống — đây là lý do
     * vỏ HUD là lớp phủ, và avatar tự bật lại `pointer-events: auto` bằng inline style.
     */
    return `
      <div class="arena-icon-layer" role="group" aria-label="${t('Avatar mua tướng — kéo thả', 'Hero purchase avatars — drag and drop')}" style="position:absolute; inset:0; pointer-events:none">
        ${parts.join('')}
      </div>`;
  }

  /**
   * GHI CHIỀU CAO **ĐO ĐƯỢC** CỦA THANH TRÊN VÀO `--arena-bar-h` — chỉ để định vị DẢI CHIÊU.
   *
   * ⚠️⚠️ VÌ SAO PHẢI ĐO (BUG ĐÃ ĐO ĐƯỢC Ở 412×915, 02/10/2026): `.arena-skill-strip` là
   * `position: fixed; top: 46px` — hằng số đó đúng ở màn ngang (bar ~46-51px) nhưng ở khổ DỌC thanh
   * trên cao **98px** (2 hàng) ⇒ chip chiêu nằm ở y 46..69, TỨC NẰM TRONG HÀNG 2 CỦA THANH và che mất
   * chip "Phe địch" (đo được: chip chiêu x 107..305 × y 46..69; chip điểm phải x 147..235 × y 52..92).
   *
   * ⚠️ KHÔNG hard-code 98px: chiều cao thanh đổi theo bề rộng màn (xuống hàng), theo ngôn ngữ (chữ dài
   * hơn) và theo `env(safe-area-inset-top)`. Đo rồi ghi vào biến CSS là cách duy nhất đúng cho mọi ca.
   * `.arena-skill-strip` dùng `top: calc(var(--arena-bar-h, 46px) + 2px)` — giá trị dự phòng giữ đúng
   * hành vi cũ khi chưa đo được (khung hình đầu).
   */
  private publishBarHeight(): void {
    const bar = this.root.querySelector<HTMLElement>('.arena-hud-bar');
    const h = bar?.getBoundingClientRect().height ?? 0;
    if (h > 0) this.root.style.setProperty('--arena-bar-h', `${Math.round(h)}px`);
  }

  /**
   * ĐẶT LẠI TOẠ ĐỘ CHO MỌI ICON — đọc kho vị trí, tính px, ghi `style.left/top`.
   *
   * Idempotent và KHÔNG dựng lại DOM ⇒ gọi được từ `resize`/ `orientationchange`/ `render()`.
   * Node nào chưa có neo đã lưu thì dùng BỐ CỤC MẶC ĐỊNH (hàng ngang ở mép dưới, xem `defaultIconPx`).
   */
  private layoutIcons(): void {
    /* Ghi chiều cao thanh trên TRƯỚC mọi nhánh `return` — dải chiêu cần số đó để không đè thanh. */
    this.publishBarHeight();
    const nodes = this.iconNodes();
    if (nodes.size === 0) return;
    const size = this.viewport();
    if (size.w <= 0 || size.h <= 0) return; // HUD đang ẩn (0×0) ⇒ để nhịp sau đặt lại
    const blocked = this.blockedRects();
    /*
     * ⚠️⚠️ ĐO CỠ CSS TRƯỚC, rồi mới chọn bố cục: lượt trước có thể đã GHI cỡ inline (thu nhỏ cho vừa cột
     * ở màn ngang), nên phải XOÁ cỡ inline rồi đo lại — nếu không, xoay sang màn dọc sẽ giữ nguyên cỡ
     * nhỏ của màn ngang. Xoá xong đọc `getBoundingClientRect` (buộc reflow) là ra cỡ CSS thật.
     */
    for (const node of nodes.values()) {
      node.style.width = '';
      node.style.height = '';
    }
    const cssHalf = this.iconHalf(nodes.values().next().value);
    /*
     * ⚠️⚠️ BỐ CỤC THEO DẢI ĐEN CỦA CANVAS (yêu cầu người dùng: xếp dọc NGOÀI ảnh sân ở màn ngang).
     * `layoutDefaultAvatars` tự quyết cột-dọc-hay-hàng-ngang và cỡ avatar cần dùng; ở đây chỉ ĐO dải
     * bằng `#arena canvas` (không tự tính công thức FIT) rồi truyền vào.
     */
    const layout = layoutDefaultAvatars(nodes.size, size, cssHalf, blocked, this.canvasBands());
    const half = layout.half;
    /*
     * ⚠️⚠️ MỌI ICON ĐỀU NẰM TRONG BỐ CỤC — KỂ CẢ ICON ĐANG KÉO.
     *
     * Bản trước loại icon đang kéo ra khỏi phép đặt chỗ (vì nó bám con trỏ). Nay icon KHÔNG di chuyển
     * nữa, nên loại nó ra là SAI theo một cách rất khó thấy: `arrangeIconLayout` xếp theo CHỈ SỐ SLOT,
     * nên bỏ một phần tử là các slot sau dịch lên ⇒ **mọi avatar còn lại nhảy sang trái ngay khi bắt
     * đầu kéo một cái**. Đây đúng loại lỗi "chỉ thấy khi nhìn màn hình".
     */
    const items: IconLayoutItem[] = [...nodes.keys()].map((key) => ({
      key,
      /*
       * ⚠️⚠️ KHỔ DỌC 2 TẦNG ⇒ BỎ QUA NEO ĐÃ LƯU (`saved = null`) — CỐ Ý, KHÔNG PHẢI BỎ SÓT.
       *
       * Ở khổ dọc, chỗ đặt avatar là do SCENE quyết định (cột dọc trong dải trống, xem
       * `arenaPortraitField`). Neo đã lưu được mã hoá theo 6 VÙNG của khung nhìn (`anchorToPx`) — neo
       * gieo ở khổ NGANG rơi vào giữa màn hình dọc, tức ĐÈ LÊN SÂN; mà ở khổ dọc HUD cũng KHÔNG né Nhà
       * được nữa (2 camera ⇒ phép suy hình chữ nhật Nhà từ `worldFromScreen` là rác — xem `blockedRects`),
       * nên không có gì kẹp neo đó ra.
       *
       * ⚠️ ĐÁNH ĐỔI: tính năng "nhớ vị trí avatar" không áp cho khổ dọc. Chấp nhận được vì thao tác kéo
       * KHÔNG ghi vị trí nữa (icon là nút cố định — xem `commitIconPos`), nên dữ liệu neo chỉ còn là di
       * sản từ bố cục cũ; ưu tiên ở đây là "avatar không bao giờ nằm trên sân".
       */
      saved: this.host?.arenaPortraitField?.() ? null : arenaIconStore().get(key),
    }));
    /*
     * ⚠️⚠️ MỘT HÀM DUY NHẤT ĐẶT CHỖ CHO MỌI AVATAR — KHÔNG còn nhánh "mặc định" riêng ở đây.
     * Lý do: bất biến "không 2 avatar giao nhau" phải đúng cho CẢ vị trí ĐÃ LƯU (neo cũ trong
     * `localStorage`, hoặc neo do công cụ/ test gieo). Nếu mặc định và đã-lưu đi hai đường riêng thì
     * chỉ một đường được khoá bất biến — đúng kiểu lỗi "test xanh mà game vẫn chồng nút".
     * `arrangeIconLayout` xếp lần lượt và coi avatar đã đặt là vật cản.
     *
     * ⚠️ `half` và `positions` lấy từ CÙNG `layout` ⇒ bố cục (cột/ hàng) và bất biến không chồng luôn
     * nói về đúng một hệ toạ độ, kể cả khi phải thu nhỏ avatar cho vừa cột.
     */
    const placements = arrangeIconLayout(items, size, half, blocked, layout.positions);
    for (const { key, pos } of placements) {
      const node = nodes.get(key);
      if (!node) continue;
      node.style.pointerEvents = 'auto';
      /* Chỉ ghi cỡ inline khi THẬT SỰ phải thu nhỏ — để CSS tự quyết cỡ ở các chế độ bình thường. */
      if (Math.abs(half.x - cssHalf.x) > 0.01 || Math.abs(half.y - cssHalf.y) > 0.01) {
        node.style.width = `${2 * half.x}px`;
        node.style.height = `${2 * half.y}px`;
      }
      node.style.left = `${Math.round(pos.x - half.x)}px`;
      node.style.top = `${Math.round(pos.y - half.y)}px`;
    }
  }

  /**
   * MÉP TRÁI/ PHẢI CỦA **VÙNG SÂN ĐANG NHÌN** (px khung nhìn) — "dải đen hai bên" ở màn ngang.
   *
   * ⚠️⚠️ HAI NGUỒN, THEO THỨ TỰ ƯU TIÊN — ĐỪNG GỘP LẠI:
   *   · KHỔ DỌC 2 TẦNG: hỏi SCENE (`host.arenaPortraitField()`), vì dải trống nằm BÊN TRONG canvas
   *     (camera có viewport hẹp hơn canvas) nên đo DOM là ra `left = 0`. Xem chú thích ở hợp đồng đó.
   *   · KHỔ NGANG/ DESKTOP: ĐO `#arena canvas` bằng `getBoundingClientRect()`, KHÔNG tính lại `Scale.FIT`
   *     (xem `CanvasBands`). Trả `null` khi chưa có canvas/ số đo rác ⇒ nơi gọi lùi về hàng ngang.
   */
  private canvasBands(): CanvasBands | null {
    const portrait = this.host?.arenaPortraitField?.();
    if (portrait) {
      /*
       * ⚠️ `avatarColumns === false` ⇒ trả `null` (KHÔNG trả dải hẹp): scene đã chừa chỗ cho hàng ngang,
       * và `layoutDefaultAvatars` chỉ lùi về hàng khi nhận `null`/ dải quá hẹp. Trả dải hẹp cũng cho ra
       * hàng, nhưng `null` nói ĐÚNG ý định ("không có dải để dùng") nên đọc lại dễ hiểu hơn.
       */
      return portrait.avatarColumns ? { left: portrait.left, right: portrait.right } : null;
    }
    if (typeof document === 'undefined') return null;
    const el = document.querySelector('#arena canvas');
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) return null;
    return { left: r.left, right: r.right };
  }

  /**
   * HÌNH CHỮ NHẬT "CẤM ĐẶT ICON" — bar trên, dock dưới VÀ **HAI NHÀ**, đo/ suy từ nguồn thật.
   *
   * ⚠️ Vì sao đo chứ không dùng hằng số: bar/dock đổi chiều cao theo chế độ màn hình (đo được:
   * bar 51px ở màn ngang thấp / 75px ở desktop; dock 68px / 140px). Hằng số ở đây là lỗi lặp lại
   * đúng loại `--arena-top-h`/ `--arena-dock-h` mà cả hai chúng ta vừa gỡ bỏ.
   *
   * ⚠️ Nới thêm 6px mỗi cạnh: để viền/ bóng của icon không chạm vào nút (chạm là `elementFromPoint`
   * ở tâm nút có thể trả về icon ⇒ nút mất bấm, dù chỉ lệch 1px).
   *
   * ⚠️⚠️ THÊM NHÀ VÀO ĐÂY LÀ YÊU CẦU CỦA LEAD (đo được trong `art/verify-arena-render/`): Nhà mới to
   * gấp ~1,9 lần và nằm sát hai mép, nên cột avatar mặc định ở mép PHẢI ĐÈ LÊN Nhà địch. Xem
   * `coreRects()` để biết cách suy hình chữ nhật của Nhà mà KHÔNG hard-code.
   */
  private blockedRects(): Rect[] {
    const out: Rect[] = [];
    for (const sel of ['.arena-hud-bar', '.arena-dock']) {
      const el = this.root.querySelector<HTMLElement>(sel);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      out.push({ x: r.left - 6, y: r.top - 6, w: r.width + 12, h: r.height + 12 });
    }
    const portrait = this.host?.arenaPortraitField?.();
    /*
     * ⚠️⚠️ KHỔ DỌC: CỘT AVATAR DỒN VÀO **NỬA DƯỚI** MÀN HÌNH — YÊU CẦU NGƯỜI DÙNG 02/10/2026:
     * *"Đối với màn hình dọc, 6 avatar dọc theo 1/2 màn bên dưới"*.
     *
     * Cách làm: ĐẨY một hình cấm PHỦ HẾT BỀ NGANG cho vùng TRÊN đường chia 2 tầng. `defaultIconBand`
     * tính "khe dọc trống" giữa các hình cấm phủ hết bề ngang (đúng cơ chế nó dùng cho bar/ dock), nên
     * cột avatar tự dồn xuống dưới `splitY` mà KHÔNG cần thêm nhánh bố cục nào. Hình cấm này ĐỒNG THỜI
     * là vật cản của `clampOutOfRects` ⇒ không avatar nào (kể cả neo cũ) nằm trên nửa sân địch.
     *
     * ⚠️ LÝ DO THIẾT KẾ: nửa dưới là **SÂN MÌNH** — avatar là nút mua/ thả quân, để cạnh đúng nửa sân mà
     * người chơi thao tác thì kéo-thả ngắn nhất và mắt không phải nhảy giữa hai tầng.
     *
     * ⚠️ CHỈ khi `avatarColumns` (cột dọc trong dải trống): chế độ HÀNG NGANG ở mép dưới đã nằm ở nửa
     * dưới rồi, thêm hình cấm này chỉ tổ thu hẹp chỗ của chính nó.
     */
    if (portrait?.avatarColumns) {
      const splitY = Number.isFinite(portrait.splitY) ? Math.max(0, portrait.splitY) : 0;
      const el = this.root.getBoundingClientRect();
      const vw = el.width > 0 ? el.width : window.innerWidth;
      if (splitY > 0) out.push({ x: -6, y: -6, w: vw + 12, h: splitY + 6 });
    }
    /*
     * ⚠️⚠️ KHỔ DỌC 2 TẦNG ⇒ BỎ HẲN PHÉP NÉ NHÀ — CỐ Ý, KHÔNG PHẢI "QUÊN".
     *
     * `coreRects()` suy hình chữ nhật Nhà bằng 2 điểm mẫu qua `worldFromScreen(0,0)` và
     * `worldFromScreen(w,h)`. Ở khổ dọc, `worldFromScreen` CHỌN CAMERA THEO TẦNG của `y` ⇒ hai điểm mẫu
     * rơi vào HAI CAMERA khác nhau (tầng trên/ tầng dưới có `scrollX` khác nhau) ⇒ hiệu số `dx/dy` không
     * còn là phép biến đổi affine của một camera; vùng "Nhà" tính ra là RÁC và có thể đẩy avatar vô cớ.
     *
     * Và không cần né: avatar nay nằm trong DẢI TRỐNG hai bên sân, ngoài vùng vẽ của cả hai camera.
     * (Ở khổ ngang thì avatar có thể ở trong sân nên phép né vẫn cần — giữ nguyên.)
     */
    if (!portrait) out.push(...this.coreRects());
    return out;
  }

  /**
   * HÌNH CHỮ NHẬT MÀN HÌNH CỦA **HAI NHÀ** — suy từ `ARENA_CORE` + `ARENA_CORE_DRAW_R`, 0 số hard-code.
   *
   * ⚠️⚠️ VÌ SAO PHẢI QUY ĐỔI THẾ GIỚI → MÀN HÌNH Ở ĐÂY: vị trí icon là px màn hình, còn Nhà là toạ độ
   * thế giới. HUD **KHÔNG được tự viết công thức** `Scale.FIT` (xem `ArenaHudHost.worldFromScreen`) —
   * nhưng hợp đồng chỉ có chiều màn-hình → thế-giới. Cách đúng mà không cần thêm phương thức: lấy HAI
   * ĐIỂM MẪU qua chính `worldFromScreen` rồi suy phép biến đổi affine (không xoay) — cùng một đường
   * quy đổi mà HUD dùng lúc thả, nên hai bên không thể lệch nhau.
   *
   * ⚠️ `ARENA_CORE_DRAW_R` là CỠ VẼ của Nhà (không phải vỏ va chạm): cái cần né là ART người chơi NHÌN
   * THẤY, đúng thứ Lead chỉ ra trong ảnh.
   *
   * ⚠️ Trả `[]` khi chưa có host/ số đo rác: thà không né còn hơn né bằng toạ độ `NaN` (icon sẽ biến
   * mất — lỗi tệ hơn nhiều so với icon đè Nhà).
   */
  private coreRects(): Rect[] {
    const host = this.host;
    if (!host || typeof host.worldFromScreen !== 'function') return [];
    const size = this.viewport();
    if (size.w <= 0 || size.h <= 0) return [];
    const a = host.worldFromScreen(0, 0);
    const b = host.worldFromScreen(size.w, size.h);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.abs(dx) < 1e-6 || Math.abs(dy) < 1e-6) return [];
    /* client = f(world) — hàm ngược của phép biến đổi đã lấy mẫu (affine, không xoay). */
    const toClient = (wx: number, wy: number): Vec2 => ({
      x: ((wx - a.x) / dx) * size.w,
      y: ((wy - a.y) / dy) * size.h,
    });
    const out: Rect[] = [];
    for (const cx of [ARENA_CORE.leftX, ARENA_CORE.rightX]) {
      const tl = toClient(cx - CORE_ART_HALF, CORE_ART_BOTTOM - 2 * CORE_ART_HALF);
      const br = toClient(cx + CORE_ART_HALF, CORE_ART_BOTTOM);
      const x = Math.min(tl.x, br.x);
      const y = Math.min(tl.y, br.y);
      const w = Math.abs(br.x - tl.x);
      const h = Math.abs(br.y - tl.y);
      if (!(w > 0 && h > 0) || !Number.isFinite(x) || !Number.isFinite(y)) continue;
      out.push({ x: x - 6, y: y - 6, w: w + 12, h: h + 12 });
    }
    return out;
  }

  /**
   * GỌI KHI NGƯỜI CHƠI BẤM "VỀ TRANG CHỦ" — do `Game` gắn vào.
   * ⚠️ Không để HUD tự gọi `Hud.showScreen`: HUD Arena không biết gì về `Hud` của game TD, và vòng
   * phụ thuộc đó sẽ khiến việc gỡ Arena ra khỏi game là bất khả thi. Một callback là đủ.
   */
  onExit: (() => void) | null = null;

  /**
   * GỌI KHI NGƯỜI CHƠI BẤM ⚙️ (MỞ BẢNG CÀI ĐẶT) — do `Game` gắn vào.
   *
   * ⚠️⚠️ VÌ SAO ARENA CẦN NÚT NÀY (lỗ hổng thật, đo được — không phải "thêm cho đẹp"):
   * `#btnSettingsHome` (⚙️ ở trang chủ) nằm TRONG `#home`, mà CSS có
   * `#app[data-screen='arena'] #home { display: none }`. Nghĩa là **khi ĐANG chơi Arena, KHÔNG có
   * đường nào đổi ngôn ngữ hay tắt/bật tiếng** — người chơi tiếng Anh vào Arena là mắc kẹt trong
   * giao diện tiếng Việt cho tới khi thoát ra. Trong trận 10-wave thì ngược lại: `#btnSettings` nằm
   * trong `#topHud` và bấm được. Arena thiếu đúng nút đó.
   *
   * Bằng chứng phụ (vì sao nó lộ ra lúc này): `tools/verify-arena-task5.mjs` §9b cần đổi ngôn ngữ để
   * kiểm HUD Arena. Bản cũ `import()` thẳng `/src/i18n/index.ts` nên CHẾT trên bản build (§3.2 của
   * bàn giao). Khi sửa sang "bấm đúng đường người chơi dùng" thì Playwright báo
   * `element is not visible` cho `#btnSettingsHome` — tức chính cái lỗ hổng này.
   *
   * Vẫn giữ nguyên kiến trúc: HUD Arena KHÔNG cầm `Hud` của game TD, chỉ báo qua callback (giống
   * `onExit`). `Game` nối nó vào `hud.openSettings()` — CÙNG một `#settingsPanel` dùng chung, nên
   * ngôn ngữ/ âm thanh vẫn chỉ có MỘT nguồn sự thật.
   */
  onSettings: (() => void) | null = null;

  /** HIỆN/ ẨN HUD. `#app[data-screen='arena']` là công tắc CSS; đây chỉ đổi cờ `hidden`. */
  show(): void {
    this.root.classList.remove('hidden');
    /*
     * ⚠️ MỖI LẦN VÀO ARENA LÀ MỘT LƯỢT MỚI: đặt lại cổng bắt đầu + các mốc thông báo. `bind()` cũng
     * chạy mỗi lượt nhưng nó chỉ gắn sự kiện MỘT lần (xem `bound`), nên chỗ đặt lại phải ở `show()`.
     */
    this.resetStageState();
    /*
     * ⚠️⚠️ KHÔNG VẼ LẠI ẢNH CHỤP CỦA LƯỢT TRƯỚC (`this.last`) Ở ĐÂY — BUG ĐÃ ĐO ĐƯỢC 03/10/2026:
     * người dùng báo *"sau khi thắng/thua xong ấn thoát, vào lại thì không thấy nút start"*.
     *
     * NGUYÊN NHÂN: ảnh chụp cuối của lượt trước mang `status` ĐÃ KẾT THÚC ('won'/'lost'), mà
     * `paintStageFx` có nhánh `if (this.startPhase !== 'counting' && s.status !== 'prep') this.startPhase
     * = 'running'` ⇒ nhịp vẽ bằng ảnh chụp CŨ đẩy `startPhase` sang 'running' NGAY SAU `resetStageState()`
     * ⇒ tới khi sim mở lượt mới ở `status = 'prep'` thì điều kiện `startPhase === 'idle'` đã sai, nút
     * `Bắt đầu` KHÔNG BAO GIỜ hiện.
     *
     * SỐ ĐO THẬT (Chrome, trước khi vá): vào lại ⇒ `world.status = 'prep'` nhưng `startPhase = 'running'`,
     * nút tồn tại mà `getBoundingClientRect()` toàn 0 ⇒ `tools/arena-start.mjs` KHÔNG bấm được cổng.
     *
     * Nay `show()` XOÁ ảnh chụp cũ rồi `render(null)` (HUD trống đúng một nhịp). `Game.startArenaFromHome`
     * gọi `renderArenaHud()` NGAY SAU `show()` nên ảnh chụp MỚI được vẽ trong cùng lượt gọi — không có
     * khung hình nào người chơi thấy HUD trống.
     */
    this.last = null;
    this.render(null);
  }

  /** Đặt lại trạng thái cổng bắt đầu + mốc thông báo (dùng cho `show()` và `dispose()`). */
  private resetStageState(): void {
    if (this.startTimer !== null) {
      window.clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    this.startPhase = 'idle';
    this.lastSpeedMul = 1;
    this.lastCountdownSec = null;
    this.speedFxHoldUntil = 0;
    this.stageFx?.clear();
    this.stageFx?.showStart(false);
  }

  hide(): void {
    this.root.classList.add('hidden');
    /*
     * ⚠️ ẢNH CHỤP ĐI THEO LƯỢT TRẬN: rời Arena thì `this.last` là ảnh chụp của trận ĐÃ XONG. Giữ nó lại
     * là để `setResult(null)`/ nhịp vẽ kế tiếp dùng số liệu cũ — chính cơ chế đã gây bug "vào lại không
     * có nút Bắt đầu" (xem `show()`).
     */
    this.last = null;
    /*
     * ⚠️ RỜI MÀN ARENA PHẢI XOÁ BÓNG — bóng do SCENE vẽ nằm trên sân, không phải trong `#arenaHud`,
     * nên `hidden` của HUD KHÔNG che nó. Bỏ sót ⇒ vào trận sau (hoặc về trang chủ) vẫn thấy một bóng
     * tướng mờ đứng giữa sân, không có cách nào đoán ra nguồn.
     */
    this.hideDeployGhost();
    this.clearToast();
    /*
     * ⚠️⚠️ DỪNG VÒNG `rAF` CỦA NGÒI — YÊU CẦU #7. `hide()` là đường RỜI MÀN; để vòng lặp chạy tiếp là
     * vẽ hạt vào một canvas không ai nhìn, mãi mãi (và `#arenaHud.hidden` vẫn nằm trong DOM). Cờ
     * `hidden` cũng là thứ `tickFuseFx` tự kiểm mỗi khung — dừng ở đây là dừng SỚM, không chờ khung sau.
     */
    this.stopFuseFx();
    /*
     * ⚠️ LỚP THÔNG BÁO SỐNG NGOÀI `root` NÊN `hidden` CỦA ROOT KHÔNG CHE NÓ — phải ẩn tay, nếu không
     * chữ "×3"/ số đếm lùi sẽ nằm lại giữa màn hình TRANG CHỦ sau khi thoát trận.
     */
    this.resetStageState();
    this.stageFx?.show(false);
  }

  /**
   * TẠO LƯỜI LỚP THÔNG BÁO GIỮA SÂN + NỐI NÚT `Bắt đầu`. Gọi ở `bind()` (chỉ trong trình duyệt).
   *
   * ⚠️ Nút `Bắt đầu` nối ở ĐÂY (không qua uỷ quyền `root`) vì node của nó KHÔNG nằm trong `root` —
   * xem `ArenaStageFx`.
   */
  private ensureStageFx(): ArenaStageFx | null {
    if (this.stageFx) return this.stageFx;
    if (typeof document === 'undefined' || !document.body) return null;
    this.stageFx = new ArenaStageFx(() => this.beginArenaStart(), t('Bắt đầu', 'Start'));
    return this.stageFx;
  }

  /**
   * CHẠY ĐẾM LÙI 3-2-1 RỒI MỞ CỔNG TRẬN.
   *
   * Yêu cầu người dùng: *"tôi phải click Bắt đầu, sẽ đếm lùi 3s (hiển thị số xuất hiện rồi biến mất
   * 3,2,1,Bắt đầu, thì lúc đó game mới bắt đầu)"*.
   *
   * ⚠️ THỜI ĐIỂM MỞ CỔNG: đúng lúc chữ `Bắt đầu` HIỆN (bước thứ 4), không phải sau khi nó biến mất —
   * người dùng nói "… 3,2,1,Bắt đầu, thì lúc đó game mới bắt đầu".
   * ⚠️ `startPhase` chặn bấm hai lần (nút vẫn ở đó 1 giây đầu): bấm lại khi đang đếm là vô hại.
   */
  private beginArenaStart(): void {
    if (this.startPhase !== 'idle') return;
    this.startPhase = 'counting';
    this.stageFx?.showStart(false);
    const steps: Array<{ text: string; hold: number }> = [
      { text: '3', hold: 1000 },
      { text: '2', hold: 1000 },
      { text: '1', hold: 1000 },
      { text: t('Bắt đầu', 'Start'), hold: 900 },
    ];
    let i = 0;
    const step = (): void => {
      const item = steps[i];
      if (!item) return;
      this.stageFx?.announce(item.text, i < steps.length - 1 ? 'alert' : 'speed', item.hold);
      if (i === steps.length - 1) {
        this.startPhase = 'running';
        this.host?.startArenaNow?.();
        this.startTimer = null;
        return;
      }
      i++;
      this.startTimer = window.setTimeout(step, item.hold);
    };
    step();
  }

  /**
   * VẼ LỚP THÔNG BÁO THEO ẢNH CHỤP — gọi ở ĐẦU `render()` (TRƯỚC nhánh `if (this.drag) return`).
   *
   * ⚠️⚠️ VÌ SAO PHẢI ĐỨNG TRƯỚC NHÁNH KÉO: `render()` tự bỏ qua khi người chơi đang kéo avatar (để không
   * huỷ node đang thao tác) — nếu lớp này nằm sau nhánh đó thì một cú kéo dài đúng 10 giây cuối sẽ làm
   * ĐỨNG đếm lùi cuối trận. Lớp này không đụng `root` nên không có lý do gì để bị chặn.
   */
  private paintStageFx(s: ArenaSnapshot | null): void {
    const fx = this.ensureStageFx();
    if (!fx) return;
    if (!s || this.root.classList.contains('hidden')) {
      fx.show(false);
      return;
    }
    fx.show(true);
    fx.setStartLabel(t('Bắt đầu', 'Start'));
    /*
     * NEO VÀO "GIỮA SÔNG": ở khổ dọc, tâm sân là đường VẠCH CHIA (`.arena-band-seam`), không phải 50%
     * khung nhìn. HUD đọc thẳng biến CSS mà `paintBandSeam` vừa ghi; khổ ngang (không có vạch) ⇒ 50%.
     */
    const seam = this.root.querySelector<HTMLElement>('.arena-band-seam');
    const seamY = seam && seam.offsetParent !== null ? Number.parseFloat(seam.style.getPropertyValue('--seam-y')) : Number.NaN;
    fx.setAnchorY(Number.isFinite(seamY) ? seamY : null);

    /*
     * ⚠️ CỔNG BẮT ĐẦU ĐỌC TỪ `status` CỦA SIM: sim ở `'prep'` = CHƯA bắt đầu (scene cố ý không tick khi
     * cổng còn đóng). Nếu vì lý do nào đó trận đã chạy mà HUD chưa kịp đếm lùi (host giả/ công cụ),
     * trạng thái tự chuyển sang `running` để không hiện nút `Bắt đầu` đè lên trận đang đánh.
     */
    if (this.startPhase !== 'counting' && s.status !== 'prep') this.startPhase = 'running';
    fx.showStart(this.startPhase === 'idle' && s.status === 'prep');

    if (this.startPhase !== 'running') return;

    /*
     * (1) MỐC TỐC ĐỘ ×2/ ×3/ ×5 — chữ lớn + nháy MẠNH. `arenaTimeSpeedMul` là MỘT nguồn sự thật với vòng
     * lặp tick của scene (xem `ARENA_SPEED_RAMP`), nên thông báo không thể lệch với tốc độ thật.
     * ⚠️ PHẢI truyền `s.matchSeconds`: mốc **×5 ở 10 giây cuối** tính theo `matchSeconds - t`, thiếu tham
     * số này thì cả tốc độ thật lẫn nhãn đều không bao giờ lên ×5.
     */
    const mul = arenaTimeSpeedMul(s.seconds, s.matchSeconds);
    if (mul > this.lastSpeedMul && mul > 1) {
      fx.announce(`×${mul}`, 'speed', 1600);
      this.speedFxHoldUntil = performance.now() + 1600; // xem chú thích field: đếm lùi không được ghi đè
    }
    this.lastSpeedMul = mul;

    /*
     * (2) 10 GIÂY CUỐI — nháy NHẸ + số đếm lùi từng giây. `Math.ceil` để "còn 9,4s" hiện `10` (đúng cảm
     * giác đếm ngược của người chơi); chỉ chạy khi trận ĐANG đánh (hết giờ/ phá Nhà thì bảng kết quả
     * tiếp quản, không chồng số lên).
     * ⚠️ NHỊN một nhịp khi vừa thông báo mốc tốc độ (`speedFxHoldUntil`): ở ×5 mỗi giây trận chỉ ~0,2 giây
     * thực nên đếm lùi sẽ ghi đè chữ "×5" ngay lập tức. Số đếm lùi vẫn chạy đủ sau đó.
     */
    const remain = s.matchSeconds - s.seconds;
    const sec = Math.ceil(remain);
    if (s.status === 'fighting' && remain > 0 && remain <= 10 && performance.now() >= this.speedFxHoldUntil) {
      if (sec !== this.lastCountdownSec) {
        this.lastCountdownSec = sec;
        fx.announce(String(sec), 'alert', 900);
      }
    } else if (remain > 10) {
      this.lastCountdownSec = null;
    }
  }

  render(snapshot: ArenaSnapshot | null): void {
    this.last = snapshot;
    /*
     * ⚠️ `has-result` — CỜ ĐỂ CSS ẨN HẾT HUD CÒN LẠI KHI BĂNG KẾT QUẢ ĐANG HIỆN.
     *
     * Người dùng: *"Màn hình báo chiến thắng/thua arena để full màn hình và front các hiển thị khác"*.
     * Cờ sống trong HUD (KHÔNG chỉ trong DOM) vì `render()` ghi lại `innerHTML` mỗi nhịp: nếu chỉ gắn
     * class cho băng thì nhịp vẽ kế tiếp có thể dựng lại DOM mà quên nó. Đặt TRƯỚC nhánh `if (this.drag)`
     * vì trạng thái này không được phụ thuộc thao tác kéo.
     */
    this.root.classList.toggle('has-result', !!this.banner);
    /*
     * `is-max-gold` — VÍ ĐẦY (chạm trần 500) ⇒ CSS vẽ VIỀN HUD NHẤP NHÁY.
     *
     * Người dùng 03/10/2026: *"Thông báo max vàng thay bằng viền HUD nhấp nháy"*. Giống `has-result`, cờ
     * này phải sống ở JS vì `render()` dựng lại `innerHTML` mỗi nhịp. Điều kiện lấy THẲNG từ ảnh chụp do
     * `Game` đọc sim (`snapshot.resource`) — HUD không tự đọc `ArenaWorld`.
     */
    this.root.classList.toggle('is-max-gold', !!snapshot && snapshot.resource >= ARENA_ECON.maxResource);
    /* Lớp thông báo cập nhật TRƯỚC nhánh `drag` — xem `paintStageFx`. */
    this.paintStageFx(snapshot);
    /*
     * ⚠️⚠️ CHẶN VẼ LẠI KHI ĐANG KÉO — YÊU CẦU #6 CỦA TASK, VÀ LÀ ĐIỀU KIỆN SỐNG CÒN CỦA KÉO THẢ.
     *
     * `render()` ghi lại `innerHTML` ~10 lần/giây (xem `Game.onArenaFrame`). Node icon đang được
     * kéo là CON của `root` ⇒ mỗi nhịp vẽ lại là node đó bị HUỶ: `setPointerCapture` mất hiệu lực,
     * `drag.node` trỏ vào một phần tử đã rời DOM, và nút BIẾN MẤT ngay giữa thao tác.
     *
     * ⚠️ Cờ này KHÔNG được phép làm HUD "đóng băng" mãi: nó chỉ sống trong thao tác kéo (vài trăm
     * ms), và `onPointerUp` gọi `render(this.last)` một lần ngay sau khi thả ⇒ số liệu bắt kịp.
     * Nếu `pointerup` không bao giờ tới (app bị ẩn/ phần cứng mất chạm), `pointercancel` cũng đi
     * vào `onPointerUp`, nên không có đường kẹt vĩnh viễn.
     */
    if (this.drag) return;
    if (!snapshot) {
      this.root.innerHTML = '';
      /* ⚠️ Không còn DOM ⇒ không còn `.fuse-spark` để neo canvas: dừng vòng vẽ luôn (xem `attachFuseFx`). */
      this.stopFuseFx();
      return;
    }
    const host = this.host;
    const heroClasses = snapshot.heroClasses;
    /*
     * ⚠️ TÊN CLASS LẤY QUA `className()` (i18n theo id) — KHÔNG dùng `CLASSES[id].name` vì đó là
     * chuỗi TIẾNG VIỆT gốc; người chơi EN sẽ đọc thấy tiếng Việt giữa giao diện tiếng Anh.
     */
    const heroes = host ? host.heroOptions(heroClasses) : [];

    /*
     * AVATAR MUA TƯỚNG — MỘT avatar mỗi class đã mở khoá, cộng avatar NÔNG DÂN.
     *
     * ⚠️⚠️ VÌ SAO BỎ HẲN PANEL `.arena-heroes` + NÚT `.arena-hero` (và cả nút `.arena-farmer` trong
     * dock): người dùng chốt *"tap vào avatar mua tướng … hoặc kéo avatar đến vị trí tôi muốn"*, tức
     * avatar LÀ đường mua. Giữ thêm nút mua trong dock là HAI đường vào cùng một lệnh, và đường thứ
     * hai sẽ lệch (nút mua tại Nhà, avatar kéo được) mà không ai nhớ phải sửa cả hai.
     *
     * ⚠️ Thứ tự avatar = thứ tự `heroOptions()` (scene lấy từ danh sách class đã mở khoá) ⇒ người
     * chơi thấy đúng thứ tự ổn định giữa các nhịp vẽ, không nhảy chỗ.
     */
    const iconKeys: IconKey[] = [...heroes.map((h) => `hero:${h.classId}`), 'farmer'];
    const iconHtml = this.iconsHtml(
      iconKeys,
      heroes,
      snapshot.farmerCost,
      snapshot.resource,
      snapshot.farmerCount,
      snapshot.farmerMax,
    );

    /*
     * ⚠️⚠️ HAI KHỐI NÀY LÀ CON CỦA `#arenaHud`, VÀ `#arenaHud` LÀ LỚP PHỦ `position:fixed; inset:0`.
     *
     * LỊCH SỬ BA ĐỜI (đọc để không quay lại đời 2):
     *   · ĐỜI 1 — HUD là MỘT lớp phủ `fixed; inset:0`, `#ingame` "chừa chỗ" bằng hai biến CSS
     *     HARD-CODE đo ở 1440×900 (`--arena-top-h: 44px; --arena-dock-h: 428px`). Ở màn ngang
     *     điện thoại (844×390) chiều cao khung nhìn NHỎ HƠN dock ⇒ dock tràn ra ngoài (đo được đáy
     *     504 > 390) và sân chỉ còn một dải mỏng.
     *   · ĐỜI 2 — hai khối được `mount()` chuyển vào `#ingame` làm ANH EM của `#stage` để `#stage`
     *     tự co (`flex:1`). Hết hard-code, nhưng sân KHÔNG full screen: bar + dock ăn chỗ thật.
     *   · ĐỜI 3 (NAY) — người dùng chốt **SÂN ĐẤU FULL SCREEN**: `#stage` phủ kín `#ingame`, còn
     *     `#arenaHud` quay lại là LỚP PHỦ `fixed; inset:0; pointer-events:none` (z-index 30), với
     *     `.arena-hud-bar` neo `top:0` và `.arena-dock` neo `bottom:0` (CSS của Lead). Vì vậy
     *     `mount()` ĐÃ BỊ XOÁ — di chuyển hai khối ra khỏi `#arenaHud` là phá đúng thiết kế này.
     *
     * ⚠️⚠️ VÌ SAO VỎ PHẢI `pointer-events:none` VÀ TỪNG NÚT PHẢI BẬT LẠI `auto`: vỏ phủ kín màn hình,
     * nếu nó nhận sự kiện thì MỌI CÚ BẤM CHỌN LAND TRÊN SÂN BỊ NUỐT (sân nằm dưới lớp phủ). CSS lo
     * phần đó (`.arena-hud` = none, `.arena-hud button`/ `.chip`/ `.core` = auto), và icon kéo thả
     * của ta TỰ khai `pointer-events:auto` bằng inline style trong `iconsHtml()` — nên nó sống sót
     * kể cả khi CSS đổi.
     *
     * ⚠️⚠️ CLASS ĐÃ ĐỔI THEO LUẬT MỚI — ĐỪNG "KHÔI PHỤC CHO ĐỦ BỘ":
     *   · XOÁ: `.arena-lands` · `.arena-land` · `.arena-heroes` · `.arena-hero` · `.arena-farmer`
     *     (3 nút land + nút mua tướng/nông dân trong dock — không còn DOM nào tạo ra chúng);
     *   · XOÁ 02/10/2026: `.arena-dock` + bảng "Kinh tế & nhịp trận" + 2 chip `.arena-score`
     *     (số máu) + `.arena-cadence`/ `.arena-hint` (xem chú thích ngay dưới khối `innerHTML`);
     *   · XOÁ TIẾP 02/10/2026: chip `.arena-res` (tài nguyên nay nằm TRÊN ĐẦU NHÀ MÌNH — xem
     *     `updateCoreLabels`) và `.arena-hud-actions` (2 nút nay ở hai ĐẦU thanh: back TRÁI, setting PHẢI);
     *   · GIỮ: #arenaToast · #btnArenaExit · #btnArenaSettings · `.arena-drag-icon` (avatar) ·
     *     `.arena-icon-layer` · `.arena-band-seam` (vạch chia 2 tầng + đồng hồ, do HUD sinh).
     * ⚠️ Các bài đo cũ (`tools/verify-arena-layout.mjs`, `tools/verify-arena-task5.mjs`) còn query
     * selector đã xoá ⇒ phải cập nhật sang `.arena-drag-icon[data-icon]` (đã báo Lead).
     */
    /*
     * ⚠️⚠️ THỨ TỰ DOM = THỨ TỰ TRÊN MÀN HÌNH — NÚT BACK ĐỨNG ĐẦU, NÚT SETTING ĐỨNG CUỐI.
     * Người dùng: *"nút setting để ở bên phải topbar, nút back để bên trái topbar"*. `.arena-hud-right`
     * có `margin-left: auto` nên đẩy nút setting ra mép phải, còn back là phần tử đầu tiên ⇒ mép trái.
     */
    const portrait = this.host?.arenaPortraitField?.() ?? null;
    /*
     * NHÃN TUA THỜI GIAN — yêu cầu người dùng: *"đến phút thứ 1, tốc độ game x2 … phút thứ 3 tăng lên
     * thành x3"* · *"trong 10s cuối, speed nâng lên thành x5"*. HUD chỉ ĐỌC lại hệ số từ
     * `ARENA_SPEED_RAMP` (một nguồn sự thật với vòng lặp tick của scene) và hiện "×2"/"×3"/"×5"; ở ×1
     * thì không hiện gì để thanh HUD không rối.
     */
    const speedMul = arenaTimeSpeedMul(snapshot.seconds, snapshot.matchSeconds);
    const speedTag = speedMul > 1 ? ` · ×${speedMul}` : '';
    this.root.innerHTML = `
      <div class="arena-hud-bar">
        <button id="btnArenaExit" class="arena-exit" data-i18n-title="${t('Quay lại', 'Back')}" aria-label="${t('Quay lại', 'Back')}">◀</button>
        <div class="arena-hud-left">
          <span class="arena-chip arena-clock">
            <b>${formatMatchClock(snapshot.seconds)}</b>
            <small>${t(ARENA_TEXT.matchTime, ARENA_TEXT_EN.matchTime)} · ${formatMatchClock(snapshot.matchSeconds)}${speedTag}</small>
          </span>
        </div>
        <!--
          ⚠️ CHIP TRẠNG THÁI LÀ CON TRỰC TIẾP CỦA THANH (không nằm trong .arena-hud-left) và được CSS
          CĂN GIỮA TUYỆT ĐỐI — người dùng: *"Trạng thái trận Đang đánh căn giữa topbar"*. Nằm trong nhóm
          trái thì nó chỉ đứng cạnh nút back, không bao giờ ra giữa màn hình được.
        -->
        <span class="arena-chip arena-status st-${this.safeStatusClass(snapshot.status)}">
          ${statusLabel(snapshot.status)}
        </span>
        <div class="arena-hud-right">
          <!--
            ⚠️⚠️ CHIP TÀI NGUYÊN (.arena-res) VÀ 2 CHIP MÁU (.arena-score) ĐÃ BỊ BỎ KHỎI THANH TRÊN
            (02/10/2026) — ĐỌC TRƯỚC KHI "KHÔI PHỤC CHO ĐỦ BỘ".

            Người dùng: *"số điểm máu và tài nguyên địch để trên đầu nhà chính. Bỏ số máu trên topbar"*
            rồi *"Tài nguyên địch đã có trên header nhà địch nhưng tài nguyên quân ta chưa có chuyển lên
            header nhà quân mình"*. Hai chip máu KHÔNG phải "điểm trận" như tên lớp gợi ý: trường
            score().left của sim = MÁU NHÀ ĐỊCH, score().right = MÁU NHÀ MÌNH, nên chúng chính là
            "số máu" mà người dùng muốn dời đi. Nay MỖI NHÀ hiện máu + ví của chính nó do TẦNG VẼ vẽ
            ngay TRÊN ĐẦU NHÀ (updateCoreLabels trong src/render/arena-battle.ts).

            ⚠️ KHÔNG DÙNG DẤU BACKTICK TRONG KHỐI NÀY: cả khối đang nằm TRONG một template literal,
            nên backtick sẽ KẾT THÚC CHUỖI SỚM và gây lỗi cú pháp ở tận dòng sau — đúng lỗi đã sập.
          -->
          <button id="btnArenaSettings" class="arena-exit arena-settings" data-i18n-title="${t('Cài đặt', 'Settings')}" data-i18n-aria="${t('Cài đặt', 'Settings')}" aria-label="${t('Cài đặt', 'Settings')}">⚙️</button>
        </div>
      </div>

      ${portrait ? this.bandSeamHtml(snapshot) : ''}

      <!--
        ⚠️⚠️ DẢI CHIÊU (.arena-skill-strip) ĐÃ BỊ XOÁ HẲN (03/10/2026) — ĐỪNG KHÔI PHỤC.

        Người dùng: *"Bỏ thẻ thông báo skill đang dùng, skill available"* ⇒ không còn thẻ "chiêu kế tiếp +
        đếm ngược + tên chiêu vừa niệm" nào trên màn hình. Kéo theo: trường ArenaSnapshot.heroSkills (và
        đoạn dựng nó ở game.ts) đã xoá, CSS .arena-skill-strip/ .arena-skill đã xoá, tool
        verify-arena-skill-strip.mjs đã xoá. Chiêu vẫn tự niệm như cũ — chỉ bỏ phần HIỂN THỊ.

        ⚠️ KHÔNG DÙNG DẤU BACKTICK TRONG KHỐI NÀY (đang nằm trong template literal của render).
      -->

      <!--
        ⚠️⚠️ BẢNG "KINH TẾ & NHỊP TRẬN" ĐÃ BỊ XOÁ HẲN (02/10/2026) — ĐỪNG KHÔI PHỤC.

        Người dùng: *"bỏ bảng Kinh tế nhịp theo trận"*. Bảng đó (.arena-dock > .quick-recruit-row) chứa
        tiêu đề panel + "Tài nguyên địch" + dòng nhịp thả quân + dòng gợi ý kéo avatar. Hai thứ TRONG SỐ
        ĐÓ đã có chỗ đúng hơn: tài nguyên địch nay nằm TRÊN ĐẦU NHÀ ĐỊCH (updateCoreLabels), còn trạng
        thái trận đã có chip .arena-status trên thanh trên.

        ⚠️ HỆ QUẢ CÓ CHỦ Ý — BỐ CỤC KHỔ DỌC ĐỔI THEO: bỏ bảng là insetBottom của 2 tầng giảm còn 0 ⇒
        panelH tăng ⇒ sân to hơn hẳn. Nhưng khi sân to ra thì DẢI TRỐNG hai bên hẹp lại và cột avatar
        44px không còn vừa — scene vì thế chừa trước một gutter bên trái cho cột avatar
        (makeArenaPortraitBands nhận gutter, xem ArenaHudHost.arenaPortraitField). Bỏ bảng mà KHÔNG làm
        việc đó thì avatar hoặc đè lên sân, hoặc nhảy về hàng ngang.

        ⚠️ .arena-toast/ .arena-banner KHÔNG nằm trong bảng này (lý do đã ghi ở dưới) nên không mất.
        ⚠️ KHÔNG DÙNG DẤU BACKTICK TRONG KHỐI NÀY.
      -->

      <!--
        ⚠️⚠️ TOAST VÀ BĂNG KẾT QUẢ LÀ CON TRỰC TIẾP CỦA #arenaHud — ĐỪNG ĐƯA VÀO MỘT KHỐI absolute
        CÓ overflow (BUG ĐÃ ĐO ĐƯỢC 02/10/2026, ảnh art/verify-arena-hud/3-toast-*.png chỉ thấy nền sân).

        Lúc đó toast nằm trong .arena-dock (position:absolute + overflow-y:auto) ⇒ khối đó vừa là KHỐI
        CHỨA của con absolute, vừa CẮT phần tràn ra ngoài hộp của nó. CSS đặt toast ở
        bottom: calc(42vh + 12px) — tức CAO HƠN hẳn dock — nên toast bị overflow cắt mất: DOM vẫn báo
        display:block, textContent đầy đủ, getBoundingClientRect có số… nhưng NGƯỜI CHƠI KHÔNG THẤY GÌ.
        Đúng loại lỗi "chỉ Chrome mới bắt được": mọi bài kiểm DOM đều xanh.

        ⚠️ Bảng dock đã bị xoá (xem trên) nhưng luật này GIỮ NGUYÊN GIÁ TRỊ: toast/ băng kết quả phải là
        con của vỏ HUD (fixed; inset:0) — nếu sau này có ai thêm lại một khối cuộn nào ở dưới thì đừng
        nhét hai phần tử này vào đó.

        ⚠️ KHÔNG DÙNG DẤU BACKTICK TRONG COMMENT NÀY (đang nằm trong template literal).
      -->
      <p class="arena-toast hidden" id="arenaToast"></p>
      ${this.bannerHtml()}

      <!--
        ⚠️ BĂNG "VÀNG ĐÃ ĐẦY" GIỮA SÂN ĐÃ BỊ XOÁ (03/10/2026) — ĐỪNG KHÔI PHỤC.

        Người dùng: *"Thông báo max vàng thay bằng viền HUD nhấp nháy"* ⇒ tín hiệu đầy ví nay là VIỀN NHẤP
        NHÁY của cả lớp HUD (class is-max-gold trên #arenaHud — xem render() và CSS .arena-hud.is-max-gold).
        Nhãn tài nguyên trên ĐẦU NHÀ vẫn đổi đỏ + "⚠️ MAX VÀNG!" (tầng vẽ) — đó là số liệu tại chỗ, không
        phải "thông báo".

        ⚠️ KHÔNG DÙNG DẤU BACKTICK TRONG KHỐI NÀY (đang nằm trong template literal của render).
      -->

      ${iconHtml}
    `;

    /*
     * ĐẶT LẠI TOẠ ĐỘ CHO ICON VỪA DỰNG.
     *
     * ⚠️ `innerHTML` vừa thay TOÀN BỘ node con ⇒ mọi vị trí `left/top` ghi ở nhịp trước đã mất theo
     * node cũ. `layoutIcons()` đọc lại kho vị trí và ghi toạ độ cho node MỚI — đây là lý do vị trí
     * icon phải nằm trong `localStorage` chứ không chỉ trong DOM.
     *
     * ⚠️⚠️ VÀ PHẢI ĐẶT LẠI LẦN NỮA SAU KHI LAYOUT ỔN ĐỊNH — BUG ĐÃ ĐO ĐƯỢC TRONG CHROME.
     *
     * Ngay sau `innerHTML`, bar/dock CHƯA có kích thước cuối (ảnh/ chữ/ font chưa đo xong, và ở nhịp
     * đầu chúng có thể còn 0×0). Khi đó `blockedRects()` trả về danh sách RỖNG ⇒ icon không né được
     * bar/dock và rơi trúng nút: đo được icon SÂN nằm ở `(1410,522)` mà `elementFromPoint` trả `null`
     * (bị lớp khác che) và cú bấm KHÔNG triệu hồi được tướng.
     *
     * Vì vậy đặt lại một lần nữa ở khung hình kế tiếp (`requestAnimationFrame`) — lúc đó số đo đã
     * đúng. Rẻ (một lượt ghi `style` cho ≤5 node) và KHÔNG dựng lại DOM nên không mất thao tác kéo.
     */
    this.layoutIcons();
    this.scheduleIconRelayout();
    /*
     * ⚠️ ĐỊNH VỊ LẠI VẠCH CHIA 2 TẦNG sau `innerHTML` — node vừa bị thay nên `style` cũ (và cả vạch
     * "thời gian còn lại") mất theo. Xem `paintBandSeam`.
     */
    this.paintBandSeam(snapshot, portrait);
    /*
     * ⚠️ VẼ LẠI TOAST SAU KHI DỰNG LẠI DOM — xem chú thích ở `flash()`. Không có dòng này thì mọi
     * cú bấm mua đều "im lặng": `flash()` ghi vào phần tử CŨ vừa bị `innerHTML` thay thế.
     */
    this.paintToast();
    /*
     * ⚠️ GẮN LẠI CANVAS NGÒI SAU CÙNG — `innerHTML` vừa huỷ `.fuse-spark` cũ (và canvas nằm trong đó).
     * Phải chạy SAU `paintBandSeam` để `--seam-remain` đã có trước khung vẽ đầu; canvas neo bằng CSS
     * (`left/top: 50%` của `.fuse-spark`) nên KHÔNG cần cập nhật toạ độ mỗi khung.
     */
    this.attachFuseFx();
  }

  /**
   * VẠCH CHIA 2 TẦNG CỦA KHỔ DỌC: **đồng hồ trận ở giữa** + **vạch vàng = THỜI GIAN CÒN LẠI**.
   *
   * Yêu cầu người dùng 02/10/2026 (nguyên văn): *"Đối với màn hình dọc, thời gian trận đang diễn ra
   * được bao nhiêu phút để ở giữa screen (ở giữa chữ Sân địch và Sân mình và nằm front vạch vàng, thời
   * gian còn lại chính là vạch màu vàng chia đôi màn hình trên và dưới, nó sẽ cháy dần từ bên phải sang
   * trái"*.
   *
   * Cách dựng: ba phần tử DOM trong `.arena-band-seam` (nhãn SÂN ĐỊCH ở trên, nhãn SÂN MÌNH ở dưới,
   * chip đồng hồ Ở GIỮA — tức nằm TRÊN đường vàng), cộng vạch `<i>` có bề rộng = `--seam-remain`.
   *
   * ⚠️⚠️ Ở ĐÂY (HUD) CHỨ KHÔNG PHẢI SCENE — ĐỔI CHỖ CÓ CHỦ Ý trong lượt này:
   *   · SỐ ĐỒNG HỒ là dữ liệu của ảnh chụp (`snapshot.seconds`/`matchSeconds`), mà scene KHÔNG cầm ảnh
   *     chụp — trước đây scene giữ vạch chia nên nó chỉ vẽ được 2 nhãn chữ, không vẽ được đồng hồ;
   *   · HUD vốn đã vẽ lại ~10 lần/giây ⇒ vạch "cháy dần" mượt ở 10Hz mà KHÔNG cần thêm một vòng cập nhật
   *     DOM nào từ scene (scene ghi DOM mỗi khung hình là việc đắt và sai tầng).
   *   · Scene vẫn là bên quyết định HÌNH HỌC (khổ dọc/ khổ ngang) qua `arenaPortraitField()`; HUD chỉ
   *     THI HÀNH toạ độ đó ⇒ không có hai nguồn sự thật.
   *
   * ⚠️ Vẫn là DOM (không phải GameObject của Phaser) vì lý do cũ: nhãn vẽ bằng Phaser sẽ hiện HAI LẦN
   * (mỗi camera vẽ một bản lệch theo viewport — số đo ghi trong git history của scene). DOM nằm trên
   * canvas nên vẽ đúng MỘT lần, và `.arena-hud` là `pointer-events: none` nên không nuốt cú chạm.
   */
  private bandSeamHtml(s: ArenaSnapshot): string {
    return `
      <div class="arena-band-seam">
        <i class="arena-band-burn" aria-hidden="true"><u class="fuse-spark"></u></i>
        <span class="abs-top" aria-hidden="true">${t('SÂN ĐỊCH', 'ENEMY SIDE')}</span>
        <span class="abs-bottom" aria-hidden="true">${t('SÂN MÌNH', 'YOUR SIDE')}</span>
        <span class="arena-seam-clock" data-i18n-title="${t('Thời gian trận', 'Match time')}"><b>${formatMatchClock(
          s.seconds,
        )}</b><small>/ ${formatMatchClock(s.matchSeconds)}</small></span>
      </div>`;
  }

  /**
   * GHI TOẠ ĐỘ + PHẦN TRĂM THỜI GIAN CÒN LẠI CHO VẠCH CHIA — gọi sau MỖI `render()`.
   *
   * ⚠️ `--seam-remain` = `1 − giây/độDàiTrận`, kẹp `[0,1]`: vạch vàng DÀI BẰNG PHẦN THỜI GIAN CÒN LẠI,
   * neo mép TRÁI ⇒ nó ngắn dần từ PHẢI sang TRÁI (đúng câu *"cháy dần từ bên phải sang trái"*).
   *
   * ⚠️ Số rác (`matchSeconds` 0/NaN) ⇒ coi như HẾT GIỜ (vạch biến mất) chứ KHÔNG chia cho 0 ra `NaN`
   * (NaN trong `calc()` làm cả vạch mất tích kèm mọi thứ khác dùng biến đó).
   */
  private paintBandSeam(s: ArenaSnapshot, p: { left: number; right: number; splitY: number } | null): void {
    const el = this.root.querySelector<HTMLElement>('.arena-band-seam');
    if (!el || !p) return;
    const dur = Number.isFinite(s.matchSeconds) ? s.matchSeconds : 0;
    const t0 = Number.isFinite(s.seconds) ? s.seconds : 0;
    const remain = dur > 0 ? Math.max(0, Math.min(1, 1 - t0 / dur)) : 0;
    el.style.setProperty('--seam-y', `${p.splitY.toFixed(1)}px`);
    el.style.setProperty('--seam-x', `${((p.left + p.right) / 2).toFixed(1)}px`);
    el.style.setProperty('--seam-remain', remain.toFixed(4));
  }

  /**
   * GẮN CANVAS NGÒI VÀO `.fuse-spark` + BẢO ĐẢM VÒNG `rAF` ĐANG CHẠY — gọi ở CUỐI mỗi `render()`.
   *
   * ⚠️⚠️ VÌ SAO PHẢI GẮN LẠI MỖI NHỊP: `render()` ghi `innerHTML` ~10 lần/giây ⇒ `.fuse-spark` là node
   * MỚI, canvas cũ đã bị huỷ khỏi DOM. Canvas (đối tượng JS) thì GIỮ NGUYÊN trong `this.fuseCanvas`,
   * chỉ `appendChild` sang node mới — nhờ vậy hạt đang cháy KHÔNG bị reset theo nhịp DOM.
   *
   * ⚠️ KHÔNG sinh hạt khi thiếu ngòi/ HUD `hidden`: khổ NGANG không có `.arena-band-seam` (xem
   * `bandSeamHtml`) nên `querySelector` trả `null` — dừng vòng lặp thay vì vẽ vào hư không.
   *
   * ⚠️⚠️ `prefers-reduced-motion: reduce` ⇒ **0 HẠT**, chỉ MỘT khung lõi tĩnh rồi dừng `rAF`
   * (yêu cầu #6). Kiểm ở ĐÂY chứ không chỉ lúc tạo canvas: người dùng có thể bật/tắt trong lúc chơi, mà
   * `render()` chạy 10 lần/giây nên trạng thái mới được nhận trong ≤100ms.
   *
   * ⚠️ BACKING STORE = `132 × dpr` (dpr kẹp ≤ 3) rồi `setTransform(dpr,…)`: vẽ bằng toạ độ CSS nhưng
   * bitmap đủ nét trên màn retina. Kẹp ≤ 3 vì DPR 4 (một số máy Android) nhân đôi diện tích tô mỗi
   * khung mà mắt không phân biệt được ở khối 132px.
   */
  private attachFuseFx(): void {
    const spark = this.root.querySelector<HTMLElement>('.fuse-spark');
    if (!spark || this.root.classList.contains('hidden')) {
      this.stopFuseFx();
      return;
    }
    let canvas = this.fuseCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.className = 'fuse-fx';
      /* Trang trí thuần tuý: trình đọc màn hình không có gì để đọc ở đây. */
      canvas.setAttribute('aria-hidden', 'true');
      const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
      canvas.width = Math.round(FUSE_FX_SIZE * dpr);
      canvas.height = Math.round(FUSE_FX_SIZE * dpr);
      canvas.dataset.fuse = 'on';
      canvas.dataset.sparks = '0';
      this.fuseCanvas = canvas;
      this.fuseCtx = canvas.getContext('2d');
      this.fuseCoreGrad = null;
      /* ⚠️ `width =` VỪA RESET transform ⇒ phải `setTransform` SAU khi đặt kích thước. */
      this.fuseCtx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    if (canvas.parentElement !== spark) spark.appendChild(canvas);
    const ctx = this.fuseCtx;
    if (!ctx) return;
    if (this.fusePrefersReduced()) {
      /* 0 hạt. Dừng TRƯỚC khi vẽ để chắc chắn không còn khung nào ghi `dataset.sparks` khác 0. */
      this.stopFuseFx();
      canvas.dataset.fuse = 'reduced';
      canvas.dataset.sparks = '0';
      ctx.clearRect(0, 0, FUSE_FX_SIZE, FUSE_FX_SIZE);
      this.drawFuseCore(ctx, 1);
      return;
    }
    canvas.dataset.fuse = 'on';
    if (this.fuseRaf === null) {
      this.fuseLast = 0;
      this.fuseBurstIn = 0.25 + Math.random() * 0.6;
      this.fuseRaf = requestAnimationFrame(this.tickFuseFx);
    }
  }

  /**
   * DỪNG VÒNG VẼ + XOÁ HẠT — gọi ở `hide()`/ `dispose()`/ nhánh `render(null)`/ khổ ngang.
   *
   * ⚠️ Luôn `cancelAnimationFrame` trước khi trả: `tickFuseFx` tự hẹn khung kế tiếp, nên chỉ "không
   * vẽ nữa" là vô nghĩa — vòng lặp vẫn sống và vẫn chiếm khung hình cho tới khi tab bị đóng.
   */
  private stopFuseFx(): void {
    if (this.fuseRaf !== null) {
      cancelAnimationFrame(this.fuseRaf);
      this.fuseRaf = null;
    }
    this.fuseSparks.length = 0;
    this.fuseSpawnAcc = 0;
    this.fuseLast = 0;
  }

  /** `matchMedia` cho reduced-motion — tạo LƯỜI một lần, `.matches` luôn phản ánh trạng thái hiện tại. */
  private fusePrefersReduced(): boolean {
    if (!this.fuseMotionQuery && typeof window.matchMedia === 'function') {
      this.fuseMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    }
    return this.fuseMotionQuery?.matches ?? false;
  }

  /**
   * MỘT KHUNG CỦA NGÒI — cập nhật hạt rồi vẽ.
   *
   * ⚠️⚠️ `dt` KẸP ≤ 50ms: tab bị ẩn/ máy khựng rồi quay lại cho `dt` vài giây ⇒ `vy += g*dt` biến hạt
   * thành vệt dài vọt ra ngoài khung, và bộ đếm sinh hạt nhả cả trăm hạt trong một khung (trần 90 vẫn
   * cứu được, nhưng hình sẽ giật). Kẹp là đủ và rẻ.
   *
   * ⚠️⚠️ `dataset.sparks`/ `dataset.fuse` GHI MỖI KHUNG: đây là HỢP ĐỒNG ĐO ĐƯỢC (yêu cầu #8) để
   * `tools/verify-arena-fuse.mjs` đọc trạng thái thật từ Chrome. KHÔNG `console.log` mỗi khung.
   */
  private tickFuseFx = (now: number): void => {
    this.fuseRaf = null;
    const canvas = this.fuseCanvas;
    const ctx = this.fuseCtx;
    const spark = this.root.querySelector<HTMLElement>('.fuse-spark');
    if (!canvas || !ctx || !spark || this.root.classList.contains('hidden')) {
      this.stopFuseFx();
      return;
    }
    /*
     * ⚠️ `render()` vừa thay `innerHTML` (canvas rời DOM) mà khung này chạy trước `attachFuseFx` ⇒ gắn
     * lại TẠI CHỖ, GIỮ hạt. Không gắn lại thì vòng lặp tự giết mình mỗi nhịp DOM (ngòi nhấp nháy/ tắt).
     */
    if (!canvas.isConnected) spark.appendChild(canvas);
    if (this.fusePrefersReduced()) {
      this.stopFuseFx();
      canvas.dataset.fuse = 'reduced';
      canvas.dataset.sparks = '0';
      ctx.clearRect(0, 0, FUSE_FX_SIZE, FUSE_FX_SIZE);
      this.drawFuseCore(ctx, 1);
      return;
    }
    const dt = this.fuseLast === 0 ? 1 / 60 : Math.min(0.05, (now - this.fuseLast) / 1000);
    this.fuseLast = now;
    this.stepFuseSparks(dt);
    ctx.clearRect(0, 0, FUSE_FX_SIZE, FUSE_FX_SIZE);
    this.drawFuseSparks(ctx);
    /* Lõi vẽ SAU hạt: nó là "nguồn", luôn nằm trên cùng và luôn hiện kể cả khi hạt thưa. */
    this.drawFuseCore(ctx, this.fuseFlickerStep());
    canvas.dataset.fuse = 'on';
    canvas.dataset.sparks = String(this.fuseSparks.length);
    this.fuseRaf = requestAnimationFrame(this.tickFuseFx);
  };

  /**
   * VẬT LÝ + SINH HẠT CHO MỘT KHUNG.
   *
   * ⚠️ PHÁT XẠ KHÔNG ĐỀU — đây là điều người dùng muốn ("cháy chậm thật"): một dòng nền
   * (`FUSE_BASE_RATE`) cộng từng đợt tóe nhỏ cách nhau 0,35–1,2s NGẪU NHIÊN, thỉnh thoảng kèm một TIA
   * "nổ lách tách" bay xa. Nếu chỉ có dòng nền đều thì ngòi trông như vòi phun; nếu chỉ có đợt thì
   * ngòi tắt ngấm giữa các đợt.
   *
   * ⚠️ Mọi phép biến đổi đi TRÊN TỪNG HẠT (`vy += g*dt`, `v *= drag`, `age += dt`) — không có phép
   * "scale cả vòng" nào, nên hạt già/ chết lệch nhau tự nhiên.
   */
  private stepFuseSparks(dt: number): void {
    const list = this.fuseSparks;
    this.fuseSpawnAcc += FUSE_BASE_RATE * dt;
    while (this.fuseSpawnAcc >= 1) {
      this.fuseSpawnAcc -= 1;
      this.spawnFuseSpark(false);
    }
    this.fuseBurstIn -= dt;
    if (this.fuseBurstIn <= 0) {
      const n = 5 + Math.floor(Math.random() * 9);
      for (let i = 0; i < n; i++) this.spawnFuseSpark(false);
      /* TIA lách tách: HIẾM (22% số đợt) nhưng SÁNG và DÀI gấp nhiều lần hạt thường ⇒ đọc ra "loé tia". */
      if (Math.random() < 0.22) this.spawnFuseSpark(true);
      this.fuseBurstIn = 0.35 + Math.random() * 0.85;
    }
    const drag = Math.max(0, 1 - FUSE_DRAG * dt);
    /* ⚠️ DUYỆT NGƯỢC để `splice` tại chỗ — không tạo mảng mới mỗi khung (yêu cầu #7). */
    for (let i = list.length - 1; i >= 0; i--) {
      const p = list[i];
      p.vy += FUSE_GRAVITY * dt;
      p.vx *= drag;
      p.vy *= drag;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.age += dt;
      if (p.age >= p.life) list.splice(i, 1);
    }
  }

  /**
   * SINH MỘT HẠT TẠI ĐẦU NGÒI (tâm canvas). `crackle = true` = TIA "nổ lách tách".
   *
   * ⚠️⚠️ HƯỚNG PHÁT XẠ LÀ 360° (`Math.random() * 2π`), KHÔNG phải bán cầu hướng lên.
   * Bản đầu tôi chọn bán cầu hướng lên vì nghĩ "ngòi thuốc nổ phun lên rồi hạt rơi xuống" — nhưng ẢNH
   * THAM CHIẾU của người dùng là **pháo hoa cầm tay**: tia bắn ra MỌI HƯỚNG quanh lõi. Người dùng bác
   * bản bán cầu ("chưa được giống tia như ảnh") nên đây là chỗ phải theo ảnh, không theo suy luận.
   *
   * ⚠️ `trail` là ĐUÔI TIA (giây), nhân với tốc độ ra độ dài vệt (kẹp `FUSE_TRAIL_CAP`). Nhờ vậy tia
   * bay nhanh thì dài, bay chậm thì ngắn — mắt đọc ra "tia", không phải "hạt có đuôi cố định".
   */
  private spawnFuseSpark(crackle: boolean): void {
    const list = this.fuseSparks;
    if (list.length >= FUSE_MAX_SPARKS) return;
    const c = FUSE_FX_SIZE / 2;
    const ang = Math.random() * Math.PI * 2;
    /*
     * ⚠️ TỐC ĐỘ TIA LÁCH TÁCH KHÔNG NHÂN `boost` VÀO TỐC ĐỘ HẠT THƯỜNG: bản đầu nhân hệ số 6 vào cả
     * dải 25–90 ⇒ 150–540 px/s, cộng đuôi 0,28s là vệt dài tới 150px — tràn khỏi canvas 132px và phủ
     * lên chip đồng hồ. Nay hai dải TÁCH HẲN, và độ dài vẫn bị `FUSE_TRAIL_CAP` chặn lần nữa.
     */
    const speed = crackle ? 170 + Math.random() * 120 : 30 + Math.random() * 80;
    list.push({
      x: c + (Math.random() - 0.5) * 2.2,
      y: c + (Math.random() - 0.5) * 2.2,
      vx: Math.cos(ang) * speed,
      vy: Math.sin(ang) * speed,
      age: 0,
      life: crackle ? 0.3 + Math.random() * 0.35 : 0.26 + Math.random() * 0.5,
      /* Tia MẢNH: 0,45–1,2px cho hạt thường, 0,85–1,35px cho tia lách tách. */
      size: crackle ? 0.85 + Math.random() * 0.5 : 0.45 + Math.random() * 0.75,
      trail: crackle ? 0.16 + Math.random() * (FUSE_TRAIL_MAX - 0.16) : FUSE_TRAIL_MIN + Math.random() * 0.12,
      crackle,
    });
  }

  /**
   * VẼ TIA — `globalCompositeOperation = 'lighter'` để các tia CỘNG SÁNG vào nhau (đúng "loé tia").
   *
   * ⚠️⚠️ MỌI HẠT ĐỀU LÀ **TIA** (đoạn thẳng), KHÔNG còn `arc()` cho hạt thường. Bản đầu vẽ hạt thường
   * bằng chấm tròn 1–3,5px ⇒ mắt đọc ra "hạt bụi", mà ảnh tham chiếu là "sợi chỉ". Cách vẽ 3 lớp cho ra
   * tia THON: quầng mờ dày → lõi sáng mảnh → đốm sáng ở ĐẦU tia (hướng bay).
   *
   * ⚠️ Màu suy từ `age/life` (nguội dần: trắng-nóng → vàng → cam → đỏ thẫm) chứ KHÔNG theo chỉ số vòng
   * lặp: hai tia cạnh nhau có tuổi khác nhau nên màu cũng khác — chính cái bản CSS không làm được.
   */
  private drawFuseSparks(ctx: CanvasRenderingContext2D): void {
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    for (const p of this.fuseSparks) {
      const heat = Math.max(0, 1 - p.age / p.life);
      const spd = Math.hypot(p.vx, p.vy) || 1;
      /* ĐỘ DÀI VỆT = tốc độ × đuôi, kẹp trần (xem `spawnFuseSpark`). */
      const len = Math.min(FUSE_TRAIL_CAP, spd * p.trail);
      const tx = p.x - (p.vx / spd) * len;
      const ty = p.y - (p.vy / spd) * len;
      /* 1) QUẦNG: dày + tối ⇒ tia có "thân", không phải sợi chỉ đồng hạng. */
      ctx.strokeStyle = fuseSparkColor(heat * 0.72, 0.42 * heat);
      ctx.lineWidth = Math.max(0.7, p.size * 2.4);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      /* 2) LÕI TIA: mảnh + sáng ⇒ đọc ra "sợi chỉ nóng". */
      ctx.strokeStyle = fuseSparkColor(heat, 0.98 * heat);
      ctx.lineWidth = Math.max(0.5, p.size * 0.95);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      /* 3) ĐẦU TIA: đốm sáng nhỏ ở hướng ĐANG BAY (ảnh tham chiếu có đốm sáng ở đầu mỗi tia). */
      ctx.fillStyle = fuseSparkColor(1, 0.9 * heat);
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(0.4, p.size * (p.crackle ? 1.2 : 0.8)), 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /**
   * VẼ LÕI NGÒI (điểm trắng-nóng + quầng cam) — LUÔN hiện, kể cả reduced-motion/ khi tia thưa.
   *
   * ⚠️⚠️ BÁN KÍNH **10** — ĐÃ QUA NĂM LẦN CHỈNH, CẢ BỐN ĐỀU THEO NGƯỜI DÙNG (ghi lại để lần sau ai
   * chỉnh thì biết vì sao số này "nhảy"):
   *   · bản 1: **13** (đường kính 26px) + lõi CSS 16px ⇒ *"to quá"*;
   *   · bản 2: **7**, vẫn còn lõi CSS ⇒ HAI hình tròn chồng nhau ⇒ *"vẫn bị hình tròn"* ⇒ bỏ lõi CSS;
   *   · bản 3: **4** ⇒ *"Đầu ngòi đếm lùi dần ổn rồi"* nhưng xin *"to thêm một xíu"*;
   *   · bản 4: **6** ⇒ lại xin *"Điểm tâm nóng ở thanh thời gian cho to thêm chút nữa"*;
   *   · bản 5: **8** + điểm trắng 1,9px ⇒ vẫn xin *"Cho to thêm 1 tí nữa"*;
   *   · bản 6 (nay): **10** + điểm trắng 2,4px.
   * ⇒ KẾT LUẬN CẦN NHỚ: chữ *"hình tròn"* của người dùng KHÔNG phải "lõi to" mà là "có HAI vòng tròn
   * chồng nhau" (lõi CSS + lõi canvas). Bỏ lõi CSS xong thì lõi canvas TO LÊN là chuyện khác — người
   * dùng chủ động xin to thêm hai lần. Đừng "thu nhỏ cho an toàn" mà không đọc kỹ phản hồi.
   *
   * ⚠️⚠️ FLICKER LÀ NHIỄU NGẪU NHIÊN (`fuseFlickerStep`), KHÔNG phải `sin`/ keyframe đều nhịp — người
   * dùng bác đúng "nhấp nhô theo nhịp" của bản `@keyframes arenaFuseCore` cũ. Đây là lý do bỏ animation
   * CSS đó (xem khối `FUSE SPARK` trong `style.css`).
   *
   * ⚠️ Gradient tạo MỘT LẦN (`fuseCoreGrad`) rồi đổi độ sáng bằng `globalAlpha` — tạo lại gradient mỗi
   * khung là cấp phát rác ở 60Hz.
   */
  private drawFuseCore(ctx: CanvasRenderingContext2D, flicker: number): void {
    const c = FUSE_FX_SIZE / 2;
    ctx.globalCompositeOperation = 'lighter';
    if (!this.fuseCoreGrad) {
      const g = ctx.createRadialGradient(c, c, 0, c, c, 10);
      g.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
      g.addColorStop(0.4, 'rgba(255, 240, 180, 0.5)');
      g.addColorStop(0.75, 'rgba(255, 176, 52, 0.18)');
      g.addColorStop(1, 'rgba(255, 120, 0, 0)');
      this.fuseCoreGrad = g;
    }
    ctx.globalAlpha = Math.max(0.3, Math.min(1, flicker * 0.8));
    ctx.fillStyle = this.fuseCoreGrad;
    ctx.beginPath();
    ctx.arc(c, c, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    /* Điểm trắng-nóng ở tâm: 2,4–2,8px — "hạt sáng" duy nhất được phép tròn. */
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.beginPath();
    ctx.arc(c, c, 2.4 + flicker * 0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  /** Bước NHIỄU của độ sáng lõi — ngẫu nhiên nhưng có "quán tính" để không nhấp nháy kiểu đèn LED. */
  private fuseFlickerStep(): number {
    this.fuseFlicker = Math.max(0.55, Math.min(1.35, this.fuseFlicker + (Math.random() - 0.5) * 0.55));
    return this.fuseFlicker;
  }

  /**
   * ĐẶT LẠI TOẠ ĐỘ ICON Ở KHUNG HÌNH KẾ TIẾP — để `blockedRects()` đo được bar/dock thật.
   *
   * ⚠️ Huỷ lượt hẹn CŨ trước khi hẹn lượt mới: `render()` chạy ~10 lần/giây, không huỷ thì mỗi nhịp
   * lại xếp thêm một callback ⇒ hàng đợi phình ra và mỗi khung hình ghi `style` hàng chục lần.
   */
  private scheduleIconRelayout(): void {
    if (this.relayoutHandle !== null) cancelAnimationFrame(this.relayoutHandle);
    this.relayoutHandle = requestAnimationFrame(() => {
      this.relayoutHandle = null;
      /*
       * ⚠️ Chỉ chạy khi ĐANG Ở ARENA: `render()` có thể đã chạy xong rồi người chơi rời màn; ghi
       * `style` lúc đó là vô nghĩa (và `#arenaHud` đã `hidden`).
       */
      if (this.root.classList.contains('hidden')) return;
      this.layoutIcons();
    });
  }

  /**
   * BĂNG KẾT QUẢ TRẬN — hiện khi `Game` báo trận kết thúc.
   *
   * ⚠️ Tài nguyên thưởng hiện ở đây là SỐ ĐÃ ĐƯỢC CỘNG vào ví Arena, không phải số dự kiến: HUD
   * không tự tính thưởng (luật thưởng nằm ở `Game`/`sim`, một nguồn).
   */
  setResult(result: ArenaResultBanner | null): void {
    this.banner = result;
    this.render(this.last);
  }

  /**
   * HIỆN MỘT DÒNG PHẢN HỒI NGẮN (kết quả lệnh mua/ lỗi của sim) — tự ẩn sau ~2,2 giây.
   *
   * ⚠️⚠️ TRẠNG THÁI TOAST PHẢI NẰM TRONG HUD, KHÔNG CHỈ TRONG DOM — BUG ĐÃ ĐO ĐƯỢC 02/10/2026.
   *
   * `render()` ghi lại `innerHTML` ~10 lần/giây, mà `#arenaToast` là MỘT PHẦN của khối đó ⇒ cú bấm
   * gọi `flash()` xong là `render()` ở nhịp kế tiếp DỰNG LẠI phần tử toast về trạng thái `hidden` rỗng.
   * SỐ ĐO THẬT: toast chỉ sống dưới ~100ms ⇒ mắt người không kịp đọc, và mọi bài đo Chrome báo
   * "thả sai mà KHÔNG có toast lỗi" — tức yêu cầu *"thả sai thì huỷ, báo lỗi"* coi như không đạt.
   *
   * Vì vậy nội dung toast được giữ ở `this.toast` và `render()` VẼ LẠI nó — đúng khuôn mẫu đã dùng cho
   * vị trí icon (trạng thái của HUD không được sống trong DOM bị dựng lại).
   */
  private flash(res: { ok: boolean; message: string }): void {
    /*
     * ⚠️ `textContent`, KHÔNG `innerHTML`: message của sim có thể chứa ký tự người chơi nhập/ tên
     * class — chèn HTML là mở đường cho việc dựng DOM ngoài ý muốn. Và đây là chỗ DUY NHẤT trong
     * file được phép bỏ qua `t()`: message này do `sim/arena/world.ts` sinh ra bằng tiếng Việt và
     * việc dịch nó thuộc `simMsg()` (xem chú thích `ARENA_TEXT`), không phải HUD.
     */
    this.toast = { text: res.message, bad: !res.ok };
    this.paintToast();
    this.clearToastTimer();
    this.toastTimer = window.setTimeout(() => this.clearToast(), 2200);
  }

  /** Vẽ lại toast HIỆN TẠI lên phần tử đang có trong DOM (gọi từ `render()` và `flash()`). */
  private paintToast(): void {
    const el = this.root.querySelector<HTMLElement>('#arenaToast');
    if (!el) return;
    const t = this.toast;
    el.textContent = t ? t.text : '';
    el.classList.toggle('hidden', !t);
    el.classList.toggle('bad', !!t?.bad);
  }

  /** Dừng đồng hồ tự ẩn (KHÔNG xoá nội dung) — tách khỏi `clearToast` để `flash` không tự xoá mình. */
  private clearToastTimer(): void {
    if (this.toastTimer !== null) {
      window.clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  private clearToast(): void {
    this.clearToastTimer();
    this.toast = null;
    this.paintToast();
  }

  /**
   * BĂNG KẾT QUẢ + NÚT `Thoát` — yêu cầu người dùng 02/10/2026: *"Khi chiến thắng hiển thị nút thoát trên
   * thông báo đó nữa nhé, khi thoát thì ra màn hình chính và clear state"*.
   *
   * ⚠️⚠️ VÌ SAO PHẢI CÓ NÚT Ở ĐÂY: trước lượt này, băng kết quả chỉ có chữ ⇒ người chơi vừa thắng xong
   * phải tự đi tìm nút BACK `◀` trên thanh trên để thoát. Trận đã kết thúc thì đó là bước vô nghĩa.
   * ⚠️ Nút nằm TRONG `#arenaHud` (không phải overlay ngoài) nên đi ĐÚNG đường uỷ quyền `pointerdown`
   * của HUD: `onPointerDown` nhận theo id rồi `pointerup` mới chạy `runButtonCommand` — cùng luật với
   * `#btnArenaExit`, không thêm đường bấm thứ hai. Nhờ vậy nó cũng sống sót qua các nhịp `innerHTML`.
   * ⚠️ Hiện cho CẢ 3 kết cục (`won`/`lost`/`draw`) — không có lý do gì chỉ cho người thắng thoát nhanh;
   * người thua còn cần về trang chủ hơn. ⚠️ `onExit` của `Game` đã tự phân biệt: trận ĐÃ kết thúc thì
   * thoát thẳng, KHÔNG hỏi xác nhận (xem `exitArena`).
   */
  private bannerHtml(): string {
    const b = this.banner;
    if (!b) return '';
    const title =
      b.status === 'won'
        ? t('THẮNG', 'VICTORY')
        : b.status === 'lost'
          ? t('THUA', 'DEFEAT')
          : t('HOÀ', 'DRAW');
    return `
      <div class="arena-banner result-${b.status}">
        <b>${title}</b>
        <span>${t('Thưởng {n} tài nguyên Arena', 'Reward: {n} Arena resource', { n: formatArenaResource(b.reward) })}</span>
        <button type="button" class="arena-banner-exit" id="btnArenaResultExit">${t('Thoát', 'Exit')}</button>
      </div>`;
  }

  /**
   * Hậu tố class CSS CHỈ từ tập trạng thái đã biết.
   *
   * ⚠️ VÌ SAO KHÔNG nội suy thẳng `status` vào `class`: giá trị đó tới từ state sống, và một chuỗi
   * lạ (hay chuỗi do người chơi chèn qua save) sẽ tạo class CSS tuỳ ý. Danh sách trắng ở đây là
   * chặn ở cửa duy nhất mà chuỗi đó đi vào DOM.
   */
  private safeStatusClass(status: string): string {
    return status === 'fighting' || status === 'won' || status === 'lost' || status === 'draw' ? status : 'prep';
  }

  /**
   * GỠ SẠCH — dừng timer + bỏ MỌI listener. Gọi khi rời màn Arena hẳn.
   *
   * ⚠️⚠️ BUG ĐÃ SẬP Ở ĐÂY (chú thích để không lặp): bản cũ gỡ `'click'`, nhưng từ bản vá nuốt sự
   * kiện thì listener thật là `'pointerdown'` — gỡ sai tên ⇒ listener CŨ Ở LẠI. `bind()` lại lần sau
   * sẽ gắn THÊM một listener nữa, và mỗi cú bấm chạy lệnh 2 lần (triệu hồi 2 tướng, trừ tiền 2 lần).
   *
   * ⚠️ `move`/ `up`/ `cancel` gắn trên `window` nên phải gỡ đúng ở `window` — gỡ trên `root` là
   * không gỡ được gì (không có lỗi nào được ném ra, chỉ rò rỉ âm thầm).
   */
  dispose(): void {
    this.clearToast();
    if (this.relayoutHandle !== null) {
      cancelAnimationFrame(this.relayoutHandle);
      this.relayoutHandle = null;
    }
    this.cancelDrag();
    /*
     * ⚠️ `cancelDrag()` CHỈ xoá bóng khi ĐANG có thao tác kéo — nó `return` sớm khi `this.drag` là
     * `null`. Đường dispose chạy cả khi không kéo, nên phải gọi thêm ở đây; và phải gọi TRƯỚC khi
     * `this.host = null` (sau đó HUD không còn cửa nào để nói với scene).
     */
    this.hideDeployGhost();
    this.pendingBtn = null;
    if (this.bound) {
      this.root.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.root.removeEventListener('contextmenu', this.onContextMenu);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('orientationchange', this.onViewportChange);
      this.bound = false;
    }
    this.host = null;
    this.last = null;
    this.banner = null;
    /*
     * ⚠️⚠️ DỪNG VÒNG VẼ NGÒI VÀ THẢ LUÔN CANVAS — `dispose()` là "rời màn VĨNH VIỄN". Chỉ dừng `rAF`
     * thôi thì HUD đã chết vẫn giữ một canvas + context 2D sống mãi (context 2D là tài nguyên GC chậm
     * của trình duyệt); giữ `this.fuseCanvas` còn tệ hơn: lần `bind()`/ `render()` sau sẽ gắn lại một
     * canvas CŨ vào DOM MỚI mà không ai muốn.
     */
    this.stopFuseFx();
    this.fuseCanvas = null;
    this.fuseCtx = null;
    this.fuseCoreGrad = null;
    this.fuseSparks = [];
    /*
     * ⚠️ LỚP THÔNG BÁO SỐNG NGOÀI `root` ⇒ `dispose()` phải GỠ HẲN nó khỏi `document.body`, không chỉ
     * ẩn. Bỏ sót thì mỗi lần HUD được dựng lại (game đổi chế độ/ HMR) là một `#arenaStageFx` mới nằm
     * chồng lên cái cũ — cú bấm `Bắt đầu` chạy nhiều lần và chữ lớn hiện 2–3 bản lệch nhau.
     */
    this.stageFx?.dispose();
    this.stageFx = null;
  }
}
